import React, { useState, useCallback, useRef, useEffect } from "react";
import { parseApiDate, parseKanbanDate } from "../../utils/dateTime";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlus, FiX, FiZap, FiUsers, FiTrash2, FiCheck, FiSearch, FiChevronLeft, FiClipboard, FiStar, FiBarChart2, FiShare2, FiUpload, FiUploadCloud, FiHelpCircle, FiRefreshCw, FiArrowRight, FiEdit3, FiMail, FiLock, FiAlertTriangle, FiChevronDown, FiCheckCircle, FiRepeat, FiMessageSquare, FiDatabase, FiShoppingBag } from "react-icons/fi";
import { agentPhotoFor, getUploadedAgentPhoto, setAgentPhotoFromFile, removeAgentPhoto, useAgentPhotosVersion } from "../../utils/agentPhotos";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "../../app/routes";
import logo from "../../assets/images/M_Multicolor.svg";
import { getDefaultAgentVisual, COMING_SOON_AGENTS } from "./agentVisuals";
import { ensureDwConfigured, forgetDwConfigured } from "./dwAutoSetup";
import { usePagedActivity, LoadMoreSentinel } from "./usePagedActivity";
import { getPremadeSkills, getAgentMcpServers, getAgentCustomTools, getDwAgent, getExternalMcpList, getToolApprovals, saveToolApprovals, assignMcpServer, removeMcpServer, getAgentSkills, getSkillLibrary, assignSkill, removeSkill, assignPremadeToAgent, createSkillFromText, updateSkillFromText, createSkillInLibrary, deleteSkillFromLibrary, getSkillContent, listDwAgents, createDwAgent, updateDwAgent, archiveDwAgent, setPrimaryDwAgent, getAgentKanbanTasksPage, getAgentRoutineRunsPage, getAgentChatsPage, getAgentMemory, updateMemoryUserRules, runMemorySummarization, enableIntegrationForAgent, disableIntegrationForAgent, getEmailSignature, updateEmailSignature, convertDocumentToMarkdown, SKILL_DOCUMENT_ACCEPT, } from "../claudeagents/claudeAgentApi";
import { getIntegration } from "../apiintegrations/apiIntegrationService";
import { attachedToolNames } from "../apiintegrations/AgentEnablementPanel";
import IntegrationDetailBody from "../apiintegrations/IntegrationDetailBody";
import { IntegrationKeyPanel } from "../apiintegrations/IntegrationKeyPanel";
import { dedupeExternalMcps } from "../../services/marketplaceService";
import { resolveTaskTransactionId } from "../../services/kanbanAIService";
import { useToast } from "../../context/ToastContext";
import SkillShareMenuModal from "../../components/SkillShareMenuModal";
import AgentShareMenuModal from "../../components/AgentShareMenuModal";
import ToolShareMenuModal from "../../components/ToolShareMenuModal";
import { getMyTool, upsertMyTool, uninstallToolForAgency, type MyToolSummary } from "../../services/myToolsApi";
import type { MarketplaceLibraryItem, PremadeLibraryItem, PremadeSkillRecord, McpServerRecord, ExternalMcpItem, ToolApprovalMode, AgentSkillRecord, SkillLibraryItem, DwAgentRecord, AgentCustomToolRecord, AgentChatRecord, AgentKanbanTaskRecord, AgentRoutineRunRecord, AgentMemory, } from "../claudeagents/claudeAgentApi";
import "../claudeagents/claudeagents.css";
import "../../styles/DigitalWorkforce.css";
// The Integrations tab reuses the Marketplace's key panel and detail view (.mt-* only).
import "../../styles/MyTools.css";
import CommandIssuePanel from "../momentumcommand/CommandIssuePanel";
import type { CommandIssue } from "../../context/CommandIssueContext";
import { useActiveAgent } from "../../context/ActiveAgentContext";
const TOOL_TYPE_TO_INT: Record<MyToolSummary["type"], number> = {
    skill: 0, agent: 1, workflow: 2, widget: 3, mcp_server: 4, integration: 5,
};
// Activity tab lists (commands, routine runs, chats) load this many rows per request.
const ACTIVITY_PAGE_SIZE = 20;
// Read at render, not at module load: these pages are imported when the app first
// loads (usually the login screen), before login writes isAgencyOwner, so a
// module-level read stays false until a full page reload.
const readIsAgencyOwner = () => localStorage.getItem("isAgencyOwner") === "true";
type DocTab = "overview" | "activity" | "heartbeat" | "soul" | "tools" | "agents" | "mcp" | "integrations" | "memory";
type TaskPriority = "high" | "medium" | "low";
type TaskStatus = "pending" | "in_progress" | "completed";
interface Skill {
    id: string;
    name: string;
    soul: string;
}
interface Agent {
    id: string;
    name: string;
    description: string;
    model: string;
    isSystem: boolean;
    isPremade: boolean;
    isPrimary: boolean;
    avatarColor: string;
    heartbeat: string;
    soul: string;
    agentsMd: string;
    skills: Skill[];
    premadeSkillIds: number[];
    createdAt: string;
    // 'Hermes' agents run on a claw.insuranceagency.ai computer, not the
    // Anthropic Agents API. Their model is set on the Hermes side (its own
    // PUT /api/instances/{id}/model, which follows "auto" forward across
    // restarts) and none of the AGENT_MODELS ids below apply to them.
    harnessType: "Claude" | "Hermes";
    // Runtime settings, constrained by the model (see normalizeAgentRuntime).
    // effort is "" when the model has no effort control.
    effort: AgentEffort | "";
    speed: AgentSpeed;
}
interface Task {
    id: string;
    title: string;
    description: string;
    agentId: string;
    priority: TaskPriority;
    status: TaskStatus;
    createdAt: string;
}
const TASKS_KEY = "momentum_digital_workforce_tasks";
// Dev switch: show the setup screen without running setup (setup otherwise
// starts on its own the moment /status says the user is unconfigured).
const FORCE_DW_UNCONFIGURED = false;
const SHOW_ASSIGN_TASK_BUTTON = false;
const ACTIVITY_STATUS_MAP: Record<string, {
    label: string;
    color: string;
}> = {
    todo: { label: "To Do", color: "#6366f1" },
    pending: { label: "To Do", color: "#6366f1" },
    in_progress: { label: "In Progress", color: "#f59e0b" },
    running: { label: "In Progress", color: "#f59e0b" },
    in_review: { label: "In Review", color: "#00b0d3" },
    done: { label: "Done", color: "#22c55e" },
    completed: { label: "Done", color: "#22c55e" },
    success: { label: "Done", color: "#22c55e" },
    failed: { label: "Failed", color: "#ef4444" },
    error: { label: "Failed", color: "#ef4444" },
    "11111111-1111-1111-1111-111111111111": { label: "To Do", color: "#6366f1" },
    "22222222-2222-2222-2222-222222222222": { label: "In Progress", color: "#f59e0b" },
    "33333333-3333-3333-3333-333333333333": { label: "In Review", color: "#00b0d3" },
    "44444444-4444-4444-4444-444444444444": { label: "Done", color: "#22c55e" },
};
function activityStatusBadge(status?: string): {
    label: string;
    color: string;
} {
    if (!status)
        return { label: "Unknown", color: "#9ca3af" };
    const trimmed = status.trim().toLowerCase();
    if (ACTIVITY_STATUS_MAP[trimmed])
        return ACTIVITY_STATUS_MAP[trimmed];
    const key = trimmed.replace(/[\s-]+/g, "_");
    return ACTIVITY_STATUS_MAP[key] ?? { label: status, color: "#9ca3af" };
}
export const AVATAR_COLORS = ["#a200ff", "#2A65F2", "#00966E", "#FF6820", "#8854BC", "#00B0D3", "#D62612", "#FEAD0F"];
// Thinking effort — how deeply the agent reasons before acting. Higher levels are
// more thorough but slower and use more tokens.
export type AgentEffort = "low" | "medium" | "high" | "xhigh" | "max";
export type AgentSpeed = "standard" | "fast";
export const DEFAULT_AGENT_EFFORT: AgentEffort = "medium";
export const AGENT_EFFORT_OPTIONS: { value: AgentEffort; label: string; hint: string }[] = [
    { value: "low", label: "Low", hint: "Quickest and most economical — simple lookups, data entry, routine replies." },
    { value: "medium", label: "Medium", hint: "Recommended default — balanced speed and thoroughness for most agency work." },
    { value: "high", label: "High", hint: "More thorough reasoning for complex, multi-step tasks." },
    { value: "xhigh", label: "Extra high", hint: "Deep reasoning for demanding, long-running agent work." },
    { value: "max", label: "Max", hint: "Maximum reasoning when accuracy matters more than speed or cost." },
];
const ALL_EFFORTS: AgentEffort[] = ["low", "medium", "high", "xhigh", "max"];
export const AGENT_MODELS: {
    value: string;
    label: string;
    hint?: string;
    // Effort levels the model accepts. Empty = no effort control (Haiku 4.5);
    // an id missing from this list also resolves to empty (getModelEfforts).
    efforts?: AgentEffort[];
    // Fast mode (speed: "fast") — Opus 5.5, Opus 5 and Opus 4.8 only.
    supportsFast?: boolean;
    // Still resolvable for agents that carry the id (label, hint, effort rules,
    // details card) but never offered as a choice in the picker.
    legacy?: boolean;
}[] = [
    { value: "", label: "Select model" },
    { value: "claude-sonnet-5-5", label: "Sonnet 5.5", hint: "Newest Sonnet — fast and capable for everyday agency workflows.", efforts: ALL_EFFORTS },
    { value: "claude-opus-5-5", label: "Opus 5.5", hint: "Newest and most capable Opus — best for complex judgment calls, like underwriting review or multi-step reasoning.", efforts: ALL_EFFORTS, supportsFast: true },
    { value: "claude-haiku-5-5", label: "Haiku 5.5", hint: "Fastest and most economical — best for simple, high-volume tasks like data entry or quick lookups. Thinking is on (adaptive); tune it with the effort level.", efforts: ALL_EFFORTS },
    { value: "claude-opus-5", label: "Opus 5", hint: "Previous Opus — pick Opus 5.5 for new agents unless you need to pin this version.", efforts: ALL_EFFORTS, supportsFast: true },
    { value: "claude-sonnet-5", label: "Sonnet 5", hint: "Previous Sonnet — pick Sonnet 5.5 for new agents unless you need to pin this version.", efforts: ALL_EFFORTS },
    { value: "claude-opus-4-8", label: "Opus 4.8", hint: "Older Opus — pick Opus 5.5 for new agents unless you need to pin this version.", efforts: ALL_EFFORTS, supportsFast: true },
    { value: "claude-opus-4-7", label: "Opus 4.7", hint: "Older Opus — pick Opus 5.5 for new agents unless you need to pin this version.", efforts: ALL_EFFORTS },
    { value: "claude-opus-4-6", label: "Opus 4.6", hint: "Older Opus — pick Opus 5.5 for new agents unless you need to pin this version.", efforts: ["low", "medium", "high", "max"] },
    { value: "claude-sonnet-4-6", label: "Sonnet 4.6", hint: "Older Sonnet — pick Sonnet 5.5 for new agents unless you need to pin this version.", efforts: ["low", "medium", "high", "max"] },
    // Retired from the picker on 2026-10-21: its 200K window overflows on the default
    // tool surface. Kept so agents still on it display correctly until they are moved.
    { value: "claude-haiku-4-5", label: "Haiku 4.5", hint: "No longer offered — its 200K context overflows on the default tool surface. Switch this agent to Haiku 5.5.", efforts: [], legacy: true },
];
export const getModelEfforts = (modelId: string): AgentEffort[] =>
    AGENT_MODELS.find((m) => m.value === modelId)?.efforts ?? [];
export const modelSupportsFast = (modelId: string): boolean =>
    !!AGENT_MODELS.find((m) => m.value === modelId)?.supportsFast;
// Keeps effort/speed valid for the chosen model: an unsupported effort falls back
// to Medium (or the lowest level offered), no effort at all on models without it,
// and Normal speed on models without fast mode.
export function normalizeAgentRuntime(modelId: string, effort: AgentEffort | "", speed: AgentSpeed): {
    effort: AgentEffort | "";
    speed: AgentSpeed;
} {
    const efforts = getModelEfforts(modelId);
    const nextEffort: AgentEffort | "" = efforts.length === 0
        ? ""
        : effort && efforts.includes(effort)
            ? effort
            : efforts.includes(DEFAULT_AGENT_EFFORT) ? DEFAULT_AGENT_EFFORT : efforts[0];
    return { effort: nextEffort, speed: modelSupportsFast(modelId) ? speed : "standard" };
}
const MODEL_CAPABILITIES = ["Extended thinking", "Vision", "Structured output", "PDF input", "Code execution", "Citations"] as const;
type ModelDetail = {
    modelId: string;
    tagline: string;
    latest?: boolean;
    contextWindow: string;
    maxOutput: string;
    // Omitted when not confirmed; the card then leaves the "Released" stat out.
    released?: string;
    capabilities: Record<(typeof MODEL_CAPABILITIES)[number], boolean>;
};
const MODEL_DETAILS: Record<string, ModelDetail> = {
    "claude-sonnet-5-5": {
        modelId: "claude-sonnet-5-5",
        tagline: "Fast, capable model for everyday agency work",
        latest: true,
        contextWindow: "1,000,000 tokens",
        maxOutput: "128,000 tokens",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": true, Citations: true },
    },
    "claude-opus-5-5": {
        modelId: "claude-opus-5-5",
        tagline: "Most capable model for complex, high-stakes work",
        latest: true,
        contextWindow: "1,000,000 tokens",
        maxOutput: "128,000 tokens",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": true, Citations: true },
    },
    "claude-opus-5": {
        modelId: "claude-opus-5",
        tagline: "Previous Opus model for deep reasoning tasks",
        contextWindow: "1,000,000 tokens",
        maxOutput: "128,000 tokens",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": true, Citations: true },
    },
    "claude-opus-4-6": {
        modelId: "claude-opus-4-6",
        tagline: "Older Opus model for deep reasoning tasks",
        contextWindow: "1,000,000 tokens",
        maxOutput: "128,000 tokens",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": true, Citations: true },
    },
    "claude-sonnet-5": {
        modelId: "claude-sonnet-5",
        tagline: "Previous Sonnet model for everyday use",
        contextWindow: "1,000,000 tokens",
        maxOutput: "128,000 tokens",
        released: "Jun 2026",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": true, Citations: true },
    },
    "claude-opus-4-8": {
        modelId: "claude-opus-4-8",
        tagline: "Older Opus model for complex, high-stakes work",
        contextWindow: "1,000,000 tokens",
        maxOutput: "128,000 tokens",
        released: "May 2026",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": true, Citations: true },
    },
    "claude-opus-4-7": {
        modelId: "claude-opus-4-7",
        tagline: "Older Opus model for deep reasoning tasks",
        contextWindow: "1,000,000 tokens",
        maxOutput: "128,000 tokens",
        released: "Apr 2026",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": true, Citations: true },
    },
    "claude-sonnet-4-6": {
        modelId: "claude-sonnet-4-6",
        tagline: "Older Sonnet model for everyday agents",
        contextWindow: "1,000,000 tokens",
        maxOutput: "128,000 tokens",
        released: "Aug 2025",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": true, Citations: true },
    },
    "claude-haiku-5-5": {
        modelId: "claude-haiku-5-5",
        tagline: "Fastest model for daily tasks",
        latest: true,
        contextWindow: "1,000,000 tokens",
        maxOutput: "128,000 tokens",
        released: "Oct 2026",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": true, Citations: true },
    },
    // Legacy (see AGENT_MODELS): shown only for agents still on it.
    "claude-haiku-4-5": {
        modelId: "claude-haiku-4-5-20251001",
        tagline: "Previous Haiku — superseded by Haiku 5.5",
        contextWindow: "200,000 tokens",
        maxOutput: "64,000 tokens",
        released: "Oct 2025",
        capabilities: { "Extended thinking": true, Vision: true, "Structured output": true, "PDF input": true, "Code execution": false, Citations: true },
    },
};
export function ModelPicker({ value, onChange, disabled, invalid, onOpenChange, }: {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    invalid?: boolean;
    onOpenChange?: (open: boolean) => void;
}) {
    const [open, setOpenState] = useState(false);
    const [previewValue, setPreviewValue] = useState<string | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    // Legacy rows resolve for display (label/card) but are not offered in the list.
    const allModels = AGENT_MODELS.filter((m) => m.value !== "");
    const selectableModels = allModels.filter((m) => !m.legacy);
    const setOpen = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
        setOpenState((prev) => {
            const resolved = typeof next === "function" ? (next as (prev: boolean) => boolean)(prev) : next;
            onOpenChange?.(resolved);
            return resolved;
        });
    }, [onOpenChange]);
    useEffect(() => {
        if (!open)
            return;
        const handleClickOutside = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node))
                setOpen(false);
        };
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape")
                setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [open]);
    const activeValue = previewValue ?? value;
    // No fallback row: an id this table doesn't know (e.g. a dated snapshot id)
    // gets no card rather than another model's card presented as its own.
    const activeMeta = allModels.find((m) => m.value === activeValue);
    const activeDetail = activeMeta ? MODEL_DETAILS[activeMeta.value] : undefined;
    const selectedMeta = allModels.find((m) => m.value === value);
    return (<div className="dw-model-picker" ref={rootRef}>
      <button type="button" className="dw-field__select dw-model-picker__trigger" disabled={disabled} style={invalid ? { borderColor: "#ef4444" } : undefined} onClick={() => {
            if (disabled)
                return;
            setPreviewValue(value || selectableModels[0]?.value || null);
            setOpen((o) => !o);
        }}>
        {/* An id this table doesn't know (e.g. a dated snapshot id set by the
            admin update-model job) shows as-is rather than as "nothing selected". */}
        <span>{selectedMeta ? selectedMeta.label : value || "Select model"}</span>
        <FiChevronDown className="dw-model-picker__chevron"/>
      </button>
      {open && (<div className="dw-model-picker__popover">
          <div className="dw-model-picker__list">
            {selectableModels.map((m) => (<button key={m.value} type="button" className={`dw-model-picker__item${m.value === value ? " dw-model-picker__item--selected" : ""}${m.value === activeValue ? " dw-model-picker__item--active" : ""}`} onMouseEnter={() => setPreviewValue(m.value)} onClick={() => {
                    onChange(m.value);
                    setOpen(false);
                }}>
                <span className="dw-model-picker__item-top">
                  <span className="dw-model-picker__item-label">{m.label}</span>
                  {m.value === value && <FiCheck className="dw-model-picker__item-check"/>}
                </span>
                <span className="dw-model-picker__item-tagline">{MODEL_DETAILS[m.value]?.tagline}</span>
              </button>))}
          </div>
          {activeMeta && activeDetail && (<div className="dw-model-picker__card">
              <div className="dw-model-picker__card-head">
                <div>
                  <div className="dw-model-picker__card-name">Claude {activeMeta.label}</div>
                  <code className="dw-model-picker__card-id">{activeDetail.modelId}</code>
                </div>
                {activeDetail.latest && <span className="dw-model-picker__card-badge">Latest</span>}
              </div>
              <p className="dw-model-picker__card-tagline">{activeDetail.tagline}</p>
              {activeMeta.hint && <p className="dw-model-picker__card-recommend">{activeMeta.hint}</p>}
              <div className="dw-model-picker__card-stats">
                <div>
                  <span>Context window</span>
                  <strong>{activeDetail.contextWindow}</strong>
                </div>
                <div>
                  <span>Max output</span>
                  <strong>{activeDetail.maxOutput}</strong>
                </div>
                {activeDetail.released && (<div>
                    <span>Released</span>
                    <strong>{activeDetail.released}</strong>
                  </div>)}
              </div>
              <div className="dw-model-picker__card-caps">
                {MODEL_CAPABILITIES.map((cap) => (<div key={cap} className={`dw-model-picker__cap${activeDetail.capabilities[cap] ? "" : " dw-model-picker__cap--off"}`}>
                    {activeDetail.capabilities[cap] ? <FiCheck /> : <FiX />}
                    <span>{cap}</span>
                  </div>))}
              </div>
            </div>)}
        </div>)}
    </div>);
}
const PRIORITY_CONFIG: Record<TaskPriority, {
    label: string;
    color: string;
}> = {
    high: { label: "High", color: "#ef4444" },
    medium: { label: "Medium", color: "#f59e0b" },
    low: { label: "Low", color: "#22c55e" },
};
const TASK_STATUS_CONFIG: Record<TaskStatus, {
    label: string;
    color: string;
}> = {
    pending: { label: "Pending", color: "#9ca3af" },
    in_progress: { label: "In Progress", color: "#00b0d3" },
    completed: { label: "Completed", color: "#22c55e" },
};
const DOC_TABS: {
    id: DocTab;
    label: string;
}[] = [
    { id: "activity", label: "Activity" },
    { id: "overview", label: "Overview" },
    { id: "agents", label: "agents.md" },
    { id: "tools", label: "skills.md" },
    { id: "mcp", label: "Tools" },
    // Integrations tab hidden; add { id: "integrations", label: "Integrations" } back to show it.
    { id: "memory", label: "Memory" },
];
const META_KEY = "momentum_dw_agent_meta";
type AgentMeta = {
    description: string;
    model: string;
    avatarColor: string;
    agentsMd: string;
};
function loadMeta(): Record<string, Partial<AgentMeta>> {
    try {
        return JSON.parse(localStorage.getItem(META_KEY) ?? "{}");
    }
    catch {
        return {};
    }
}
export function saveMeta(agentId: string, meta: Partial<AgentMeta>): void {
    try {
        const all = loadMeta();
        localStorage.setItem(META_KEY, JSON.stringify({ ...all, [agentId]: { ...all[agentId], ...meta } }));
    }
    catch {
    }
}
function deleteMeta(agentId: string): void {
    try {
        const all = loadMeta();
        delete all[agentId];
        localStorage.setItem(META_KEY, JSON.stringify(all));
    }
    catch {
    }
}
export function getModelId(model?: string | {
    id: string;
}): string {
    if (!model)
        return "";
    return typeof model === "string" ? model : model.id;
}
// Effort/speed live on the model object returned by GET /agents/{id} and by
// create/update — model.effort.type ("xhigh") and model.speed ("standard" |
// "fast"). The list endpoint returns model as a bare id, so there this yields
// the defaults (known: false); handleSelect loads the real values.
export function readAgentRuntime(rec: DwAgentRecord, modelId: string): { effort: AgentEffort | ""; speed: AgentSpeed; known: boolean } {
    const modelObj = rec.model && typeof rec.model === "object" ? rec.model : null;
    const rawEffort = typeof modelObj?.effort === "string"
        ? modelObj.effort
        : modelObj?.effort?.type ?? rec.effort ?? "";
    const recEffort = typeof rawEffort === "string" ? rawEffort.toLowerCase() : "";
    const recSpeed: AgentSpeed = (modelObj?.speed ?? rec.speed) === "fast" ? "fast" : "standard";
    const runtime = normalizeAgentRuntime(
        modelId,
        (ALL_EFFORTS as string[]).includes(recEffort) ? (recEffort as AgentEffort) : "",
        recSpeed,
    );
    return { ...runtime, known: !!modelObj };
}
function apiToAgent(rec: DwAgentRecord, meta: Partial<AgentMeta> = {}): Agent {
    const apiModel = getModelId(rec.model);
    const resolvedModel = apiModel || meta.model || "";
    const runtime = readAgentRuntime(rec, resolvedModel);
    return {
        id: rec.agent_id,
        name: rec.name,
        description: meta.description ?? rec.description ?? "",
        model: apiModel || meta.model || "",
        isSystem: !!rec.is_system,
        isPremade: !!rec.is_premade || !!rec.persona_key,
        isPrimary: !!rec.is_primary_agent,
        avatarColor: meta.avatarColor ?? AVATAR_COLORS[0],
        heartbeat: rec.heartbeat ?? "",
        soul: rec.soul ?? "",
        agentsMd: meta.agentsMd ?? rec.system ?? "",
        skills: [],
        premadeSkillIds: [],
        createdAt: rec.updated_at ?? rec.created_at ?? new Date().toISOString(),
        harnessType: rec.harness_type === "Hermes" ? "Hermes" : "Claude",
        effort: runtime.effort,
        speed: runtime.speed,
    };
}
function loadTasks(): Task[] {
    try {
        const s = localStorage.getItem(TASKS_KEY);
        if (s)
            return JSON.parse(s) as Task[];
    }
    catch {
    }
    const now = Date.now();
    const sample: Task[] = [
        {
            id: "t-001",
            title: "Process renewal follow-ups",
            description: "Handle all clients with renewals due in 30 days",
            agentId: "dw-agent-001",
            priority: "high",
            status: "in_progress",
            createdAt: new Date(now - 2 * 3600000).toISOString(),
        },
        {
            id: "t-002",
            title: "Review new applications",
            description: "Evaluate incoming submissions from broker portal",
            agentId: "dw-agent-002",
            priority: "high",
            status: "pending",
            createdAt: new Date(now - 4 * 3600000).toISOString(),
        },
        {
            id: "t-003",
            title: "Client inquiry responses",
            description: "Respond to pending client emails",
            agentId: "dw-agent-001",
            priority: "medium",
            status: "completed",
            createdAt: new Date(now - 8 * 3600000).toISOString(),
        },
        {
            id: "t-004",
            title: "Risk assessment batch",
            description: "Run risk models on submitted applications",
            agentId: "dw-agent-002",
            priority: "medium",
            status: "completed",
            createdAt: new Date(now - 26 * 3600000).toISOString(),
        },
        {
            id: "t-005",
            title: "Policy lookup requests",
            description: "Retrieve policy details for agent queries",
            agentId: "dw-agent-001",
            priority: "low",
            status: "pending",
            createdAt: new Date(now - 3600000).toISOString(),
        },
        {
            id: "t-006",
            title: "Claims triage",
            description: "Prioritize and route new claims submissions",
            agentId: "dw-agent-001",
            priority: "high",
            status: "pending",
            createdAt: new Date(now - 30 * 60000).toISOString(),
        },
        {
            id: "t-007",
            title: "Coverage gap analysis",
            description: "Identify upsell opportunities from policy review",
            agentId: "dw-agent-002",
            priority: "low",
            status: "completed",
            createdAt: new Date(now - 48 * 3600000).toISOString(),
        },
    ];
    try {
        localStorage.setItem(TASKS_KEY, JSON.stringify(sample));
    }
    catch {
    }
    return sample;
}
function persistTasks(tasks: Task[]): void {
    try {
        localStorage.setItem(TASKS_KEY, JSON.stringify(tasks));
    }
    catch {
    }
}
function genId(): string {
    return `dw-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}
function getInitials(name: string): string {
    return (name
        .split(" ")
        .filter(Boolean)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "?");
}
function nameStartsWithDefault(name: string): boolean {
    return /^default\b/i.test(name.trim());
}
function makeBlankAgent(): Agent {
    return {
        id: genId(),
        name: "",
        description: "",
        model: "",
        isSystem: false,
        isPremade: false,
        isPrimary: false,
        avatarColor: AVATAR_COLORS[0],
        heartbeat: "",
        soul: "",
        agentsMd: "",
        skills: [],
        premadeSkillIds: [],
        createdAt: new Date().toISOString(),
        // A user-created agent is always Claude-backed — Hermes agents are
        // only ever provisioned by the backend, never from this form.
        harnessType: "Claude",
        effort: DEFAULT_AGENT_EFFORT,
        speed: "standard",
    };
}
function toDateInputValue(d: Date): string {
    return d.toISOString().slice(0, 10);
}
function defaultActivityDateRange(): {
    from: string;
    to: string;
} {
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    return { from: toDateInputValue(weekAgo), to: toDateInputValue(now) };
}
function formatRelTime(iso: string): string {
    if (!iso)
        return "—";
    const d = parseApiDate(iso);
    if (!d)
        return "—";
    return (d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
        " · " +
        d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }));
}
function formatKanbanTaskTime(iso?: string): string {
    if (!iso)
        return "—";
    const d = parseKanbanDate(iso);
    if (!d)
        return "—";
    return (d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
        " · " +
        d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }));
}
function chatSessionTitle(title?: string): string {
    const trimmed = (title ?? "").trim();
    if (!trimmed || /^\[Runtime context\]/i.test(trimmed))
        return "Untitled conversation";
    return trimmed.split("\n")[0].trim() || "Untitled conversation";
}
function routineRunSummary(notes?: string): string {
    const trimmed = (notes ?? "").trim();
    if (!trimmed)
        return "Routine run";
    const firstLine = (trimmed.split("\n")[0] || trimmed).trim();
    return firstLine.length > 100 ? `${firstLine.slice(0, 100).trim()}…` : firstLine;
}
function formatDurationSec(sec?: number): string {
    if (sec == null || sec < 0)
        return "";
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
interface MdEditorProps {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    minHeight?: number;
    readOnly?: boolean;
}
export const MdEditor: React.FC<MdEditorProps> = ({ value, onChange, placeholder, minHeight = 200, readOnly = false }) => {
    const [rawMode, setMode] = useState<"edit" | "preview">(readOnly ? "preview" : "edit");
    const mode = readOnly ? "preview" : rawMode;
    const ref = useRef<HTMLTextAreaElement>(null);
    const insert = useCallback((before: string, after: string, linePrefix = "") => {
        const el = ref.current;
        if (!el)
            return;
        const { selectionStart: s, selectionEnd: e, value: v } = el;
        const sel = v.slice(s, e);
        const repl = linePrefix ? `${linePrefix}${sel || "text"}` : `${before}${sel || "text"}${after}`;
        onChange(v.slice(0, s) + repl + v.slice(e));
        setTimeout(() => {
            el.setSelectionRange(s + repl.length, s + repl.length);
            el.focus();
        }, 0);
    }, [onChange]);
    return (<div className="dw-md-editor">
      <div className="dw-md-toolbar">
        <div className="dw-md-tabs">
          {!readOnly && (<button className={`dw-md-tab${mode === "edit" ? " dw-md-tab--on" : ""}`} onClick={() => setMode("edit")}>
              Edit
            </button>)}
          <button className={`dw-md-tab${mode === "preview" ? " dw-md-tab--on" : ""}`} onClick={() => setMode("preview")}>
            {readOnly ? "Read-only" : "Preview"}
          </button>
        </div>
        {!readOnly && mode === "edit" && (<div className="dw-md-btns">
            <button title="Bold" onClick={() => insert("**", "**")}>
              <b>B</b>
            </button>
            <button title="Italic" onClick={() => insert("*", "*")}>
              <em>I</em>
            </button>
            <span className="dw-md-div"/>
            <button title="Heading 1" onClick={() => insert("", "", "# ")}>
              H1
            </button>
            <button title="Heading 2" onClick={() => insert("", "", "## ")}>
              H2
            </button>
            <button title="Heading 3" onClick={() => insert("", "", "### ")}>
              H3
            </button>
            <span className="dw-md-div"/>
            <button title="Bullet" onClick={() => insert("", "", "- ")}>
              UL
            </button>
            <button title="Numbered" onClick={() => insert("", "", "1. ")}>
              OL
            </button>
            <span className="dw-md-div"/>
            <button title="Code" onClick={() => insert("`", "`")}>
              {"`x`"}
            </button>
            <button title="Blockquote" onClick={() => insert("", "", "> ")}>
              ❝
            </button>
          </div>)}
        <span className="dw-md-charcount">{value.length.toLocaleString()} chars</span>
      </div>

      {!readOnly && mode === "edit" ? (<textarea ref={ref} className="dw-md-textarea" style={{ minHeight }} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} spellCheck={false}/>) : (<div className="dw-md-preview" style={{ minHeight }}>
          {value.trim() ? (value.split("\n").map((line, i) => {
                if (/^### (.+)/.test(line))
                    return (<h3 key={i} className="dw-md-h3">
                    {line.replace(/^### /, "")}
                  </h3>);
                if (/^## (.+)/.test(line))
                    return (<h2 key={i} className="dw-md-h2">
                    {line.replace(/^## /, "")}
                  </h2>);
                if (/^# (.+)/.test(line))
                    return (<h1 key={i} className="dw-md-h1">
                    {line.replace(/^# /, "")}
                  </h1>);
                if (/^[-*] (.+)/.test(line))
                    return (<div key={i} className="dw-md-li">
                    • {line.replace(/^[-*] /, "")}
                  </div>);
                if (/^\d+\. (.+)/.test(line))
                    return (<div key={i} className="dw-md-li">
                    {line}
                  </div>);
                if (/^> (.+)/.test(line))
                    return (<blockquote key={i} className="dw-md-quote">
                    {line.replace(/^> /, "")}
                  </blockquote>);
                if (/^---$/.test(line.trim()))
                    return <hr key={i} className="dw-md-hr"/>;
                if (line.trim() === "")
                    return <div key={i} className="dw-md-blank-line"/>;
                const html = line
                    .replace(/`([^`]+)`/g, "<code>$1</code>")
                    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
                    .replace(/\*(.+?)\*/g, "<em>$1</em>");
                return <p key={i} className="dw-md-p" dangerouslySetInnerHTML={{ __html: html }}/>;
            })) : (<span className="dw-md-preview-empty">{readOnly ? "No content available." : "Nothing to preview yet."}</span>)}
        </div>)}
    </div>);
};
function dwSkillColor(name: string): string {
    const n = name.toLowerCase();
    if (n === "xlsx")
        return "#22c55e";
    if (n === "pptx")
        return "#f97316";
    if (n === "pdf")
        return "#ef4444";
    if (n === "docx")
        return "#3b82f6";
    return "#a78bfa";
}
const DwSkillIcon: React.FC<{
    name: string;
    size?: number;
}> = ({ name, size = 32 }) => {
    const color = dwSkillColor(name);
    return (<div style={{
            width: size,
            height: size,
            borderRadius: 8,
            flexShrink: 0,
            background: `${color}18`,
            border: `1.5px solid ${color}35`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'SF Mono','Fira Code',monospace",
            fontSize: size < 30 ? 8 : 9,
            fontWeight: 700,
            color,
            letterSpacing: "-0.02em",
        }}>
      {name.slice(0, 4).toUpperCase()}
    </div>);
};
function dwFmtVer(raw: string): string {
    if (!raw)
        return "";
    if (/^\d{8,}$/.test(raw))
        return "";
    return raw.length > 14 ? raw.slice(0, 12) + "…" : raw;
}
function dwSkillSourceLabel(source?: string): string {
    if (!source)
        return "custom";
    return source.toLowerCase() === "anthropic" ? "Agent Studio" : source;
}
type DwSkillFilter = "all" | "assigned" | "system" | "marketplace" | "custom";
type DwSkillPanel = {
    mode: "empty";
} | {
    mode: "create";
} | {
    mode: "upload";
} | {
    mode: "edit-library";
    skill: SkillLibraryItem;
} | {
    mode: "view-assigned";
    skill: AgentSkillRecord;
} | {
    mode: "view-premade";
    skill: PremadeSkillRecord;
} | {
    mode: "view-marketplace";
    skill: MarketplaceLibraryItem;
};
interface DwEditorBodyProps {
    mode: "edit" | "preview";
    onModeChange: (m: "edit" | "preview") => void;
    body: string;
    onBodyChange: (v: string) => void;
    textEditorRef: React.RefObject<HTMLTextAreaElement | null>;
    insertMd: (before: string, after: string, linePrefix?: string) => void;
    hint?: string;
    readOnly?: boolean;
    /** When set, the Edit toolbar shows an "Import doc" button; the chosen file is handed to this
     * callback (the page converts it to Markdown and inserts the result into the body). */
    onImportDocument?: (file: File) => void;
    importAccept?: string;
    importing?: boolean;
}
const DwImportDocButton: React.FC<{
    accept?: string;
    importing: boolean;
    disabled: boolean;
    onPick: (file: File) => void;
}> = ({ accept, importing, disabled, onPick }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    return (<>
      <span className="ca-fp-md-toolbar__div"/>
      <input ref={inputRef} type="file" accept={accept} style={{ display: "none" }} onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f)
                onPick(f);
        }}/>
      <button type="button" title="Import a document (PDF, Word, PowerPoint, Excel, ...) - its contents are added to the prompt as Markdown" disabled={disabled || importing} onClick={() => {
            if (!disabled && !importing)
                inputRef.current?.click();
        }}>
        {importing ? "Importing…" : "Import doc"}
      </button>
    </>);
};
const DwEditorBody: React.FC<DwEditorBodyProps> = ({ mode, onModeChange, body, onBodyChange, textEditorRef, insertMd, hint, readOnly, onImportDocument, importAccept, importing = false }) => (<div className="ca-fp-editor__body">
    <div className="ca-fp-editor__toolbar-row">
      <div className="ca-fp-editor__mode-tabs">
        <button className={`ca-fp-editor__mode-tab${mode === "edit" ? " ca-fp-editor__mode-tab--on" : ""}`} onClick={() => !readOnly && onModeChange("edit")}>
          Edit
        </button>
        <button className={`ca-fp-editor__mode-tab${mode === "preview" ? " ca-fp-editor__mode-tab--on" : ""}`} onClick={() => onModeChange("preview")}>
          Preview
        </button>
      </div>
      {mode === "edit" && (<div className={`ca-fp-md-toolbar${readOnly ? " ca-fp-md-toolbar--disabled" : ""}`}>
          <button title="Bold" onClick={() => !readOnly && insertMd("**", "**")}>
            <b>B</b>
          </button>
          <button title="Italic" onClick={() => !readOnly && insertMd("*", "*")}>
            <em>I</em>
          </button>
          <span className="ca-fp-md-toolbar__div"/>
          <button title="Heading 1" onClick={() => !readOnly && insertMd("", "", "# ")}>
            H1
          </button>
          <button title="Heading 2" onClick={() => !readOnly && insertMd("", "", "## ")}>
            H2
          </button>
          <button title="Heading 3" onClick={() => !readOnly && insertMd("", "", "### ")}>
            H3
          </button>
          <span className="ca-fp-md-toolbar__div"/>
          <button title="Bullet list" onClick={() => !readOnly && insertMd("", "", "- ")}>
            UL
          </button>
          <button title="Numbered list" onClick={() => !readOnly && insertMd("", "", "1. ")}>
            OL
          </button>
          <span className="ca-fp-md-toolbar__div"/>
          <button title="Inline code" onClick={() => !readOnly && insertMd("`", "`")}>
            {"`  `"}
          </button>
          <button title="Blockquote" onClick={() => !readOnly && insertMd("", "", "> ")}>
            ❝
          </button>
          <button title="HR" onClick={() => {
            if (!readOnly) {
                onBodyChange(body + "\n---\n");
                setTimeout(() => textEditorRef.current?.focus(), 0);
            }
        }}>
            —
          </button>
          {onImportDocument && (<DwImportDocButton accept={importAccept} importing={importing} disabled={!!readOnly} onPick={onImportDocument}/>)}
        </div>)}
      <span className="ca-fp-editor__charcount">{body.length.toLocaleString()} chars</span>
    </div>
    {mode === "edit" ? (<textarea ref={textEditorRef} className={`ca-fp-editor__textarea${readOnly ? " ca-fp-editor__textarea--readonly" : ""}`} placeholder={`# Skill Title\n\nDescribe what the agent should do.\n\n## Instructions\n- Step 1\n- Step 2`} value={body} onChange={(e) => !readOnly && onBodyChange(e.target.value)} readOnly={readOnly} spellCheck={false}/>) : (<div className="ca-fp-editor__preview">
        {body.trim() ? (body.split("\n").map((line, i) => {
            if (/^### (.+)/.test(line))
                return (<h3 key={i} className="cac-md-h3">
                  {line.replace(/^### /, "")}
                </h3>);
            if (/^## (.+)/.test(line))
                return (<h2 key={i} className="cac-md-h2">
                  {line.replace(/^## /, "")}
                </h2>);
            if (/^# (.+)/.test(line))
                return (<h1 key={i} className="cac-md-h1">
                  {line.replace(/^# /, "")}
                </h1>);
            if (/^[-*] (.+)/.test(line))
                return (<div key={i} className="cac-md-li">
                  • {line.replace(/^[-*] /, "")}
                </div>);
            if (/^\d+\. (.+)/.test(line))
                return (<div key={i} className="cac-md-li">
                  {line}
                </div>);
            if (/^> (.+)/.test(line))
                return (<blockquote key={i} className="cac-md-quote">
                  {line.replace(/^> /, "")}
                </blockquote>);
            if (/^---$/.test(line.trim()))
                return <hr key={i} className="cac-md-hr"/>;
            if (line.trim() === "")
                return <div key={i} className="cac-md-br"/>;
            const html = line
                .replace(/`([^`]+)`/g, "<code>$1</code>")
                .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
                .replace(/\*(.+?)\*/g, "<em>$1</em>");
            return <p key={i} className="cac-md-p" dangerouslySetInnerHTML={{ __html: html }}/>;
        })) : (<span className="ca-fp-editor__preview-empty">Nothing to preview yet.</span>)}
      </div>)}
    {hint && <p className="ca-fp-editor__hint">{hint}</p>}
  </div>);
const DwSkillGroupHead: React.FC<{
    tone: "assigned" | "system" | "marketplace" | "custom";
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    count: number;
    expanded?: boolean;
    onToggle?: () => void;
}> = ({ tone, icon, title, subtitle, count, expanded, onToggle }) => (<div className={`dw-skill-sec dw-skill-sec--${tone}${onToggle ? " dw-skill-sec--clickable" : ""}${expanded ? " dw-skill-sec--open" : ""}`} role={onToggle ? "button" : undefined} tabIndex={onToggle ? 0 : undefined} onClick={onToggle} onKeyDown={onToggle
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle();
            }
        }
        : undefined}>
    <span className="dw-skill-sec__icon">{icon}</span>
    <span className="dw-skill-sec__text">
      <span className="dw-skill-sec__title">{title}</span>
      <span className="dw-skill-sec__sub">{subtitle}</span>
    </span>
    <span className="dw-skill-sec__count">{count}</span>
    {onToggle && <FiChevronDown size={14} className="dw-skill-sec__chev"/>}
  </div>);
export const DwSkillsTab: React.FC<{
    agentId: string;
    agentName: string;
}> = ({ agentId, agentName }) => {
    const isAgencyOwner = readIsAgencyOwner();
    const [assigned, setAssigned] = useState<AgentSkillRecord[]>([]);
    const [library, setLibrary] = useState<SkillLibraryItem[]>([]);
    const [premadeList, setPremadeList] = useState<PremadeSkillRecord[]>([]);
    // Marketplace copies the user has installed: assign-only, content hidden.
    const [marketplaceList, setMarketplaceList] = useState<MarketplaceLibraryItem[]>([]);
    const [dataReady, setDataReady] = useState(false);
    const [filter, setFilter] = useState<DwSkillFilter>("all");
    const [search, setSearch] = useState("");
    const [panel, setPanel] = useState<DwSkillPanel>({ mode: "empty" });
    const [assignedExpanded, setAssignedExpanded] = useState(true);
    const [systemExpanded, setSystemExpanded] = useState(false);
    const [customExpanded, setCustomExpanded] = useState(false);
    const [marketplaceExpanded, setMarketplaceExpanded] = useState(false);
    const [skillName, setSkillName] = useState("");
    const [skillBody, setSkillBody] = useState("");
    const [editorMode, setEditorMode] = useState<"edit" | "preview">("edit");
    const [skillSaving, setSkillSaving] = useState(false);
    const [premadeReadonly, setPremadeReadonly] = useState(true);
    const { showError: showSkillErrorToast, showSuccess: showSkillSuccessToast, showWarning: showSkillWarningToast } = useToast();
    const [shareMenuFor, setShareMenuFor] = useState<SkillLibraryItem | null>(null);
    const [uploadTitle, setUploadTitle] = useState("");
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const fileRef = useRef<HTMLInputElement>(null);
    const [removing, setRemoving] = useState<number | null>(null);
    const [confirmRm, setConfirmRm] = useState<number | null>(null);
    const [adding, setAdding] = useState<string | null>(null);
    const [assigning, setAssigning] = useState<number | null>(null);
    const [assigningMarketplace, setAssigningMarketplace] = useState<string | null>(null);
    const [deletingLib, setDeletingLib] = useState<string | null>(null);
    const [confirmDeleteLib, setConfirmDeleteLib] = useState<string | null>(null);
    const textEditorRef = useRef<HTMLTextAreaElement>(null);
    const [importingDoc, setImportingDoc] = useState(false);
    const normalizeLibrary = (lib: {
        anthropic: SkillLibraryItem[];
        custom: SkillLibraryItem[];
        marketplace?: MarketplaceLibraryItem[];
    }): SkillLibraryItem[] => {
        // A Marketplace copy is never editable here, so keep it out of Custom even if the
        // API ever lists it there too.
        const marketplaceIds = new Set((lib.marketplace ?? []).map((m) => m.id));
        const anthropic = lib.anthropic.map((item) => ({ ...item, source: "anthropic" as const }));
        const custom = lib.custom.filter((item) => !marketplaceIds.has(item.id)).map((item) => ({ ...item, source: "custom" as const }));
        return [...anthropic, ...custom];
    };
    useEffect(() => {
        setDataReady(false);
        Promise.all([
            getAgentSkills(agentId).catch(() => [] as AgentSkillRecord[]),
            getSkillLibrary(agentId).catch(() => ({ anthropic: [] as SkillLibraryItem[], custom: [] as SkillLibraryItem[], premade: [] as PremadeLibraryItem[], marketplace: [] as MarketplaceLibraryItem[] })),
        ]).then(([sk, lib]) => {
            setAssigned(sk);
            setLibrary(normalizeLibrary(lib));
            setPremadeList((lib.premade ?? []).map((p) => ({ id: p.id, title: p.name, description: "", content: "" })));
            setMarketplaceList(lib.marketplace ?? []);
            setDataReady(true);
        });
    }, [agentId]);
    const assignedNames = new Set(assigned.map((s) => s.name));
    const assignedSkillIds = new Set(assigned.map((s) => s.skill_id ?? s.config?.skill_id).filter(Boolean));
    const systemAssignedNames = new Set(assigned.filter((s) => s.is_persona_default).map((s) => s.name));
    const unassigned = library.filter((s) => !assignedNames.has(s.name));
    const resolveLibrarySkillFromAssigned = (s: AgentSkillRecord): SkillLibraryItem | undefined => {
        const skillId = s.skill_id ?? s.config?.skill_id;
        if (skillId) {
            const byId = library.find((l) => l.id === skillId);
            if (byId)
                return byId;
        }
        return library.find((l) => l.name === s.name);
    };
    // An assigned row is a Marketplace copy when MyAMP tagged it with the listing it came
    // from (config.marketplace_tool_id, promoted like premade_id), or its skill_id is one.
    const marketplaceToolIdOf = (s: AgentSkillRecord): number | undefined => s.marketplace_tool_id ?? s.config?.marketplace_tool_id;
    const marketplaceItemFor = (s: AgentSkillRecord): MarketplaceLibraryItem | undefined => {
        const toolId = marketplaceToolIdOf(s);
        const skillId = s.skill_id ?? s.config?.skill_id;
        const found = marketplaceList.find((m) => (toolId != null && m.tool_id === toolId) || (!!skillId && m.id === skillId));
        if (found || toolId == null)
            return found;
        return { id: skillId ?? "", name: s.name, source: "marketplace", version: s.config?.version ?? null, tool_id: toolId };
    };
    const assignedMarketplaceRecord = (m: MarketplaceLibraryItem): AgentSkillRecord | undefined => assigned.find((s) => marketplaceToolIdOf(s) === m.tool_id || (s.skill_id ?? s.config?.skill_id) === m.id);
    function matchSearch(name: string, desc?: string) {
        if (!search.trim())
            return true;
        const q = search.toLowerCase();
        return name.toLowerCase().includes(q) || (desc ?? "").toLowerCase().includes(q);
    }
    const filteredAssigned = assigned.filter((s) => s.is_active !== false && matchSearch(s.name));
    const filteredPremade = premadeList.filter((p) => matchSearch(p.title, p.description));
    const filteredSystemAssigned = assigned.filter((s) => !!s.is_persona_default && !(s.premade_id ?? s.config?.premade_id) && matchSearch(s.name));
    const filteredCustom = library.filter((s) => s.source === "custom" && !systemAssignedNames.has(s.name) && matchSearch(s.name));
    const filteredMarketplace = marketplaceList.filter((m) => matchSearch(m.name));
    const openCreate = () => {
        setSkillName("");
        setSkillBody("");
        setEditorMode("edit");
        setPremadeReadonly(false);
        setPanel({ mode: "create" });
    };
    const openPremadeInEditor = (p: PremadeSkillRecord) => {
        setSkillName(p.title);
        setSkillBody(p.content);
        setEditorMode("edit");
        setPremadeReadonly(true);
        setPanel({ mode: "create" });
    };
    const openEditLibrary = async (s: SkillLibraryItem) => {
        setSkillName(s.name);
        setSkillBody("");
        setEditorMode("edit");
        setPanel({ mode: "edit-library", skill: s });
        if (s.source === "custom") {
            try {
                const res = await getSkillContent(s.id);
                setSkillBody(res.content ?? "");
            }
            catch {
            }
        }
        setTimeout(() => textEditorRef.current?.focus(), 100);
    };
    const handleCreateSkill = async () => {
        if (!skillName.trim() || !skillBody.trim())
            return;
        setSkillSaving(true);
        try {
            const newItem = await createSkillFromText(skillName.trim(), skillBody.trim(), false);
            const updated = await getSkillLibrary(agentId);
            setLibrary(normalizeLibrary(updated));
            await assignSkill(agentId, newItem);
            setAssigned(await getAgentSkills(agentId));
            setSkillName(skillName.trim());
            setPanel({ mode: "edit-library", skill: { ...newItem, source: "custom" } });
        }
        catch (e: any) {
            const message = e.message ?? "Failed to create skill";
            showSkillErrorToast(message);
        }
        finally {
            setSkillSaving(false);
        }
    };
    const handleSaveAndAssignPremade = async (premadeId?: number) => {
        if (!skillName.trim() || !skillBody.trim())
            return;
        setSkillSaving(true);
        try {
            const v1 = await createSkillFromText(skillName.trim(), skillBody.trim(), false, premadeId);
            const updated = await getSkillLibrary(agentId);
            setLibrary(normalizeLibrary(updated));
            await assignSkill(agentId, v1);
            setAssigned(await getAgentSkills(agentId));
            setSkillName(skillName.trim());
            setPremadeReadonly(true);
            setPanel({ mode: "create" });
        }
        catch (e: any) {
            const message = e.message ?? "Failed to save and assign skill";
            showSkillErrorToast(message);
        }
        finally {
            setSkillSaving(false);
        }
    };
    const handleSavePremadeOnly = async (premadeId?: number) => {
        if (!skillName.trim() || !skillBody.trim())
            return;
        setSkillSaving(true);
        try {
            await createSkillFromText(skillName.trim(), skillBody.trim(), false, premadeId);
            const updated = await getSkillLibrary(agentId);
            setLibrary(normalizeLibrary(updated));
            setPremadeReadonly(true);
        }
        catch (e: any) {
            const message = e.message ?? "Failed to save skill";
            showSkillErrorToast(message);
        }
        finally {
            setSkillSaving(false);
        }
    };
    const handleUpdateSkill = async () => {
        if (panel.mode !== "edit-library")
            return;
        if (!skillName.trim() || !skillBody.trim())
            return;
        setSkillSaving(true);
        try {
            const newItem = await updateSkillFromText(panel.skill.id, skillName.trim(), skillBody.trim(), false);
            const updated = await getSkillLibrary(agentId);
            setLibrary(normalizeLibrary(updated));
            if (assignedNames.has(panel.skill.name))
                setAssigned(await getAgentSkills(agentId));
            setPanel({ mode: "edit-library", skill: { ...newItem, source: "custom" } });
        }
        catch (e: any) {
            const message = e.message ?? "Failed to update skill";
            showSkillErrorToast(message);
        }
        finally {
            setSkillSaving(false);
        }
    };
    const handleAddSkill = async (s: SkillLibraryItem) => {
        if (adding)
            return;
        setAdding(s.id);
        try {
            await assignSkill(agentId, s);
            setAssigned(await getAgentSkills(agentId));
        }
        catch {
        }
        finally {
            setAdding(null);
        }
    };
    const handleRemoveSkill = async (id: number) => {
        setRemoving(id);
        try {
            await removeSkill(agentId, id);
            setAssigned((prev) => prev.filter((s) => s.id !== id));
            if (panel.mode === "view-assigned" && panel.skill.id === id)
                setPanel({ mode: "empty" });
        }
        catch {
        }
        finally {
            setRemoving(null);
            setConfirmRm(null);
        }
    };
    const handleAssignPremade = async (p: PremadeSkillRecord) => {
        if (assigning)
            return;
        setAssigning(p.id);
        try {
            await assignPremadeToAgent(agentId, p.id);
            setAssigned(await getAgentSkills(agentId));
        }
        catch {
        }
        finally {
            setAssigning(null);
        }
    };
    const handleAssignMarketplace = async (m: MarketplaceLibraryItem) => {
        if (assigningMarketplace)
            return;
        setAssigningMarketplace(m.id);
        try {
            await assignSkill(agentId, m);
            setAssigned(await getAgentSkills(agentId));
        }
        catch (e: any) {
            showSkillErrorToast(e.message ?? "Failed to assign skill");
        }
        finally {
            setAssigningMarketplace(null);
        }
    };
    const handleDeleteLibrarySkill = async (skillId: string) => {
        setDeletingLib(skillId);
        try {
            await deleteSkillFromLibrary(skillId);
            const updated = await getSkillLibrary(agentId);
            setLibrary(normalizeLibrary(updated));
            if (panel.mode === "edit-library" && panel.skill.id === skillId)
                setPanel({ mode: "empty" });
        }
        catch {
        }
        finally {
            setDeletingLib(null);
            setConfirmDeleteLib(null);
        }
    };
    const handleUploadSkill = async () => {
        if (!uploadTitle.trim() || !uploadFile)
            return;
        if (!/\.(zip|skill)$/i.test(uploadFile.name)) {
            setUploadError("Only .zip and .skill files are supported.");
            return;
        }
        setUploading(true);
        setUploadError("");
        try {
            await createSkillInLibrary(uploadTitle.trim(), uploadFile, false);
            const updated = await getSkillLibrary(agentId);
            setLibrary(normalizeLibrary(updated));
            setPanel({ mode: "empty" });
            setUploadTitle("");
            setUploadFile(null);
        }
        catch (e: any) {
            const message = e.message ?? "Upload failed";
            setUploadError(message);
            showSkillErrorToast(message);
        }
        finally {
            setUploading(false);
        }
    };
    const insertMd = useCallback((before: string, after: string, linePrefix = "") => {
        const el = textEditorRef.current;
        if (!el)
            return;
        const { selectionStart: s, selectionEnd: e, value } = el;
        const selected = value.slice(s, e);
        const replacement = linePrefix ? `${linePrefix}${selected || "text"}` : `${before}${selected || "text"}${after}`;
        setSkillBody(value.slice(0, s) + replacement + value.slice(e));
        setTimeout(() => {
            const newPos = s + replacement.length;
            el.setSelectionRange(newPos, newPos);
            el.focus();
        }, 0);
    }, []);
    // "Import doc" in the skill editor toolbar: the server converts a PDF / Word /
    // PowerPoint / Excel / ... file to Markdown and the text is dropped into the skill
    // body at the caret (or at the end when the editor is not focused). Nothing is
    // saved until the skill itself is saved.
    const handleImportDocument = useCallback(async (file: File) => {
        setImportingDoc(true);
        try {
            const res = await convertDocumentToMarkdown(file);
            const el = textEditorRef.current;
            const base = el ? el.value : "";
            const caret = el && document.activeElement === el ? el.selectionStart : base.length;
            const pos = Math.max(0, Math.min(caret, base.length));
            const before = base.slice(0, pos);
            const after = base.slice(pos);
            const lead = before.trim() ? (before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n") : "";
            const tail = after.trim() ? (after.startsWith("\n\n") ? "" : after.startsWith("\n") ? "\n" : "\n\n") : "";
            const head = before + lead + res.markdown;
            if (el) {
                setSkillBody(head + tail + after);
            }
            else {
                setSkillBody((prev) => (prev.trim() ? `${prev.replace(/\n*$/, "")}\n\n` : "") + res.markdown);
            }
            setTimeout(() => {
                const ta = textEditorRef.current;
                if (ta) {
                    ta.setSelectionRange(head.length, head.length);
                    ta.focus();
                }
            }, 0);
            if (res.truncated) {
                showSkillWarningToast(`${res.filename} is longer than the ${res.max_chars.toLocaleString()}-character limit, so only the first part was added. Attach the full document as a Knowledge Base file instead.`);
            }
            else {
                showSkillSuccessToast(`Added ${res.chars.toLocaleString()} characters from ${res.filename}`);
            }
        }
        catch (e: any) {
            showSkillErrorToast(e.message ?? "Failed to import document");
        }
        finally {
            setImportingDoc(false);
        }
    }, [showSkillErrorToast, showSkillSuccessToast, showSkillWarningToast]);
    const showAssignedGrp = filter === "all" || filter === "assigned";
    const showSystemGrp = filter === "all" || filter === "system";
    const showCustomGrp = filter === "all" || filter === "custom";
    const showMarketplaceGrp = (filter === "all" && marketplaceList.length > 0) || filter === "marketplace";
    if (!dataReady) {
        return (<div className="ca-fp-page dw-fp-flex-page">
        <div className="ca-fp-left">
          <div className="ca-fp-left__header">
            <div className="ca-fp-skel-bone dw-fp-skel-badge"/>
            <div className="ca-fp-skel-bone dw-fp-skel-btn-sm"/>
          </div>
          <div className="ca-fp-left__search-wrap">
            <div className="ca-fp-skel-bone dw-fp-skel-search"/>
          </div>
          <div className="ca-fp-left__filters">
            {[80, 70, 80].map((w, i) => (<div key={i} className="ca-fp-skel-bone dw-fp-skel-filter-chip" style={{ width: w }}/>))}
          </div>
          <div className="dw-fp-skel-rows">
            {[60, 75, 55, 70, 65].map((w, i) => (<div key={i} className="ca-fp-skill-row dw-fp-skel-row">
                <div className="ca-fp-skel-bone dw-fp-skel-row-avatar"/>
                <div className="dw-fp-skel-row-col">
                  <div className="ca-fp-skel-bone dw-fp-skel-row-title" style={{ width: `${w}%` }}/>
                  <div className="ca-fp-skel-bone dw-fp-skel-row-sub"/>
                </div>
              </div>))}
          </div>
        </div>
        <div className="ca-fp-right">
          <div className="ca-fp-editor">
            <div className="ca-fp-editor__topbar">
              <div className="ca-fp-editor__topbar-left">
                <div className="ca-fp-skel-bone dw-fp-skel-row-avatar"/>
                <div className="dw-fp-skel-topbar-col">
                  <div className="ca-fp-skel-bone dw-fp-skel-topbar-title"/>
                  <div className="ca-fp-skel-bone dw-fp-skel-topbar-sub"/>
                </div>
              </div>
              <div className="ca-fp-editor__topbar-actions">
                <div className="ca-fp-skel-bone dw-fp-skel-btn-md"/>
                <div className="ca-fp-skel-bone dw-fp-skel-btn-lg"/>
              </div>
            </div>
            <div className="ca-fp-editor__body">
              <div className="ca-fp-editor__toolbar-row">
                <div className="ca-fp-skel-bone dw-fp-skel-toolbar-btn-sm"/>
                <div className="ca-fp-skel-bone dw-fp-skel-toolbar-btn-lg"/>
              </div>
              <div className="ca-fp-skel-bone dw-fp-skel-body-fill"/>
            </div>
          </div>
        </div>
      </div>);
    }
    return (<div className="dw-skills-host">
      <div className="ca-fp-page dw-fp-flex-page">
        
        <div className="ca-fp-left">
          <div className="ca-fp-left__header">
            <h2 className="ca-fp-left__title">
              <FiZap size={15} style={{ color: "#a200ff" }}/> Skills
              <span className="ca-fp-left__count">{assigned.length + unassigned.length + premadeList.length + marketplaceList.filter((m) => !assignedMarketplaceRecord(m)).length}</span>
            </h2>
            <div className="ca-fp-left__header-actions">
              <button className="ca-fp-create-btn" onClick={openCreate}>
                <FiPlus size={12}/> Create
              </button>
              <button className={`ca-fp-upload-hdr-btn${panel.mode === "upload" ? " ca-fp-upload-hdr-btn--active" : ""}`} onClick={() => {
            setPanel({ mode: "upload" });
            setUploadTitle("");
            setUploadFile(null);
            setUploadError("");
        }}>
                <FiUpload size={12}/> Upload
              </button>
            </div>
          </div>

          <div className="ca-fp-left__search-wrap">
            <FiSearch size={13} className="ca-fp-left__search-icon"/>
            <input className="ca-fp-left__search" placeholder="Search skills…" value={search} onChange={(e) => setSearch(e.target.value)}/>
            {search && (<button className="ca-fp-left__search-clear" onClick={() => setSearch("")}>
                <FiX size={11}/>
              </button>)}
          </div>

          <div className={`ca-fp-left__filters${marketplaceList.length > 0 ? " ca-fp-left__filters--five" : ""}`}>
            {((marketplaceList.length > 0 ? ["all", "assigned", "system", "marketplace", "custom"] : ["all", "assigned", "system", "custom"]) as DwSkillFilter[]).map((f) => (<button key={f} className={`ca-fp-filter-tab${filter === f ? " ca-fp-filter-tab--active" : ""}`} onClick={() => {
                setFilter(f);
                if (f === "all") {
                    setAssignedExpanded(true);
                    setSystemExpanded(false);
                    setCustomExpanded(false);
                    setMarketplaceExpanded(false);
                }
                if (f === "assigned")
                    setAssignedExpanded(true);
                if (f === "system")
                    setSystemExpanded(true);
                if (f === "custom")
                    setCustomExpanded(true);
                if (f === "marketplace")
                    setMarketplaceExpanded(true);
            }}>
                {f === "all" ? "All" : f === "assigned" ? "Assigned" : f === "system" ? "System" : f === "marketplace" ? "Marketplace" : "Custom"}
                <span className="ca-fp-filter-tab__badge">
                  {f === "all"
                ? filteredAssigned.length + filteredPremade.length + filteredSystemAssigned.length + filteredMarketplace.length + filteredCustom.length
                : f === "assigned"
                    ? filteredAssigned.length
                    : f === "system"
                        ? filteredPremade.length + filteredSystemAssigned.length
                        : f === "marketplace"
                            ? filteredMarketplace.length
                            : filteredCustom.length}
                </span>
              </button>))}
          </div>

          <div className="ca-fp-left__list">
            
            {showAssignedGrp && (<>
                <DwSkillGroupHead tone="assigned" icon={<FiCheckCircle size={13}/>} title="Assigned" subtitle={`Active on ${agentName}`} count={filteredAssigned.length} expanded={assignedExpanded} onToggle={() => setAssignedExpanded((v) => !v)}/>
                {assignedExpanded && (<div className="dw-skill-sec-body dw-skill-sec-body--assigned">
                    {filteredAssigned.map((s) => {
                    const libSkill = resolveLibrarySkillFromAssigned(s);
                    const skillType = (s.skill_type || "").toLowerCase();
                    const resolvedPremadeId = s.premade_id ??
                        s.config?.premade_id ??
                        (skillType === "premade" ? premadeList.find((p) => p.title === s.name)?.id : undefined);
                    const resolvedSkillId = s.skill_id ?? s.config?.skill_id;
                    const marketplaceItem = marketplaceItemFor(s);
                    const isCustomSkill = !marketplaceItem && (skillType === "custom" || (!skillType && libSkill?.source === "custom"));
                    const isPremadeOrigin = !marketplaceItem && !isCustomSkill && (skillType === "premade" || resolvedPremadeId != null);
                    const sourceKey = s.is_persona_default
                        ? "system"
                        : marketplaceItem
                            ? "marketplace"
                            : isPremadeOrigin
                            ? "premade"
                            : isCustomSkill
                                ? "custom"
                                : (libSkill?.source ?? "anthropic");
                    const sourceLabel = s.is_persona_default
                        ? "System"
                        : marketplaceItem
                            ? "Marketplace"
                            : isPremadeOrigin
                            ? "Premade"
                            : dwSkillSourceLabel(isCustomSkill ? "custom" : libSkill?.source);
                    const isActive = (panel.mode === "view-assigned" && panel.skill.id === s.id) ||
                        (!!marketplaceItem && panel.mode === "view-marketplace" && panel.skill.tool_id === marketplaceItem.tool_id) ||
                        (panel.mode === "edit-library" && panel.skill.name === s.name) ||
                        (resolvedPremadeId &&
                            panel.mode === "create" &&
                            skillName === premadeList.find((p) => p.id === resolvedPremadeId)?.title &&
                            premadeReadonly);
                    return (<div key={s.id} className={`ca-fp-skill-row ca-fp-skill-row--assigned${isActive ? " ca-fp-skill-row--active" : ""}`} onClick={() => {
                            if (marketplaceItem) {
                                setPanel({ mode: "view-marketplace", skill: marketplaceItem });
                            }
                            else if (resolvedPremadeId) {
                                const pm = premadeList.find((p) => p.id === resolvedPremadeId);
                                if (pm)
                                    openPremadeInEditor(pm);
                                else
                                    setPanel({ mode: "view-assigned", skill: s });
                            }
                            else if (isCustomSkill) {
                                if (libSkill) {
                                    openEditLibrary(libSkill);
                                }
                                else if (resolvedSkillId) {
                                    openEditLibrary({
                                        id: resolvedSkillId,
                                        name: s.name,
                                        source: "custom",
                                        latest_version: "",
                                        created_at: s.created_at,
                                        updated_at: s.created_at,
                                    });
                                }
                                else {
                                    setPanel({ mode: "view-assigned", skill: s });
                                }
                            }
                            else {
                                setPanel({ mode: "view-assigned", skill: s });
                            }
                        }}>
                          <DwSkillIcon name={s.name} size={36}/>
                          <div className="ca-fp-skill-row__info">
                            <span className="ca-fp-skill-row__name">
                              {s.name}
                              {!resolvedPremadeId && !marketplaceItem && libSkill?.custom_version && (<span className="ca-fp-skill-row__name-ver"> {libSkill.custom_version}</span>)}
                            </span>
                            <span className="ca-fp-skill-row__ver">
                              <span className={`ca-fp-skill-row__src ca-fp-skill-row__src--${sourceKey}`}>{sourceLabel}</span>
                            </span>
                          </div>
                          <span className="ca-fp-skill-row__dot" title="Assigned & active"/>
                        </div>);
                })}
                    {filteredAssigned.length === 0 && <div className="ca-fp-left__empty">No assigned skills in this section.</div>}
                  </div>)}
              </>)}

            
            {showSystemGrp && (<>
                <DwSkillGroupHead tone="system" icon={<FiLock size={12}/>} title="System" subtitle="Premade & platform skills · assign only" count={filteredPremade.length + filteredSystemAssigned.length} expanded={systemExpanded} onToggle={() => setSystemExpanded((v) => !v)}/>

                {systemExpanded && (<div className="dw-skill-sec-body dw-skill-sec-body--system">
                    {filteredPremade.map((p) => {
                    const isActive = panel.mode === "create" && skillName === p.title && premadeReadonly;
                    const alreadyAssigned = assigned.some((s) => (s.premade_id ?? s.config?.premade_id) === p.id) ||
                        assignedNames.has(p.title) ||
                        assignedNames.has(`${p.title} v1`);
                    return (<div key={`system-premade-${p.id}`} className={`ca-fp-skill-row${isActive ? " ca-fp-skill-row--active" : ""}${alreadyAssigned ? " ca-fp-skill-row--assigned" : ""}`} onClick={() => openPremadeInEditor(p)}>
                          <DwSkillIcon name={p.title} size={36}/>
                          <div className="ca-fp-skill-row__info">
                            <span className="ca-fp-skill-row__name">{p.title}</span>
                            <span className="ca-fp-skill-row__ver">
                              <span className="ca-fp-skill-row__src ca-fp-skill-row__src--premade">Premade</span>
                            </span>
                            {p.description && <span className="ca-fp-skill-row__desc">{p.description}</span>}
                          </div>
                          {alreadyAssigned && <FiCheck size={11} style={{ color: "#22c55e", flexShrink: 0 }}/>}
                        </div>);
                })}

                    {filteredSystemAssigned.map((s) => {
                    const isActive = panel.mode === "view-assigned" && panel.skill.id === s.id;
                    const resolvedPremadeId = s.premade_id ?? s.config?.premade_id;
                    return (<div key={`system-assigned-${s.id}`} className={`ca-fp-skill-row ca-fp-skill-row--assigned${isActive ? " ca-fp-skill-row--active" : ""}`} onClick={() => {
                            if (resolvedPremadeId) {
                                const pm = premadeList.find((p) => p.id === resolvedPremadeId);
                                if (pm)
                                    openPremadeInEditor(pm);
                                else
                                    setPanel({ mode: "view-assigned", skill: s });
                            }
                            else {
                                setPanel({ mode: "view-assigned", skill: s });
                            }
                        }}>
                          <DwSkillIcon name={s.name} size={36}/>
                          <div className="ca-fp-skill-row__info">
                            <span className="ca-fp-skill-row__name">{s.name}</span>
                            <span className="ca-fp-skill-row__ver">
                              <span className="ca-fp-skill-row__src ca-fp-skill-row__src--system">System</span>
                            </span>
                          </div>
                          <span className="ca-fp-skill-row__dot" title="Assigned & active"/>
                        </div>);
                })}

                    {filteredPremade.length === 0 && filteredSystemAssigned.length === 0 && (<div className="ca-fp-left__empty">No system skills in this section.</div>)}
                  </div>)}
              </>)}

            
            {showMarketplaceGrp && (<>
                <DwSkillGroupHead tone="marketplace" icon={<FiShoppingBag size={12}/>} title="Marketplace" subtitle="Installed from the Marketplace · assign only" count={filteredMarketplace.length} expanded={marketplaceExpanded} onToggle={() => setMarketplaceExpanded((v) => !v)}/>

                {marketplaceExpanded && (<div className="dw-skill-sec-body dw-skill-sec-body--marketplace">
                    {filteredMarketplace.map((m) => {
                    const isActive = panel.mode === "view-marketplace" && panel.skill.tool_id === m.tool_id;
                    const alreadyAssigned = !!assignedMarketplaceRecord(m);
                    return (<div key={`marketplace-${m.tool_id}`} className={`ca-fp-skill-row${isActive ? " ca-fp-skill-row--active" : ""}${alreadyAssigned ? " ca-fp-skill-row--assigned" : ""}`} onClick={() => setPanel({ mode: "view-marketplace", skill: m })}>
                          <DwSkillIcon name={m.name} size={36}/>
                          <div className="ca-fp-skill-row__info">
                            <span className="ca-fp-skill-row__name">{m.name}</span>
                            <span className="ca-fp-skill-row__ver">
                              <span className="ca-fp-skill-row__src ca-fp-skill-row__src--marketplace">Marketplace</span>
                            </span>
                          </div>
                          {alreadyAssigned && <FiCheck size={11} style={{ color: "#22c55e", flexShrink: 0 }}/>}
                        </div>);
                })}

                    {filteredMarketplace.length === 0 && (<div className="ca-fp-left__empty">{search ? "No Marketplace skills match your search." : "No Marketplace skills yet. Install one from the Marketplace and it shows up here."}</div>)}
                  </div>)}
              </>)}

            {showCustomGrp && (<>
                <DwSkillGroupHead tone="custom" icon={<FiEdit3 size={12}/>} title="Custom" subtitle="Your skill library" count={filteredCustom.length} expanded={customExpanded} onToggle={() => setCustomExpanded((v) => !v)}/>

                {customExpanded && (<div className="dw-skill-sec-body dw-skill-sec-body--custom">
                    {filteredCustom.map((s) => {
                    const isActive = panel.mode === "edit-library" && panel.skill.id === s.id;
                    const isDeleting = deletingLib === s.id;
                    const confirmingDelete = confirmDeleteLib === s.id;
                    const isAssigned = assignedSkillIds.has(s.id) || assignedNames.has(s.name);
                    return (<div key={s.id} className={`ca-fp-skill-row${isActive ? " ca-fp-skill-row--active" : ""}${isAssigned ? " ca-fp-skill-row--assigned" : ""}`} onClick={() => {
                            if (!confirmingDelete)
                                openEditLibrary(s);
                        }}>
                          <DwSkillIcon name={s.name} size={36}/>
                          <div className="ca-fp-skill-row__info">
                            <span className="ca-fp-skill-row__name">
                              {s.name}
                              {s.custom_version && <span className="ca-fp-skill-row__name-ver"> {s.custom_version}</span>}
                            </span>
                            <span className="ca-fp-skill-row__ver">
                              <span className="ca-fp-skill-row__src ca-fp-skill-row__src--custom">Custom</span>
                            </span>
                          </div>
                          {isAssigned ? (<FiCheck size={11} style={{ color: "#22c55e", flexShrink: 0 }}/>) : confirmingDelete ? (<div className="ca-fp-skill-row__del-confirm" onClick={(e) => e.stopPropagation()}>
                              <button className="ca-fp-skill-row__del-yes" onClick={() => handleDeleteLibrarySkill(s.id)} disabled={isDeleting}>
                                {isDeleting ? <FiRefreshCw size={10} className="ca-fp-spin"/> : "Delete"}
                              </button>
                              <button className="ca-fp-skill-row__del-no" onClick={() => setConfirmDeleteLib(null)}>
                                Keep
                              </button>
                            </div>) : (<button className="ca-fp-skill-row__del-btn" title="Delete from library" onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDeleteLib(s.id);
                            }}>
                              <FiTrash2 size={11}/>
                            </button>)}
                        </div>);
                })}

                    {filteredCustom.length === 0 && <div className="ca-fp-left__empty">No custom skills in this section.</div>}
                  </div>)}
              </>)}

            {filteredAssigned.length === 0 && filteredPremade.length === 0 && filteredSystemAssigned.length === 0 && filteredMarketplace.length === 0 && filteredCustom.length === 0 && (<div className="ca-fp-left__empty">{search ? "No skills match your search." : "No skills yet. Create one to get started."}</div>)}
          </div>
        </div>

        
        <div className="ca-fp-right">
          {panel.mode === "empty" && (<div className="ca-fp-empty-state">
              <div className="ca-fp-empty-state__icon">
                <FiZap size={32}/>
              </div>
              <h3>Select a skill to view or edit</h3>
              <p>Choose from the list on the left, or create a new skill using the button above.</p>
              <button className="ca-fp-create-btn ca-fp-create-btn--lg" onClick={openCreate}>
                <FiPlus size={14}/> Create New Skill
              </button>
            </div>)}

          {panel.mode === "upload" && (<div className="ca-fp-editor">
              <div className="ca-fp-editor__topbar">
                <div className="ca-fp-editor__topbar-left">
                  <span className="ca-fp-editor__badge ca-fp-editor__badge--new">Upload Skill</span>
                  <span className="ca-fp-editor__skill-title">
                    Upload a .zip or .skill file
                  </span>
                </div>
                <div className="ca-fp-editor__topbar-actions">
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => {
                setPanel({ mode: "empty" });
                setUploadTitle("");
                setUploadFile(null);
                setUploadError("");
            }}>
                    <FiX size={13}/> Cancel
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleUploadSkill} disabled={uploading || !uploadTitle.trim() || !uploadFile}>
                    {uploading ? (<>
                        <FiRefreshCw size={12} className="ca-fp-spin"/> Uploading…
                      </>) : (<>
                        <FiCheck size={12}/> Upload Skill
                      </>)}
                  </button>
                </div>
              </div>
              <div className="ca-fp-upload-panel">
                <div className="ca-fp-upload-panel__field">
                  <label className="ca-fp-upload-panel__label">Skill Title</label>
                  <input className="ca-fp-upload-panel__input" placeholder="e.g. Quote Formatter" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} autoFocus/>
                </div>
                <div className="ca-fp-upload-panel__field">
                  <label className="ca-fp-upload-panel__label">File</label>
                  <input ref={fileRef} type="file" accept=".zip,.skill" className="dw-fp-hidden-input" onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                if (file && !/\.(zip|skill)$/i.test(file.name)) {
                    setUploadError("Only .zip and .skill files are supported.");
                    setUploadFile(null);
                    e.target.value = "";
                    return;
                }
                setUploadError("");
                setUploadFile(file);
            }}/>
                  <button className="ca-fp-upload-panel__dropzone" onClick={() => fileRef.current?.click()}>
                    <div className="ca-fp-upload-panel__dropzone-icon">
                      <FiUploadCloud size={26}/>
                    </div>
                    <div className="ca-fp-upload-panel__dropzone-text">
                      {uploadFile ? (<>
                          <strong>{uploadFile.name}</strong>
                          <span>Click to change file</span>
                        </>) : (<>
                          <strong>Click to choose file</strong>
                          <span>Supports .zip and .skill formats</span>
                        </>)}
                    </div>
                  </button>
                </div>
                {uploadError && <p className="ca-fp-upload-panel__error">{uploadError}</p>}
                <p className="ca-fp-upload-panel__hint">The skill will be added to your library. You can assign it to {agentName} after uploading.</p>
              </div>
            </div>)}

          {panel.mode === "create" &&
            (() => {
                const isPremadePrefill = premadeList.some((p) => p.title === skillName || `${p.title} v1` === skillName);
                const premadeItem = isPremadePrefill ? premadeList.find((p) => p.title === skillName || `${p.title} v1` === skillName) : null;
                const alreadyAssigned = isPremadePrefill && (assignedNames.has(skillName) || !!(premadeItem && assignedNames.has(`${premadeItem.title} v1`)));
                const isAssigning = isPremadePrefill && premadeItem ? assigning === premadeItem.id : false;
                if (isPremadePrefill) {
                    const premadeAssignedRecord = premadeItem ? assigned.find((s) => s.premade_id === premadeItem.id) : undefined;
                    const isRemovingAssigned = premadeAssignedRecord ? removing === premadeAssignedRecord.id : false;
                    return (<div className="ca-fp-editor">
                    <div className="ca-fp-editor__topbar">
                      <div className="ca-fp-editor__topbar-left">
                        <DwSkillIcon name={skillName} size={36}/>
                        <div>
                          <div className="dw-fp-inline-title-row">
                            <span className="ca-fp-editor__skill-title">{skillName}</span>
                            <span className="ca-fp-editor__badge ca-fp-editor__badge--premade">Premade</span>
                            {alreadyAssigned && <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Assigned</span>}
                          </div>
                          {premadeItem?.description && <span className="ca-fp-editor__skill-sub">{premadeItem.description}</span>}
                        </div>
                      </div>
                      <div className="ca-fp-editor__topbar-actions">
                        <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => {
                            setPanel({ mode: "empty" });
                            setSkillName("");
                            setSkillBody("");
                        }}>
                          <FiX size={13}/> Close
                        </button>
                        {!premadeAssignedRecord ? (<button className="ca-fp-editor__btn ca-fp-editor__btn--assign" onClick={() => premadeItem && handleAssignPremade(premadeItem)} disabled={!!assigning || alreadyAssigned}>
                            {isAssigning ? (<>
                                <FiRefreshCw size={12} className="ca-fp-spin"/> Assigning…
                              </>) : alreadyAssigned ? (<>
                                <FiCheck size={12}/> Assigned
                              </>) : (<>
                                <FiPlus size={12}/> Assign to {agentName}
                              </>)}
                          </button>) : confirmRm === premadeAssignedRecord.id ? (<>
                            <span className="dw-fp-confirm-label">Unassign?</span>
                            <button className="ca-fp-editor__btn ca-fp-editor__btn--danger" onClick={() => handleRemoveSkill(premadeAssignedRecord.id)} disabled={isRemovingAssigned}>
                              {isRemovingAssigned ? <FiRefreshCw size={12} className="ca-fp-spin"/> : "Yes, Unassign"}
                            </button>
                            <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setConfirmRm(null)}>
                              No
                            </button>
                          </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" onClick={() => setConfirmRm(premadeAssignedRecord.id)}>
                            <FiTrash2 size={12}/> Unassign
                          </button>)}
                      </div>
                    </div>
                    <div className="ca-fp-anthropic-view">
                      <div className="ca-fp-anthropic-view__banner">
                        <div className="ca-fp-anthropic-view__banner-icon">
                          <FiLock size={16}/>
                        </div>
                        <div className="ca-fp-anthropic-view__banner-text">
                          <span className="ca-fp-anthropic-view__banner-title">Premade Skill</span>
                          <span className="ca-fp-anthropic-view__banner-desc">
                            This is a premade skill. Prompt content is hidden and cannot be viewed or updated here. You can only assign or unassign
                            this skill for {agentName}.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>);
                }
                return (<div className="ca-fp-editor">
                  <div className="ca-fp-editor__topbar">
                    <div className="ca-fp-editor__topbar-left">
                      {isPremadePrefill ? (<>
                          <DwSkillIcon name={skillName} size={36}/>
                          <div>
                            <div className="dw-fp-inline-title-row">
                              <span className="ca-fp-editor__skill-title">{skillName}</span>
                              <span className="ca-fp-editor__badge ca-fp-editor__badge--premade">Premade</span>
                            </div>
                            {premadeItem?.description && <span className="ca-fp-editor__skill-sub">{premadeItem.description}</span>}
                          </div>
                        </>) : (<>
                          <span className="ca-fp-editor__badge ca-fp-editor__badge--new">New Skill</span>
                          <input className="ca-fp-editor__name-input" placeholder="Skill name (e.g. Quote Formatter)" value={skillName} onChange={(e) => setSkillName(e.target.value)} autoFocus/>
                        </>)}
                    </div>
                    <div className="ca-fp-editor__topbar-actions">
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => {
                        if (isPremadePrefill) {
                            setPremadeReadonly(true);
                        }
                        else {
                            setPanel({ mode: "empty" });
                            setSkillName("");
                            setSkillBody("");
                        }
                    }}>
                        <FiX size={13}/> Cancel
                      </button>
                      {isPremadePrefill ? (<>
                          <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" title="Create a custom copy in the library only. It will not be assigned to this agent." onClick={() => handleSavePremadeOnly(premadeItem?.id)} disabled={skillSaving || !skillName.trim() || !skillBody.trim()}>
                            {skillSaving ? (<>
                                <FiRefreshCw size={12} className="ca-fp-spin"/> Creating library copy…
                              </>) : ("Copy to Library Only")}
                          </button>
                          <button className="ca-fp-editor__btn ca-fp-editor__btn--save" title="Create a custom copy and assign it to this agent immediately." onClick={() => handleSaveAndAssignPremade(premadeItem?.id)} disabled={skillSaving || !skillName.trim() || !skillBody.trim()}>
                            {skillSaving ? (<>
                                <FiRefreshCw size={12} className="ca-fp-spin"/> Creating and assigning…
                              </>) : (<>
                                <FiCheck size={12}/> Copy + Assign to Agent
                              </>)}
                          </button>
                        </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleCreateSkill} disabled={skillSaving || !skillName.trim() || !skillBody.trim()}>
                          {skillSaving ? (<>
                              <FiRefreshCw size={12} className="ca-fp-spin"/> Creating…
                            </>) : (<>
                              <FiCheck size={12}/> Create New Skill &amp; Assign
                            </>)}
                        </button>)}
                    </div>
                  </div>
                  <DwEditorBody mode={editorMode} onModeChange={setEditorMode} body={skillBody} onBodyChange={setSkillBody} textEditorRef={textEditorRef} insertMd={insertMd} onImportDocument={handleImportDocument} importAccept={SKILL_DOCUMENT_ACCEPT} importing={importingDoc} hint={isPremadePrefill
                        ? `Copy to Library Only: creates "${skillName} v1" in your library. Copy + Assign to Agent: creates it and assigns it now.`
                        : undefined}/>
                </div>);
            })()}

          {panel.mode === "view-marketplace" &&
            (() => {
                const m = panel.skill;
                const assignedRecord = assignedMarketplaceRecord(m);
                const isAssigning = assigningMarketplace === m.id;
                const isRemovingAssigned = assignedRecord ? removing === assignedRecord.id : false;
                return (<div className="ca-fp-editor">
                    <div className="ca-fp-editor__topbar">
                      <div className="ca-fp-editor__topbar-left">
                        <DwSkillIcon name={m.name} size={36}/>
                        <div>
                          <div className="dw-fp-inline-title-row">
                            <span className="ca-fp-editor__skill-title">{m.name}</span>
                            <span className="ca-fp-editor__badge ca-fp-editor__badge--marketplace">Marketplace</span>
                            {assignedRecord && <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Assigned</span>}
                          </div>
                          {m.version && !/^\d{8,}$/.test(m.version) && <span className="ca-fp-editor__skill-sub">v{m.version}</span>}
                        </div>
                      </div>
                      <div className="ca-fp-editor__topbar-actions">
                        <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setPanel({ mode: "empty" })}>
                          <FiX size={13}/> Close
                        </button>
                        {!assignedRecord ? (<button className="ca-fp-editor__btn ca-fp-editor__btn--assign" onClick={() => handleAssignMarketplace(m)} disabled={!!assigningMarketplace || !m.id}>
                            {isAssigning ? (<>
                                <FiRefreshCw size={12} className="ca-fp-spin"/> Assigning…
                              </>) : (<>
                                <FiPlus size={12}/> Assign to {agentName}
                              </>)}
                          </button>) : confirmRm === assignedRecord.id ? (<>
                            <span className="dw-fp-confirm-label">Unassign?</span>
                            <button className="ca-fp-editor__btn ca-fp-editor__btn--danger" onClick={() => handleRemoveSkill(assignedRecord.id)} disabled={isRemovingAssigned}>
                              {isRemovingAssigned ? <FiRefreshCw size={12} className="ca-fp-spin"/> : "Yes, Unassign"}
                            </button>
                            <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setConfirmRm(null)}>
                              No
                            </button>
                          </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" onClick={() => setConfirmRm(assignedRecord.id)}>
                            <FiTrash2 size={12}/> Unassign
                          </button>)}
                      </div>
                    </div>
                    <div className="ca-fp-anthropic-view">
                      <div className="ca-fp-anthropic-view__banner">
                        <div className="ca-fp-anthropic-view__banner-icon">
                          <FiLock size={16}/>
                        </div>
                        <div className="ca-fp-anthropic-view__banner-text">
                          <span className="ca-fp-anthropic-view__banner-title">Marketplace Skill</span>
                          <span className="ca-fp-anthropic-view__banner-desc">
                            Marketplace skill: installed from the Marketplace; its instructions stay private to the publisher. You can only
                            assign or unassign this skill for {agentName}.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>);
            })()}

          {panel.mode === "edit-library" &&
            (() => {
                const s = panel.skill;
                const isAssigned = assignedNames.has(s.name);
                const assignedRecord = assigned.find((a) => a.name === s.name);
                const isSystemSkill = systemAssignedNames.has(s.name);
                const isRemoving = assignedRecord ? removing === assignedRecord.id : false;
                return (<div className="ca-fp-editor">
                  <div className="ca-fp-editor__topbar">
                    <div className="ca-fp-editor__topbar-left">
                      <DwSkillIcon name={s.name} size={36}/>
                      <div>
                        <div className="dw-fp-inline-title-row">
                          <span className="ca-fp-editor__skill-title">{s.name}</span>
                          <span className={`ca-fp-editor__badge ca-fp-editor__badge--${isSystemSkill ? "assigned" : s.source}`}>
                            {isSystemSkill ? "System" : dwSkillSourceLabel(s.source)}
                          </span>
                          {isAssigned && <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Assigned</span>}
                        </div>
                        {(s.custom_version || (s.latest_version && !/^\d{8,}$/.test(s.latest_version))) && (<span className="ca-fp-editor__skill-sub">{s.custom_version ?? `v${s.latest_version}`}</span>)}
                      </div>
                    </div>
                    <div className="ca-fp-editor__topbar-actions">
                      {!isAssigned && s.source === "custom" && !isSystemSkill && (<button className="ca-fp-editor__btn ca-fp-editor__btn--assign" onClick={() => handleAddSkill(s)} disabled={!!adding}>
                          {adding === s.id ? (<>
                              <FiRefreshCw size={12} className="ca-fp-spin"/> Adding…
                            </>) : (<>
                              <FiPlus size={12}/> Assign to {agentName}
                            </>)}
                        </button>)}
                      {isAssigned &&
                        assignedRecord &&
                        !isSystemSkill &&
                        (confirmRm === assignedRecord.id ? (<>
                            <span className="dw-fp-confirm-label">Remove?</span>
                            <button className="ca-fp-editor__btn ca-fp-editor__btn--danger" onClick={() => handleRemoveSkill(assignedRecord.id)} disabled={isRemoving}>
                              {isRemoving ? <FiRefreshCw size={12} className="ca-fp-spin"/> : "Yes, Remove"}
                            </button>
                            <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setConfirmRm(null)}>
                              No
                            </button>
                          </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" onClick={() => setConfirmRm(assignedRecord.id)}>
                            <FiTrash2 size={12}/> Remove
                          </button>))}
                      {s.source === "custom" && !isSystemSkill && (<button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleUpdateSkill} disabled={skillSaving || !skillName.trim() || !skillBody.trim()}>
                          {skillSaving ? (<>
                              <FiRefreshCw size={12} className="ca-fp-spin"/> Updating…
                            </>) : (<>
                              <FiCheck size={12}/> Update Skill
                            </>)}
                        </button>)}
                      {s.source === "custom" && !isSystemSkill && (<button className="ca-fp-editor__btn ca-fp-editor__btn--assign" onClick={() => setShareMenuFor(s)} disabled={skillSaving || !skillName.trim() || !skillBody.trim()}>
                          <FiShare2 size={12}/> Share
                        </button>)}
                    </div>
                  </div>
                  {isSystemSkill ? (<div className="ca-fp-anthropic-view">
                      <div className="ca-fp-anthropic-view__banner">
                        <div className="ca-fp-anthropic-view__banner-icon">
                          <FiLock size={16}/>
                        </div>
                        <div className="ca-fp-anthropic-view__banner-text">
                          <span className="ca-fp-anthropic-view__banner-title">System Skill</span>
                          <span className="ca-fp-anthropic-view__banner-desc">
                            This is a default persona system skill. It cannot be updated or removed from this agent.
                          </span>
                        </div>
                      </div>
                    </div>) : s.source === "custom" ? (<DwEditorBody mode={editorMode} onModeChange={setEditorMode} body={skillBody} onBodyChange={setSkillBody} textEditorRef={textEditorRef} insertMd={insertMd} onImportDocument={handleImportDocument} importAccept={SKILL_DOCUMENT_ACCEPT} importing={importingDoc} hint="Edit and save — updates the same skill in place (version increments automatically)."/>) : (<div className="ca-fp-anthropic-view">
                      <div className="ca-fp-anthropic-view__banner">
                        <div className="ca-fp-anthropic-view__banner-icon">
                          <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
                            <g transform="translate(20,20)">
                              {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (<rect key={a} x="-2" y="-14" width="4" height="9" rx="2" fill="rgba(162,0,255,0.82)" transform={`rotate(${a})`}/>))}
                            </g>
                          </svg>
                        </div>
                        <div className="ca-fp-anthropic-view__banner-text">
                          <span className="ca-fp-anthropic-view__banner-title">Agent Studio-provided skill</span>
                          <span className="ca-fp-anthropic-view__banner-desc">
                            This skill is maintained by Agent Studio. Its content is managed externally and cannot be edited here. You can assign it
                            to {agentName} and it will be applied automatically.
                          </span>
                        </div>
                      </div>
                      <div className="ca-fp-anthropic-view__meta-grid">
                        <div className="ca-fp-anthropic-view__meta-card">
                          <span className="ca-fp-anthropic-view__meta-label">Version</span>
                          <span className="ca-fp-anthropic-view__meta-val">{s.latest_version}</span>
                        </div>
                        <div className="ca-fp-anthropic-view__meta-card">
                          <span className="ca-fp-anthropic-view__meta-label">Source</span>
                          <span className="ca-fp-anthropic-view__meta-val ca-fp-anthropic-view__meta-val--orange">Agent Studio</span>
                        </div>
                        <div className="ca-fp-anthropic-view__meta-card">
                          <span className="ca-fp-anthropic-view__meta-label">Created</span>
                          <span className="ca-fp-anthropic-view__meta-val">{new Date(s.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="ca-fp-anthropic-view__meta-card">
                          <span className="ca-fp-anthropic-view__meta-label">Status</span>
                          <span className={`ca-fp-anthropic-view__meta-val ${isAssigned ? "ca-fp-anthropic-view__meta-val--green" : "ca-fp-anthropic-view__meta-val--muted"}`}>
                            {isAssigned ? "Assigned to agent" : "Not assigned"}
                          </span>
                        </div>
                      </div>
                      {!isAssigned && (<div className="ca-fp-anthropic-view__cta">
                          <button className="ca-fp-editor__btn ca-fp-editor__btn--assign ca-fp-anthropic-view__cta-btn" onClick={() => handleAddSkill(s)} disabled={!!adding}>
                            {adding === s.id ? (<>
                                <FiRefreshCw size={13} className="ca-fp-spin"/> Assigning…
                              </>) : (<>
                                <FiPlus size={13}/> Assign to {agentName}
                              </>)}
                          </button>
                          <span className="ca-fp-anthropic-view__cta-hint">The skill will be added to {agentName}'s active skill set.</span>
                        </div>)}
                      {isAssigned && (<div className="ca-fp-anthropic-view__assigned-state">
                          <FiCheck size={14} style={{ color: "#22c55e" }}/>
                          <span>This skill is active and assigned to {agentName}.</span>
                        </div>)}
                    </div>)}
                </div>);
            })()}

          {panel.mode === "view-assigned" &&
            (() => {
                const s = panel.skill;
                const isSystemSkill = !!s.is_persona_default;
                const resolvedPremadeId = s.premade_id ?? s.config?.premade_id;
                const isRemoving = removing === s.id;
                const libSkill = resolveLibrarySkillFromAssigned(s);
                const resolvedSkillId = s.skill_id ?? s.config?.skill_id;
                const sourceFromAssigned = (s.skill_type || "").toLowerCase() === "custom" ? "custom" : "anthropic";
                const assignedSource = libSkill?.source ?? sourceFromAssigned;
                const isCustomSkill = assignedSource === "custom";
                const isPremadeSkill = !isSystemSkill && !isCustomSkill && !!resolvedPremadeId;
                const skillTag = isSystemSkill ? "System" : isPremadeSkill ? "Premade" : "Custom";
                const canEdit = !isSystemSkill && isCustomSkill && !!resolvedSkillId;
                return (<div className="ca-fp-editor">
                  <div className="ca-fp-editor__topbar">
                    <div className="ca-fp-editor__topbar-left">
                      <DwSkillIcon name={s.name} size={36}/>
                      <div>
                        <div className="dw-fp-inline-title-row">
                          <span className="ca-fp-editor__skill-title">{s.name}</span>
                          <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">{skillTag}</span>
                          <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Assigned</span>
                        </div>
                        <span className="ca-fp-editor__skill-sub">{skillTag.toLowerCase()}</span>
                      </div>
                    </div>
                    <div className="ca-fp-editor__topbar-actions">
                      {canEdit && (<button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => openEditLibrary(libSkill ?? {
                            id: resolvedSkillId!,
                            name: s.name,
                            source: "custom",
                            latest_version: "",
                            created_at: s.created_at,
                            updated_at: s.created_at,
                        })}>
                          <FiEdit3 size={12}/> Edit
                        </button>)}
                      {!isSystemSkill &&
                        (confirmRm === s.id ? (<>
                            <span className="dw-fp-confirm-label">Remove?</span>
                            <button className="ca-fp-editor__btn ca-fp-editor__btn--danger" onClick={() => handleRemoveSkill(s.id)} disabled={isRemoving}>
                              {isRemoving ? <FiRefreshCw size={12} className="ca-fp-spin"/> : "Yes, Remove"}
                            </button>
                            <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setConfirmRm(null)}>
                              No
                            </button>
                          </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" onClick={() => setConfirmRm(s.id)}>
                            <FiTrash2 size={12}/> Remove
                          </button>))}
                    </div>
                  </div>
                  {isSystemSkill || isPremadeSkill ? (<div className="ca-fp-anthropic-view">
                      <div className="ca-fp-anthropic-view__banner">
                        <div className="ca-fp-anthropic-view__banner-icon">
                          <FiLock size={16}/>
                        </div>
                        <div className="ca-fp-anthropic-view__banner-text">
                          <span className="ca-fp-anthropic-view__banner-title">{isSystemSkill ? "System Skill" : "Premade Skill"}</span>
                          <span className="ca-fp-anthropic-view__banner-desc">
                            {isSystemSkill
                            ? "This is a system skill. Prompt content is hidden and cannot be viewed or updated here."
                            : `This is a premade skill. Prompt content is hidden and cannot be viewed or updated here. You can only assign or unassign this skill for ${agentName}.`}
                          </span>
                        </div>
                      </div>
                      <div className="ca-fp-anthropic-view__meta-grid">
                        <div className="ca-fp-anthropic-view__meta-card">
                          <span className="ca-fp-anthropic-view__meta-label">Status</span>
                          <span className="ca-fp-anthropic-view__meta-val ca-fp-anthropic-view__meta-val--green">Assigned to agent</span>
                        </div>
                        <div className="ca-fp-anthropic-view__meta-card">
                          <span className="ca-fp-anthropic-view__meta-label">Type</span>
                          <span className="ca-fp-anthropic-view__meta-val">{skillTag}</span>
                        </div>
                        <div className="ca-fp-anthropic-view__meta-card">
                          <span className="ca-fp-anthropic-view__meta-label">Added</span>
                          <span className="ca-fp-anthropic-view__meta-val">{new Date(s.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>) : (<div className="ca-fp-editor__readonly">
                      <div className="ca-fp-editor__readonly-meta">
                        <span>
                          <strong>Status:</strong> Active & Assigned
                        </span>
                        <span>
                          <strong>Type:</strong> {skillTag}
                        </span>
                        <span>
                          <strong>Added:</strong> {new Date(s.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {canEdit && (<p className="ca-fp-editor__readonly-msg dw-fp-mt-16">
                          Click <strong>Edit</strong> to modify this custom skill.
                        </p>)}
                    </div>)}
                </div>);
            })()}
        </div>
      </div>
      {shareMenuFor && (<SkillShareMenuModal skillId={shareMenuFor.id} skillName={skillName} skillBody={skillBody} isAgencyOwner={isAgencyOwner} onClose={() => setShareMenuFor(null)}/>)}
    </div>);
};
/* ─────────────────────────────────────────────
   DwIntegrationsTab

   API Integration Wizard tools each need to be turned on per Digital
   Workforce agent — being installed (personal or agency-wide) just makes
   one eligible, same as Skills need per-agent assignment via skills.md.
   Unlike the "Tools" tab above (DwMcpTab, Composio/report tools), this one
   goes through enableIntegrationForAgent/disableIntegrationForAgent
   specifically, which work on system/persona agents — the generic
   custom-tools endpoint that tab uses does not.
───────────────────────────────────────────── */
const MYTOOLS_API_BASE = process.env.REACT_APP_Auth_Url || "";
function myToolsAuthHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken") || ""}` };
}
async function fetchInstalledIntegrations(): Promise<Array<{ myToolId: string; apiIntegrationId: number; name: string; description?: string }>> {
  const res = await fetch(`${MYTOOLS_API_BASE}/api/MyTools/installed`, { headers: myToolsAuthHeaders() });
  if (!res.ok) return [];
  const json = await res.json();
  const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
  return list
    .filter((t) => t.type === 5 && t.apiIntegrationId != null)
    .map((t) => ({ myToolId: String(t.id), apiIntegrationId: t.apiIntegrationId, name: t.name, description: t.description }));
}

interface DwIntegrationRow {
  integrationId: number; myToolId: string; name: string; description?: string; icon: string; host: string; enabled: boolean;
  // Only its creator can share it (AMPApi CreateShare / publish are owner-only).
  ownedByMe: boolean;
}

function hostOf(url?: string | null): string {
  if (!url) return "";
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** Same two-pane layout as the Tools tab (DwMcpTab): installed integrations on the
 *  left, the selected one's details and Enable/Disable on the right. */
export const DwIntegrationsTab: React.FC<{ agentId: string; agentName: string }> = ({ agentId, agentName }) => {
  const isAgencyOwner = readIsAgencyOwner();
  const navigate = useNavigate();
  const [rows, setRows] = useState<DwIntegrationRow[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareTool, setShareTool] = useState<MyToolSummary | null>(null);
  const [loadingShare, setLoadingShare] = useState(false);
  const { showError: showShareError } = useToast();

  const load = useCallback(async (keepSelection = false) => {
    setError(null);
    try {
      const [installed, toolsResponse] = await Promise.all([
        fetchInstalledIntegrations(),
        // attached_only: the full listing builds the whole tool catalog and is slow;
        // only which tools are on matters here.
        getAgentCustomTools(agentId, true).catch(() => null) as Promise<unknown>,
      ]);
      // Each installed integration's own record gives its chat tool name (the slug,
      // MyAMP compiler._tool_name) - including ones another agency made Global,
      // which listIntegrations (this agency's own) doesn't return.
      const details = await Promise.all(installed.map((i) => getIntegration(i.apiIntegrationId).catch(() => null)));
      const attachedNames = attachedToolNames(toolsResponse);
      const next = installed.map((i, n) => {
        const integration = details[n]?.integration;
        return {
          integrationId: i.apiIntegrationId,
          myToolId: i.myToolId,
          ownedByMe: !!integration && String(integration.user_id) === (localStorage.getItem("userId") || ""),
          name: i.name,
          description: i.description,
          icon: integration?.icon || "🔌",
          host: hostOf(integration?.base_url),
          enabled: !!integration && attachedNames.has(integration.slug),
        };
      });
      setRows(next);
      setSelectedId((prev) => {
        if (keepSelection && prev !== null && next.some((r) => r.integrationId === prev)) return prev;
        return (next.find((r) => r.enabled) ?? next[0])?.integrationId ?? null;
      });
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : "Couldn't load integrations for this agent.");
    }
  }, [agentId]);

  useEffect(() => {
    setRows(null);
    setSearch("");
    load();
  }, [load]);

  const selected = rows?.find((r) => r.integrationId === selectedId) ?? null;

  // Same Share menu the Tools tab opens for an MCP server.
  const openShare = async (myToolId: string) => {
    setLoadingShare(true);
    try {
      const tool = await getMyTool(myToolId);
      if (tool) setShareTool(tool);
      else showShareError("Couldn't find your listing for this integration.");
    } catch (e: unknown) {
      showShareError(e instanceof Error ? e.message : "Failed to open Share");
    } finally {
      setLoadingShare(false);
    }
  };
  // upsertMyTool's UPDATE overwrites every column, so send back every field the row has.
  const shareWithAgency = async () => {
    if (!shareTool) return;
    const updated = { ...shareTool, visibility: "my_agency" as const };
    setShareTool(updated);
    await upsertMyTool({
      id: Number(updated.id), type: TOOL_TYPE_TO_INT[updated.type], name: updated.name, category: updated.category,
      icon: updated.icon, description: updated.description, skills: updated.skills, kbFileNames: updated.kbFileNames,
      visibility: 1,
    });
  };

  const setEnabled = async (row: DwIntegrationRow, on: boolean) => {
    setToggling(true);
    setError(null);
    try {
      if (on) await enableIntegrationForAgent(agentId, row.integrationId);
      else await disableIntegrationForAgent(agentId, row.integrationId);
      setConfirmDisable(false);
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't update this integration for the agent.");
    } finally {
      setToggling(false);
    }
  };

  const q = search.trim().toLowerCase();
  const matches = (r: DwIntegrationRow) =>
    !q || r.name.toLowerCase().includes(q) || r.host.toLowerCase().includes(q) || (r.description ?? "").toLowerCase().includes(q);
  const enabledRows = (rows ?? []).filter((r) => r.enabled && matches(r));
  const installedRows = (rows ?? []).filter((r) => !r.enabled && matches(r));

  const renderRow = (r: DwIntegrationRow) => (
    <div key={r.integrationId}
      className={`dw-mcp-row${r.enabled ? " dw-mcp-row--connected" : ""}${r.integrationId === selectedId ? " dw-mcp-row--active" : ""}`}
      onClick={() => { setSelectedId(r.integrationId); setConfirmDisable(false); setError(null); }}>
      <div className={`dw-mcp-icon${r.enabled ? " dw-mcp-icon--connected" : ""}`}><span aria-hidden="true">{r.icon}</span></div>
      <div className="dw-mcp-row__info">
        <span className="dw-mcp-row__name">{r.name}</span>
        {r.host && <span className="dw-mcp-row__url">{r.host}</span>}
      </div>
      {r.enabled && <span className="dw-mcp-dot" title="Connected"/>}
    </div>
  );

  if (rows === null) {
    return (<div className="dw-mcp-wrap">
      <div className="dw-mcp-list">
        <div className="dw-mcp-list__header">
          <span className="dw-mcp-list__title"><FiDatabase size={11} style={{ color: "#a200ff" }}/> Integrations</span>
        </div>
        {[0, 1, 2].map((i) => (<div key={i} className="dw-mcp-row dw-mcp-row--skeleton">
          <div className="dw-mcp-icon"><FiDatabase size={12}/></div>
          <div className="dw-mcp-skeleton__lines">
            <div className="dw-mcp-skeleton__line dw-mcp-skeleton__line--name" style={{ width: `${60 + i * 10}%` }}/>
            <div className="dw-mcp-skeleton__line dw-mcp-skeleton__line--desc"/>
          </div>
        </div>))}
      </div>
      <div className="dw-mcp-editor">
        <div className="dw-mcp-editor__body">
          <div className="dw-mcp-editor__topbar">
            <div className="ca-fp-skel-bone dw-fp-skel-row-avatar"/>
            <div className="dw-fp-skel-topbar-col--minw0">
              <div className="ca-fp-skel-bone dw-fp-skel-topbar-title"/>
              <div className="ca-fp-skel-bone dw-fp-skel-topbar-sub-wide"/>
            </div>
          </div>
        </div>
      </div>
    </div>);
  }

  return (<div className="dw-mcp-wrap">
    <div className="dw-mcp-list">
      <div className="dw-mcp-list__header">
        <span className="dw-mcp-list__title">
          <FiDatabase size={11} style={{ color: "#a200ff" }}/>
          Integrations
          <span className="dw-mcp-count">{rows.length}</span>
        </span>
      </div>

      <div className="dw-mcp-search-wrap">
        <FiSearch size={11} className="dw-mcp-search-icon"/>
        <input className="dw-mcp-search" placeholder="Search integrations…" value={search} onChange={(e) => setSearch(e.target.value)}/>
        {search && (<button className="dw-mcp-search-clear" onClick={() => setSearch("")}><FiX size={10}/></button>)}
      </div>

      <div className="dw-mcp-list__rows">
        {enabledRows.length > 0 && (<>
          <div className="dw-mcp-group-label">Connected <span className="dw-mcp-group-badge">{enabledRows.length}</span></div>
          {enabledRows.map(renderRow)}
        </>)}
        {installedRows.length > 0 && (<>
          <div className="dw-mcp-group-label" style={{ marginTop: enabledRows.length > 0 ? 10 : 0 }}>
            Available <span className="dw-mcp-group-badge">{installedRows.length}</span>
          </div>
          {installedRows.map(renderRow)}
        </>)}
        {enabledRows.length === 0 && installedRows.length === 0 && (
          <div className="dw-tools-empty">{search ? "No integrations match." : "No API integrations installed yet."}</div>
        )}
      </div>

      <div className="dw-mcp-cta">
        <p className="dw-mcp-cta__text">Install API integrations from the Marketplace.</p>
        <button className="dw-mcp-cta__btn" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=marketplace`)}>
          Marketplace <FiArrowRight size={11}/>
        </button>
      </div>
    </div>

    <div className="dw-mcp-editor">
      {!selected ? (<div className="dw-mcp-editor__empty">
        <FiDatabase size={28} style={{ color: "rgba(162,0,255,0.25)" }}/>
        <div>{rows.length ? "Select an integration to see what it does" : "Install an API integration to use it with this agent"}</div>
      </div>) : (<div className="dw-mcp-editor__body">
        <div className="dw-mcp-editor__topbar">
          <div className={`dw-mcp-icon dw-mcp-icon--lg${selected.enabled ? " dw-mcp-icon--connected" : ""}`}><span aria-hidden="true">{selected.icon}</span></div>
          <div className="dw-fp-flex-minw0">
            <div className="dw-fp-inline-title-row">
              <span className="dw-mcp-editor__name">{selected.name}</span>
              <span className={`dw-mcp-editor__badge ${selected.enabled ? "dw-mcp-editor__badge--connected" : "dw-mcp-editor__badge--platform"}`}>
                {selected.enabled ? "Connected" : "Available"}
              </span>
            </div>
            {selected.host && <span className="dw-mcp-editor__url">{selected.host}</span>}
          </div>
          <div className="dw-mcp-editor__actions">
            {selected.ownedByMe && (<button className="dw-mcp-btn dw-mcp-btn--ghost" onClick={() => openShare(selected.myToolId)} disabled={loadingShare}>
              <FiShare2 size={11}/> Share
            </button>)}
            {!selected.enabled ? (<button className="dw-mcp-btn dw-mcp-btn--connect" onClick={() => setEnabled(selected, true)} disabled={toggling}>
              {toggling ? <><FiRefreshCw size={11} style={{ animation: "spin 1s linear infinite" }}/> Connecting…</> : <><FiPlus size={11}/> Connect</>}
            </button>) : confirmDisable ? (<>
              <span className="dw-fp-confirm-label">Disconnect?</span>
              <button className="dw-mcp-btn dw-mcp-btn--danger" onClick={() => setEnabled(selected, false)} disabled={toggling}>
                {toggling ? <FiRefreshCw size={11} style={{ animation: "spin 1s linear infinite" }}/> : "Yes, Remove"}
              </button>
              <button className="dw-mcp-btn dw-mcp-btn--ghost" onClick={() => setConfirmDisable(false)}>No</button>
            </>) : (<button className="dw-mcp-btn dw-mcp-btn--danger-ghost" onClick={() => setConfirmDisable(true)}>
              <FiTrash2 size={11}/> Disconnect
            </button>)}
          </div>
        </div>

        {error && <p className="dw-mcp-editor__hint dw-mcp-editor__hint--error">{error}</p>}

        <IntegrationKeyPanel key={`key-${selected.integrationId}`} integrationId={selected.integrationId}/>
        {selected.description && <p className="dw-mcp-editor__hint" style={{ margin: "4px 0 18px" }}>{selected.description.split("\n")[0]}</p>}
        <IntegrationDetailBody key={`detail-${selected.integrationId}`} apiIntegrationId={selected.integrationId}/>
      </div>)}
    </div>
    {shareTool && (<ToolShareMenuModal
        tool={{ id: shareTool.id, name: shareTool.name, type: "integration", visibility: shareTool.visibility, publishStatus: shareTool.publishStatus, isAgencyInstalled: shareTool.isAgencyInstalled }}
        isAgencyOwner={isAgencyOwner}
        onShareWithAgency={shareWithAgency}
        onUninstallForAgency={() => uninstallToolForAgency(shareTool.id)
          .then(() => load(true))
          .catch(err => setError(err instanceof Error ? err.message : "Couldn't uninstall it for your agency."))}
        onClose={() => setShareTool(null)}
      />)}
  </div>);
};

type McpPanel = {
    mode: "empty";
} | {
    mode: "custom-tools";
    tools: Array<{
        name: string;
        description?: string;
        attached: boolean;
    }>;
} | {
    mode: "connected";
    server: McpServerRecord;
    tools: Array<{
        Name: string;
        Description?: string;
    }>;
} | {
    mode: "available";
    item: ExternalMcpItem;
    tools: Array<{
        Name: string;
        Description?: string;
    }>;
};
export const DwMcpTab: React.FC<{
    agentId: string;
    agentName: string;
}> = ({ agentId, agentName }) => {
    const isAgencyOwner = readIsAgencyOwner();
    const navigate = useNavigate();
    const [connected, setConnected] = useState<McpServerRecord[]>([]);
    const [customTools, setCustomTools] = useState<Array<{
        name: string;
        description?: string;
        attached: boolean;
    }>>([]);
    const [external, setExternal] = useState<ExternalMcpItem[]>([]);
    const [toolApprovals, setToolApprovals] = useState<Record<string, ToolApprovalMode>>({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [panel, setPanel] = useState<McpPanel>({ mode: "empty" });
    const [confirmRm, setConfirmRm] = useState<number | null>(null);
    const [removingId, setRemovingId] = useState<number | null>(null);
    const [connectingId, setConnectingId] = useState<string | null>(null);
    const [connectError, setConnectError] = useState<string | null>(null);
    const [mcpShareTool, setMcpShareTool] = useState<MyToolSummary | null>(null);
    const [loadingMcpShare, setLoadingMcpShare] = useState(false);
    const { showError: showMcpShareError } = useToast();
    const handleOpenMcpShare = async (myToolId: string) => {
        setLoadingMcpShare(true);
        try {
            const tool = await getMyTool(myToolId);
            if (tool)
                setMcpShareTool(tool);
            else
                showMcpShareError("Couldn't find your listing for this connection.");
        }
        catch (e: unknown) {
            showMcpShareError(e instanceof Error ? e.message : 'Failed to open Share');
        }
        finally {
            setLoadingMcpShare(false);
        }
    };
    // Share with Agency just bumps visibility — but upsertMyTool's UPDATE path
    // overwrites every column unconditionally, so this must send back every
    // field mcpShareTool already carries, not just the changed one.
    const handleMcpShareWithAgency = async () => {
        if (!mcpShareTool)
            return;
        const updated = { ...mcpShareTool, visibility: "my_agency" as const };
        setMcpShareTool(updated);
        await upsertMyTool({
            id: Number(updated.id),
            type: TOOL_TYPE_TO_INT[updated.type],
            name: updated.name,
            category: updated.category,
            icon: updated.icon,
            description: updated.description,
            skills: updated.skills,
            kbFileNames: updated.kbFileNames,
            visibility: 1,
            mcpUrl: updated.mcpUrl,
            mcpHeadersTemplate: updated.mcpHeadersTemplate,
            configSchema: updated.configSchema,
            skillContent: updated.skillContent,
            agentShareToken: updated.agentShareToken,
        });
    };
    function normalizeCustomToolsPayload(raw: unknown): AgentCustomToolRecord[] {
        if (Array.isArray(raw))
            return raw as AgentCustomToolRecord[];
        if (raw && typeof raw === "object") {
            const rec = raw as Record<string, unknown>;
            if (Array.isArray(rec.data))
                return rec.data as AgentCustomToolRecord[];
            if (Array.isArray(rec.tools))
                return rec.tools as AgentCustomToolRecord[];
            if (Array.isArray(rec.items))
                return rec.items as AgentCustomToolRecord[];
        }
        return [];
    }
    useEffect(() => {
        setLoading(true);
        setPanel({ mode: "empty" });
        setSearch("");
        Promise.all([
            getAgentMcpServers(agentId).catch(() => [] as McpServerRecord[]),
            getAgentCustomTools(agentId, true).catch(() => [] as AgentCustomToolRecord[]),
            getExternalMcpList().catch(() => [] as ExternalMcpItem[]),
            getToolApprovals(agentId).catch(() => ({}) as Record<string, ToolApprovalMode>),
        ])
            .then(([srv, customTools, ext, ta]) => {
            const customToolList = normalizeCustomToolsPayload(customTools);
            const mappedCustomTools = customToolList.map((t) => ({
                name: t.name || t.tool_name || t.title || "Unnamed Tool",
                description: t.description,
                attached: t.attached === true || t.is_attached === true || t.isAttached === true,
            }));
            setConnected(srv);
            setCustomTools(mappedCustomTools);
            setExternal(dedupeExternalMcps(ext));
            setToolApprovals(ta);
            if (srv.length > 0) {
                const first = srv[0];
                const extItem = ext.find((e) => e.url === first.url);
                const tools = parseMcpTools(extItem?.tools);
                setPanel({ mode: "connected", server: first, tools });
            }
            else if (mappedCustomTools.length > 0) {
                setPanel({ mode: "custom-tools", tools: mappedCustomTools });
            }
        })
            .finally(() => setLoading(false));
    }, [agentId]);
    function parseMcpTools(raw?: string): Array<{
        Name: string;
        Description?: string;
    }> {
        try {
            return raw ? JSON.parse(raw) : [];
        }
        catch {
            return [];
        }
    }
    function matchSearch(name: string, extra?: string) {
        if (!search.trim())
            return true;
        const q = search.toLowerCase();
        return name.toLowerCase().includes(q) || (extra ?? "").toLowerCase().includes(q);
    }
    const connectedUrls = new Set(connected.map((s) => s.url));
    const attachedCustomCount = customTools.filter((t) => t.attached).length;
    const filteredConnected = connected.filter((s) => matchSearch(s.name, s.url));
    const filteredExternal = external.filter((i) => matchSearch(i.name, i.description));
    const handleApproval = (serverName: string, toolName: string, mode: ToolApprovalMode) => {
        const key = `${serverName}::${toolName}`;
        const updated = { ...toolApprovals, [key]: mode };
        setToolApprovals(updated);
        saveToolApprovals(agentId, updated).catch(() => { });
    };
    const handleConnect = async (item: ExternalMcpItem) => {
        if (connectingId)
            return;
        setConnectingId(item.transactionId);
        setConnectError(null);
        try {
            await assignMcpServer(agentId, item.name, item.url, item.transactionId);
            const updated = await getAgentMcpServers(agentId);
            setConnected(updated);
            if (panel.mode === "available" && panel.item.transactionId === item.transactionId) {
                const srv = updated.find((s) => s.url === item.url);
                if (srv)
                    setPanel({ mode: "connected", server: srv, tools: parseMcpTools(item.tools) });
            }
        }
        catch (err) {
            setConnectError(err instanceof Error ? err.message : "Failed to connect this MCP server. Please try again.");
        }
        finally {
            setConnectingId(null);
        }
    };
    const handleRemove = async (id: number) => {
        setRemovingId(id);
        try {
            await removeMcpServer(agentId, id);
            setConnected((prev) => prev.filter((s) => s.id !== id));
            if (panel.mode === "connected" && panel.server.id === id)
                setPanel({ mode: "empty" });
        }
        catch {
        }
        finally {
            setRemovingId(null);
            setConfirmRm(null);
        }
    };
    if (loading) {
        return (<div className="dw-mcp-wrap">
        <div className="dw-mcp-list">
          <div className="dw-mcp-list__header">
            <span className="dw-mcp-list__title">
              <FiShare2 size={11} style={{ color: "#a200ff" }}/> MCP
            </span>
          </div>
          {[0, 1, 2, 3].map((i) => (<div key={i} className="dw-mcp-row dw-mcp-row--skeleton">
              <div className="dw-mcp-icon">
                <FiShare2 size={12}/>
              </div>
              <div className="dw-mcp-skeleton__lines">
                <div className="dw-mcp-skeleton__line dw-mcp-skeleton__line--name" style={{ width: `${60 + i * 10}%` }}/>
                <div className="dw-mcp-skeleton__line dw-mcp-skeleton__line--desc"/>
              </div>
            </div>))}
        </div>
        <div className="dw-mcp-editor">
          <div className="dw-mcp-editor__body">
            <div className="dw-mcp-editor__topbar">
              <div className="ca-fp-skel-bone dw-fp-skel-row-avatar"/>
              <div className="dw-fp-skel-topbar-col--minw0">
                <div className="ca-fp-skel-bone dw-fp-skel-topbar-title"/>
                <div className="ca-fp-skel-bone dw-fp-skel-topbar-sub-wide"/>
              </div>
            </div>
            <div className="dw-mcp-section-head">
              <div className="ca-fp-skel-bone dw-fp-skel-mcp-section-label"/>
            </div>
            <div className="dw-mcp-tools-grid">
              {[0, 1, 2, 3, 4, 5].map((i) => (<div key={i} className="dw-mcp-tool-card dw-mcp-tool-card--skeleton">
                  <div className="dw-mcp-tool-card__meta">
                    <div className="ca-fp-skel-bone dw-fp-skel-mcp-tool-name" style={{ width: `${55 + (i % 3) * 10}%` }}/>
                    <div className="ca-fp-skel-bone dw-fp-skel-mcp-desc"/>
                  </div>
                </div>))}
            </div>
          </div>
        </div>
      </div>);
    }
    return (<div className="dw-mcp-wrap">
      
      <div className="dw-mcp-list">
        <div className="dw-mcp-list__header">
          <span className="dw-mcp-list__title">
            <FiShare2 size={11} style={{ color: "#a200ff" }}/>
            Tools
            <span className="dw-mcp-count">{connected.length + (customTools.length > 0 ? 1 : 0)}</span>
          </span>
        </div>

        <div className="dw-mcp-search-wrap">
          <FiSearch size={11} className="dw-mcp-search-icon"/>
          <input className="dw-mcp-search" placeholder="Search servers…" value={search} onChange={(e) => setSearch(e.target.value)}/>
          {search && (<button className="dw-mcp-search-clear" onClick={() => setSearch("")}>
              <FiX size={10}/>
            </button>)}
        </div>

        <div className="dw-mcp-list__rows">
          {(customTools.length > 0 || filteredConnected.length > 0) && (<>
              {filteredConnected.length > 0 && (<div className="dw-mcp-group-label">
                  Connected <span className="dw-mcp-group-badge">{filteredConnected.length}</span>
                </div>)}

              {filteredConnected.map((s) => {
                const extItem = external.find((e) => e.url === s.url);
                const tools = parseMcpTools(extItem?.tools);
                const isActive = panel.mode === "connected" && panel.server.id === s.id;
                return (<div key={s.id} className={`dw-mcp-row dw-mcp-row--connected${isActive ? " dw-mcp-row--active" : ""}`} onClick={() => setPanel({ mode: "connected", server: s, tools })}>
                    <div className="dw-mcp-icon dw-mcp-icon--connected">
                      <FiShare2 size={12}/>
                    </div>
                    <div className="dw-mcp-row__info">
                      <span className="dw-mcp-row__name">{s.name}</span>
                      <span className="dw-mcp-row__url">
                        {s.url.slice(0, 36)}
                        {s.url.length > 36 ? "…" : ""}
                      </span>
                    </div>
                    <span className="dw-mcp-dot" title="Connected"/>
                  </div>);
            })}

              {customTools.length > 0 && (<>
                  <div className="dw-mcp-group-label" style={{ marginTop: filteredConnected.length > 0 ? 8 : 0 }}>
                    Custom <span className="dw-mcp-group-badge">1</span>
                  </div>
                  <div className={`dw-mcp-row${panel.mode === "custom-tools" ? " dw-mcp-row--active" : ""}`} onClick={() => setPanel({ mode: "custom-tools", tools: customTools })}>
                    <div className="dw-mcp-icon">
                      <FiZap size={12}/>
                    </div>
                    <div className="dw-mcp-row__info">
                      <span className="dw-mcp-row__name">Custom Tools</span>
                      <span className="dw-mcp-row__url">
                        {attachedCustomCount > 0 ? `${attachedCustomCount}/${customTools.length} attached` : "Available custom tools (none attached)"}
                      </span>
                    </div>
                    <span className="dw-mcp-tool-count">{customTools.length}</span>
                  </div>
                </>)}
            </>)}

          {filteredExternal.length > 0 && (<>
              <div className="dw-mcp-group-label" style={{ marginTop: filteredConnected.length > 0 || customTools.length > 0 ? 10 : 0 }}>
                Available <span className="dw-mcp-group-badge">{filteredExternal.length}</span>
              </div>
              {filteredExternal.map((item) => {
                const isConn = connectedUrls.has(item.url);
                const isActive = !isConn && panel.mode === "available" && panel.item.transactionId === item.transactionId;
                const tools = parseMcpTools(item.tools);
                return (<div key={item.transactionId} className={`dw-mcp-row${isConn ? " dw-mcp-row--connected" : ""}${isActive ? " dw-mcp-row--active" : ""}`} onClick={() => {
                        setConnectError(null);
                        if (isConn) {
                            const srv = connected.find((s) => s.url === item.url);
                            if (srv)
                                setPanel({ mode: "connected", server: srv, tools });
                        }
                        else {
                            setPanel({ mode: "available", item, tools });
                        }
                    }}>
                    <div className={`dw-mcp-icon${isConn ? " dw-mcp-icon--connected" : ""}`}>
                      <FiShare2 size={12}/>
                    </div>
                    <div className="dw-mcp-row__info">
                      <span className="dw-mcp-row__name">{item.name}</span>
                      {item.description && <span className="dw-mcp-row__url">{item.description}</span>}
                    </div>
                    {item.needsUserCredentials && (<span title="You need to set up your personal credentials for this tool" style={{
                            fontSize: 11, fontWeight: 600, color: "#fbbf24", background: "rgba(245,158,11,0.12)",
                            border: "1px solid rgba(245,158,11,0.3)", borderRadius: 6, padding: "1px 6px", marginRight: 6,
                        }}>
                        🔑 Needs credentials
                      </span>)}
                    {isConn && <span className="dw-mcp-dot" title="Connected"/>}
                    {tools.length > 0 && !isConn && <span className="dw-mcp-tool-count">{tools.length}</span>}
                  </div>);
            })}
            </>)}

          {filteredConnected.length === 0 && filteredExternal.length === 0 && (<div className="dw-tools-empty">{search ? "No tools match." : "No tools available."}</div>)}
        </div>

        <div className="dw-mcp-cta">
          <p className="dw-mcp-cta__text">Add new MCP servers from the Marketplace.</p>
          <button className="dw-mcp-cta__btn" onClick={() => navigate(ROUTES.SETTINGS_MCP)}>
            MCP Server <FiArrowRight size={11}/>
          </button>
        </div>
      </div>

      
      <div className="dw-mcp-editor">
        {panel.mode === "empty" && (<div className="dw-mcp-editor__empty">
            <FiShare2 size={28} style={{ color: "rgba(162,0,255,0.25)" }}/>
            <div>Select a server to view its tools</div>
          </div>)}

        {panel.mode === "custom-tools" && (<div className="dw-mcp-editor__body">
            <div className="dw-mcp-editor__topbar">
              <div className="dw-mcp-icon dw-mcp-icon--connected dw-mcp-icon--lg">
                <FiZap size={16}/>
              </div>
              <div className="dw-fp-flex-minw0">
                <div className="dw-fp-inline-title-row">
                  <span className="dw-mcp-editor__name">Custom Tools</span>
                  <span className="dw-mcp-editor__badge dw-mcp-editor__badge--connected">{attachedCustomCount > 0 ? "Connected" : "Available"}</span>
                </div>
                <span className="dw-mcp-editor__url">Custom tools for {agentName}</span>
              </div>
            </div>

            <div className="dw-mcp-section-head">
              Tools <span className="dw-mcp-group-badge">{panel.tools.length}</span>
            </div>

            {panel.tools.length === 0 ? (<p className="dw-mcp-editor__hint">No custom tools are attached to this agent.</p>) : (<div className="dw-mcp-tools-grid">
                {panel.tools.map((t, i) => (<div key={`${t.name}-${i}`} className="dw-mcp-tool-card">
                    <div className="dw-mcp-tool-card__meta">
                      <span className="dw-mcp-tool-card__name">{t.name}</span>
                      <span className="dw-mcp-editor__badge dw-mcp-editor__badge--connected dw-fp-w-fit">
                        {t.attached ? "Attached" : "Not attached"}
                      </span>
                      {t.description && (<span className="dw-mcp-tool-card__desc">
                          {t.description.split("\n")[0].slice(0, 120)}
                          {t.description.length > 120 ? "…" : ""}
                        </span>)}
                    </div>
                  </div>))}
              </div>)}
          </div>)}

        {panel.mode === "connected" &&
            (() => {
                const { server: s, tools } = panel;
                const isRemoving = removingId === s.id;
                const myToolId = external.find((e) => e.url === s.url)?.myToolId;
                return (<div className="dw-mcp-editor__body">
                <div className="dw-mcp-editor__topbar">
                  <div className="dw-mcp-icon dw-mcp-icon--connected dw-mcp-icon--lg">
                    <FiShare2 size={16}/>
                  </div>
                  <div className="dw-fp-flex-minw0">
                    <div className="dw-fp-inline-title-row">
                      <span className="dw-mcp-editor__name">{s.name}</span>
                      <span className="dw-mcp-editor__badge dw-mcp-editor__badge--connected">Connected</span>
                    </div>
                    <span className="dw-mcp-editor__url">{s.url}</span>
                  </div>
                  <div className="dw-mcp-editor__actions">
                    {myToolId && (<button className="dw-mcp-btn dw-mcp-btn--ghost" onClick={() => handleOpenMcpShare(myToolId)} disabled={loadingMcpShare}>
                        <FiShare2 size={11}/> Share
                      </button>)}
                    {confirmRm === s.id ? (<>
                        <span className="dw-fp-confirm-label">Disconnect?</span>
                        <button className="dw-mcp-btn dw-mcp-btn--danger" onClick={() => handleRemove(s.id)} disabled={isRemoving}>
                          {isRemoving ? <FiRefreshCw size={11} style={{ animation: "spin 1s linear infinite" }}/> : "Yes, Remove"}
                        </button>
                        <button className="dw-mcp-btn dw-mcp-btn--ghost" onClick={() => setConfirmRm(null)}>
                          No
                        </button>
                      </>) : (<button className="dw-mcp-btn dw-mcp-btn--danger-ghost" onClick={() => setConfirmRm(s.id)}>
                        <FiTrash2 size={11}/> Disconnect
                      </button>)}
                  </div>
                </div>

                <div className="dw-mcp-section-head">
                  Tools <span className="dw-mcp-group-badge">{tools.length}</span>
                </div>

                {tools.length === 0 ? (<p className="dw-mcp-editor__hint">No tool definitions available for this server.</p>) : (<div className="dw-mcp-tools-grid">
                    {tools.map((t, i) => {
                            const key = `${s.name}::${t.Name}`;
                            const mode = toolApprovals[key] ?? "ask";
                            return (<div key={i} className="dw-mcp-tool-card">
                          <div className="dw-mcp-tool-card__meta">
                            <span className="dw-mcp-tool-card__name">{t.Name}</span>
                            {t.Description && (<span className="dw-mcp-tool-card__desc">
                                {t.Description.split("\n")[0].slice(0, 120)}
                                {t.Description.length > 120 ? "…" : ""}
                              </span>)}
                          </div>
                          
                        </div>);
                        })}
                  </div>)}
              </div>);
            })()}

        {panel.mode === "available" &&
            (() => {
                const { item, tools } = panel;
                const isConn = connectedUrls.has(item.url);
                const isConnecting = connectingId === item.transactionId;
                return (<div className="dw-mcp-editor__body">
                <div className="dw-mcp-editor__topbar">
                  <div className="dw-mcp-icon dw-mcp-icon--lg">
                    <FiShare2 size={16}/>
                  </div>
                  <div className="dw-fp-flex-minw0">
                    <div className="dw-fp-inline-title-row">
                      <span className="dw-mcp-editor__name">{item.name}</span>
                      <span className="dw-mcp-editor__badge dw-mcp-editor__badge--platform">Platform</span>
                    </div>
                    {item.description && <span className="dw-mcp-editor__url">{item.description}</span>}
                  </div>
                  <div className="dw-mcp-editor__actions">
                    {item.myToolId && (<button className="dw-mcp-btn dw-mcp-btn--ghost" onClick={() => handleOpenMcpShare(item.myToolId!)} disabled={loadingMcpShare}>
                        <FiShare2 size={11}/> Share
                      </button>)}
                    {!isConn && (<button className="dw-mcp-btn dw-mcp-btn--connect" onClick={() => handleConnect(item)} disabled={!!connectingId}>
                        {isConnecting ? (<>
                            <FiRefreshCw size={11} style={{ animation: "spin 1s linear infinite" }}/> Connecting…
                          </>) : (<>
                            <FiPlus size={11}/> Connect
                          </>)}
                      </button>)}
                    {isConn &&
                        (() => {
                            const srv = connected.find((s) => s.url === item.url);
                            if (!srv)
                                return null;
                            return confirmRm === srv.id ? (<>
                            <span className="dw-fp-confirm-label">Disconnect?</span>
                            <button className="dw-mcp-btn dw-mcp-btn--danger" onClick={() => handleRemove(srv.id)}>
                              Yes
                            </button>
                            <button className="dw-mcp-btn dw-mcp-btn--ghost" onClick={() => setConfirmRm(null)}>
                              No
                            </button>
                          </>) : (<button className="dw-mcp-btn dw-mcp-btn--danger-ghost" onClick={() => setConfirmRm(srv.id)}>
                            <FiTrash2 size={11}/> Disconnect
                          </button>);
                        })()}
                  </div>
                </div>

                {!isConn && connectError && (
                  <p className="dw-mcp-editor__hint dw-mcp-editor__hint--error">{connectError}</p>
                )}

                <div className="dw-mcp-section-head">
                  Available Tools <span className="dw-mcp-group-badge">{tools.length}</span>
                </div>
                {tools.length === 0 ? (<p className="dw-mcp-editor__hint">No tool definitions available for this server.</p>) : (<div className="dw-mcp-tools-grid">
                    {tools.map((t, i) => (<div key={i} className="dw-mcp-tool-card dw-mcp-tool-card--preview">
                        <div className="dw-mcp-tool-card__meta">
                          <span className="dw-mcp-tool-card__name">{t.Name}</span>
                          {t.Description && (<span className="dw-mcp-tool-card__desc">
                              {t.Description.split("\n")[0].slice(0, 120)}
                              {t.Description.length > 120 ? "…" : ""}
                            </span>)}
                        </div>
                      </div>))}
                  </div>)}

                <div className="dw-mcp-section-head dw-fp-mt-16">
                  Endpoint
                </div>
                <code className="dw-mcp-endpoint">{item.url}</code>
              </div>);
            })()}
      </div>
      {mcpShareTool && (<ToolShareMenuModal
          tool={{ id: mcpShareTool.id, name: mcpShareTool.name, type: mcpShareTool.type as "skill" | "mcp_server", visibility: mcpShareTool.visibility, publishStatus: mcpShareTool.publishStatus, isAgencyInstalled: mcpShareTool.isAgencyInstalled }}
          isAgencyOwner={isAgencyOwner}
          onShareWithAgency={handleMcpShareWithAgency}
          onUninstallForAgency={() => uninstallToolForAgency(mcpShareTool.id)
            .catch(err => showMcpShareError(err instanceof Error ? err.message : "Couldn't uninstall it for your agency."))}
          onClose={() => setMcpShareTool(null)}
        />)}
    </div>);
};
const SIGNATURE_PLACEHOLDER = "<p>Sincerely,</p>\n<p><strong>Jane Doe</strong><br>Account Executive<br>Your Agency Name<br>(555) 555-0100 | jane@youragency.com</p>\n<img src=\"https://www.youragency.com/logo.png\" alt=\"Your Agency\" width=\"160\">";
/** Sandboxed preview document: no scripts run, images still load, links open in a new tab. */
const signaturePreviewDoc = (html: string) => "<!doctype html><html><head><meta charset=\"utf-8\"><base target=\"_blank\"><style>body{margin:12px;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1a1a1a;background:#fff}</style></head><body>" + html + "</body></html>";
export const DwMemoryTab: React.FC<{
    agentId: string;
}> = ({ agentId }) => {
    const [rules, setRules] = useState("");
    const [learnedRules, setLearnedRules] = useState("");
    const [lastUpdated, setLastUpdated] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Email signature is per USER (every agent, every send surface), not per agent;
    // it lives on the Memory tab because that is where users already tune behaviour.
    const [signature, setSignature] = useState("");
    const [sigLoading, setSigLoading] = useState(true);
    const [sigSaving, setSigSaving] = useState(false);
    const [sigSaved, setSigSaved] = useState(false);
    const [sigError, setSigError] = useState<string | null>(null);
    const applyMemory = (mem: AgentMemory) => {
        setRules(mem.user_added_rules ?? "");
        setLearnedRules(mem.chat_summary_rules ?? "");
        setLastUpdated(mem.last_summarized_at);
    };
    const loadMemory = useCallback(() => {
        setLoading(true);
        setError(null);
        getAgentMemory(agentId)
            .then(applyMemory)
            .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load memory"))
            .finally(() => setLoading(false));
    }, [agentId]);
    useEffect(() => {
        loadMemory();
    }, [loadMemory]);
    const handleSaveRules = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            const res = await updateMemoryUserRules(agentId, rules);
            setRules(res.user_added_rules ?? rules);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        }
        catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Failed to save rules");
        }
        finally {
            setSaving(false);
        }
    }, [agentId, rules]);
    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        setError(null);
        try {
            await runMemorySummarization(agentId);
            await loadMemory();
        }
        catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Failed to refresh memory");
        }
        finally {
            setRefreshing(false);
        }
    }, [agentId, loadMemory]);
    useEffect(() => {
        let cancelled = false;
        setSigLoading(true);
        setSigError(null);
        getEmailSignature()
            .then((res) => { if (!cancelled) setSignature(res.signature_html ?? ""); })
            .catch((e: unknown) => { if (!cancelled) setSigError(e instanceof Error ? e.message : "Failed to load email signature"); })
            .finally(() => { if (!cancelled) setSigLoading(false); });
        return () => { cancelled = true; };
    }, []);
    const handleSaveSignature = useCallback(async () => {
        setSigSaving(true);
        setSigError(null);
        try {
            const res = await updateEmailSignature(signature);
            setSignature(res.signature_html ?? signature);
            setSigSaved(true);
            setTimeout(() => setSigSaved(false), 2000);
        }
        catch (e: unknown) {
            setSigError(e instanceof Error ? e.message : "Failed to save email signature");
        }
        finally {
            setSigSaving(false);
        }
    }, [signature]);
    if (loading) {
        return (<div className="dw-agent-ov">
        <div className="dw-ov-block">
          <div className="dw-ov-block__hdr">
            <FiEdit3 size={11}/> Your Rules
          </div>
          <div className="ca-fp-skel-bone" style={{ height: 90, margin: 13 }}/>
        </div>
        <div className="dw-ov-block dw-ov-block--fill">
          <div className="dw-ov-block__hdr">
            <FiDatabase size={11}/> Learned Rules
          </div>
          <div className="ca-fp-skel-bone" style={{ height: 140, margin: 13 }}/>
        </div>
      </div>);
    }
    return (<div className="dw-agent-ov dw-agent-ov--fill">
      {error && <div className="dw-panel__footer-error">{error}</div>}

      
      <div className="dw-ov-block">
        <div className="dw-ov-block__hdr">
          <FiEdit3 size={11}/> Your Rules
          <button type="button" className="dw-mcp-cta__btn" onClick={handleSaveRules} disabled={saving} style={{ marginLeft: "auto" }}>
            {saved ? (<>
                <FiCheck size={11}/> Saved
              </>) : saving ? (<>
                <FiRefreshCw size={11} style={{ animation: "dw-spin 0.8s linear infinite" }}/> Saving…
              </>) : ("Save")}
          </button>
        </div>

        <div style={{ padding: 13 }}>
          <textarea className="dw-field__input dw-field__textarea" style={{ minHeight: 110 }} placeholder={"Always cc my assistant on client emails.\nPrefer bullet-point summaries.\nDefault to NowCerts for all policy lookups."} value={rules} onChange={(e) => setRules(e.target.value)}/>
        </div>
      </div>

      
      
      <div className="dw-ov-block">
        <div className="dw-ov-block__hdr">
          <FiMail size={11}/> Email Signature
          <span className="dw-ov-block__badge">All agents</span>
          <button type="button" className="dw-mcp-cta__btn" onClick={handleSaveSignature} disabled={sigSaving || sigLoading} style={{ marginLeft: "auto" }}>
            {sigSaved ? (<>
                <FiCheck size={11}/> Saved
              </>) : sigSaving ? (<>
                <FiRefreshCw size={11} style={{ animation: "dw-spin 0.8s linear infinite" }}/> Saving…
              </>) : ("Save")}
          </button>
        </div>
        <div style={{ padding: 13 }}>
          <p className="dw-field__hint" style={{ margin: "0 0 8px 0" }}>
            Appended automatically to every email your agents send (NowCerts, Gmail, Outlook), so they never guess who signs.
            Paste HTML; a logo must be a publicly hosted image URL. Leave it empty and agents close with your name and agency.
          </p>
          {sigError && <div className="dw-panel__footer-error">{sigError}</div>}
          <textarea className="dw-field__input dw-field__textarea" style={{ minHeight: 110, fontFamily: "monospace", fontSize: 12 }} placeholder={SIGNATURE_PLACEHOLDER} value={signature} onChange={(e) => setSignature(e.target.value)} disabled={sigLoading}/>
          {signature.trim() && (<div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", marginBottom: 6 }}>Preview</div>
              <iframe title="Email signature preview" sandbox="" srcDoc={signaturePreviewDoc(signature)} style={{ width: "100%", height: 150, border: "1px solid var(--border-color, #e0e0e0)", borderRadius: 6, background: "#ffffff" }}/>
            </div>)}
        </div>
      </div>

      <div className="dw-ov-block dw-ov-block--fill">
        <div className="dw-ov-block__hdr">
          <FiDatabase size={11}/> Learned Rules
          {lastUpdated && (<span className="dw-ov-block__badge">Updated {new Date(lastUpdated).toLocaleDateString()}</span>)}
          <button type="button" className="dw-icon-btn" onClick={handleRefresh} disabled={refreshing} title="Re-summarize from recent sessions" style={{ marginLeft: lastUpdated ? 4 : "auto", flexShrink: 0 }}>
            <FiRefreshCw size={12} style={refreshing ? { animation: "dw-spin 0.8s linear infinite" } : undefined}/>
          </button>
        </div>
        <div className="dw-ov-task-list dw-ov-task-list--scroll" style={{ padding: 13 }}>
          {learnedRules.trim() ? (<p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--text-secondary)", whiteSpace: "pre-wrap" }}>
              {learnedRules}
            </p>) : (<div className="dw-ov-block__empty">
              <FiDatabase size={20} style={{ opacity: 0.3 }}/>
              <span>Nothing learned yet — click refresh to generate.</span>
            </div>)}
        </div>
      </div>
    </div>);
};
/**
 * The agent's avatar on its profile header. For your own agents it's also
 * the logo control: click it to upload, replace or remove a logo, which then
 * shows everywhere the agent appears (chat bar, Digital Team, Command
 * Center, a command's conversation). See utils/agentPhotos.ts.
 */
/** A logo picked on the New Agent form, before the agent (and its id) exists. */
export type PendingAgentLogo = { file: File; preview: string };
export const AgentLogoControl: React.FC<{
    agentId: string;
    name: string;
    color: string;
    isSystem: boolean;
    editable: boolean;
    /** New Agent form: hold the picked image as a draft (previewed here) until
     *  the agent is created and has an id to store it under. */
    draft?: { logo: PendingAgentLogo | null; onPick: (file: File) => void; onClear: () => void };
}> = ({ agentId, name, color, isSystem, editable, draft }) => {
    useAgentPhotosVersion();
    const [menuOpen, setMenuOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const photo = draft ? draft.logo?.preview : agentPhotoFor(agentId, isSystem ? name : undefined);
    const hasUpload = draft ? !!draft.logo : !!getUploadedAgentPhoto(agentId);
    useEffect(() => {
        if (!menuOpen) return;
        const onDown = (e: MouseEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setMenuOpen(false); };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
    }, [menuOpen]);
    const avatar = (<span className="dw-agent-avatar dw-agent-avatar--panel dw-agent-logo__img" style={photo ? undefined : { background: color }}>
        {photo ? <img src={photo} alt=""/> : getInitials(name)}
      </span>);
    if (!editable) return avatar;
    const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (draft) {
            // Same checks setAgentPhotoFromFile applies, up front, so a bad file
            // is caught now rather than silently after the agent is created.
            if (!file.type.startsWith("image/")) { setError("Choose an image file (PNG, JPG, SVG or WebP)."); return; }
            if (file.size > 8 * 1024 * 1024) { setError("That image is over 8 MB — choose a smaller one."); return; }
            setError("");
            draft.onPick(file);
            return;
        }
        setBusy(true);
        setError("");
        // Errors carry the API's message, e.g. "Only the agent's owner can change its logo."
        try { await setAgentPhotoFromFile(agentId, file); }
        catch (err) { setError(err instanceof Error ? err.message : "Couldn't use that image."); }
        finally { setBusy(false); }
    };
    const onRemove = async () => {
        setBusy(true);
        setError("");
        try { await removeAgentPhoto(agentId); }
        catch (err) { setError(err instanceof Error ? err.message : "Couldn't remove the logo."); }
        finally { setBusy(false); }
    };
    return (<div className="dw-agent-logo" ref={wrapRef}>
        <button type="button" className="dw-agent-logo__btn" onClick={() => setMenuOpen((o) => !o)} aria-haspopup="menu" aria-expanded={menuOpen} aria-label={`${name} logo — ${hasUpload ? "change or remove" : "upload"}`} title={hasUpload ? "Change logo" : "Upload a logo"} disabled={busy}>
          {avatar}
          <span className="dw-agent-logo__edit" aria-hidden="true">{busy ? <FiRefreshCw size={10} className="ca-fp-spin"/> : <FiEdit3 size={10}/>}</span>
        </button>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif" hidden onChange={onPick}/>
        {menuOpen && (<div className="dw-agent-logo__menu" role="menu">
            <button type="button" role="menuitem" className="dw-agent-logo__item" onClick={() => { setMenuOpen(false); inputRef.current?.click(); }}>
              <FiUpload size={13}/> {hasUpload ? "Replace logo" : "Upload logo"}
            </button>
            {hasUpload && (<button type="button" role="menuitem" className="dw-agent-logo__item dw-agent-logo__item--danger" onClick={() => { setMenuOpen(false); if (draft) draft.onClear(); else void onRemove(); }}>
                <FiTrash2 size={13}/> Remove logo
              </button>)}
            <div className="dw-agent-logo__hint">{draft ? "Square images look best. Saved with the agent when you create it." : "Square images look best. Shows wherever this agent appears."}</div>
          </div>)}
        {error && <div className="dw-agent-logo__error" role="alert">{error}</div>}
      </div>);
};
const DigitalWorkforcePage: React.FC = () => {
    // Pre-warm the slim agent list (the card grid needs description/model/
    // harness_type, which compact leaves out) so fetchAgents below reuses it.
    useState(() => { listDwAgents(false, false, true); });
    const isAgencyOwner = readIsAgencyOwner();
    // Agent cards show uploaded logos — re-render when one changes.
    useAgentPhotosVersion();
    const navigate = useNavigate();
    const location = useLocation();
    const { setActiveAgent, requestOpen } = useActiveAgent();
    const [agents, setAgents] = useState<Agent[]>([]);
    const [agentsLoading, setAgentsLoading] = useState(true);
    const [agentSaving, setAgentSaving] = useState(false);
    const [agentError, setAgentError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [form, setForm] = useState<Agent | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [activeTab, setActiveTab] = useState<DocTab>("agents");
    const [agentShareMenuOpen, setAgentShareMenuOpen] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const [agentSearch, setAgentSearch] = useState("");
    const [tasks, setTasks] = useState<Task[]>(loadTasks);
    const [assignTaskDraft, setAssignTaskDraft] = useState<{
        issue: CommandIssue;
        modelLabel: string;
    } | null>(null);
    const [modelPickerOpen, setModelPickerOpen] = useState(false);
    const [draftPanelKey, setDraftPanelKey] = useState(0);
    const [taskRefreshKey, setTaskRefreshKey] = useState(0);
    const [activitySubTab, setActivitySubTab] = useState<"routines" | "sessions" | "tasks">("tasks");
    const [activityDateRange, setActivityDateRange] = useState(defaultActivityDateRange);
    const [pendingDeleteAgent, setPendingDeleteAgent] = useState<{
        id: string;
        name: string;
    } | null>(null);
    const [dwConfigured, setDwConfigured] = useState<boolean | null>(null);
    const [setupBusy, setSetupBusy] = useState(false);
    const [setupError, setSetupError] = useState<string | null>(null);
    // "Start Chat" no longer opens a separate full-page conversation — the
    // global chat bar at the bottom of every page IS where you chat with an
    // agent now, so this just re-targets it and pops it open.
    const startChatWithAgent = useCallback((agentId: string, agentName: string, avatarColor: string, isSystem: boolean) => {
        const name = isSystem ? agentName.replace(/-\d+$/, "") : agentName;
        requestOpen({
            id: agentId,
            name,
            avatarColor,
            avatarInitials: getInitials(name),
            avatarPhoto: agentPhotoFor(agentId, isSystem ? name : undefined),
        });
    }, [requestOpen]);
    const openAssignTaskForAgent = useCallback((agentName: string, agentId: string, modelValue: string) => {
        setDraftPanelKey(0);
        setAssignTaskDraft({
            issue: {
                id: "__new__",
                code: "",
                title: "",
                status: "todo",
                assigneeType: 2,
                agentName,
                agentId,
                comments: [],
            },
            // Guard the empty case explicitly: "" matches AGENT_MODELS' own
            // placeholder entry, which would print the literal words "Select
            // model" as though they were this agent's model. A Hermes agent has
            // no Anthropic model at all, so blank is the honest label.
            modelLabel: modelValue
                ? AGENT_MODELS.find((m) => m.value === modelValue)?.label ?? modelValue
                : "",
        });
    }, []);
    // The slim agent list leaves out agents.md (system), soul and heartbeat —
    // multi-KB each. They're read from the full list (the stored text, not the
    // composed prompt GET /agents/{id} returns) the first time an agent is
    // opened. Until then a save leaves all three untouched on the server, so a
    // blank editor can never overwrite them.
    const textLoadedRef = useRef<Set<string>>(new Set());
    const loadAgentText = useCallback((agent: Agent) => {
        if (textLoadedRef.current.has(agent.id))
            return;
        listDwAgents()
            .then((records) => {
            const byId = new Map(records.map((r) => [r.agent_id, r]));
            const textOf = (r: DwAgentRecord) => ({ agentsMd: r.system ?? "", soul: r.soul ?? "", heartbeat: r.heartbeat ?? "" });
            records.forEach((r) => textLoadedRef.current.add(r.agent_id));
            setAgents((prev) => prev.map((a) => {
                const r = byId.get(a.id);
                return r ? { ...a, ...textOf(r) } : a;
            }));
            const rec = byId.get(agent.id);
            if (!rec)
                return;
            const loaded = textOf(rec);
            // Keep anything the user already typed while this was loading.
            setForm((prev) => (prev && prev.id === agent.id
                ? {
                    ...prev,
                    agentsMd: prev.agentsMd === agent.agentsMd ? loaded.agentsMd : prev.agentsMd,
                    soul: prev.soul === agent.soul ? loaded.soul : prev.soul,
                    heartbeat: prev.heartbeat === agent.heartbeat ? loaded.heartbeat : prev.heartbeat,
                }
                : prev));
        })
            .catch(() => { /* leave the editors as they are; save keeps the stored text */ });
    }, []);
    const fetchAgents = useCallback(async () => {
        const meta = loadMeta();
        const records = await listDwAgents(false, false, true);
        const normalized = records.map((r) => apiToAgent(r, meta[r.agent_id]));
        // The slim list has no agents.md / soul / heartbeat; loadAgentText fills them again.
        textLoadedRef.current.clear();
        normalized.sort((a, b) => {
            if (a.isSystem !== b.isSystem)
                return a.isSystem ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        setAgents(normalized);
        try {
            localStorage.setItem("momentum_digital_workforce_agents", JSON.stringify(records.map((r) => ({ id: r.agent_id, name: r.name }))));
        }
        catch { }
    }, []);
    // Setup runs on its own - nobody clicks "launch setup" any more (Digital
    // Workforce is on for every agency). AppShell already started it in the
    // background at sign-in; this joins that run if it's still going, or
    // starts a fresh one, and shows progress / error + Retry meanwhile.
    const runDwSetup = useCallback(async () => {
        setSetupBusy(true);
        setSetupError(null);
        try {
            await ensureDwConfigured();
            setDwConfigured(true);
            setAgentsLoading(true);
            await fetchAgents();
        }
        catch (e: unknown) {
            setSetupError(e instanceof Error ? e.message : "Setup failed");
        }
        finally {
            setSetupBusy(false);
            setAgentsLoading(false);
        }
    }, [fetchAgents]);
    const loadDwState = useCallback(async () => {
        setAgentsLoading(true);
        setAgentError(null);
        try {
            // ensureDwConfigured() already calls getStatus() internally and
            // caches the result in sessionStorage — no need for a second
            // /status round-trip that dwAutoSetup already did at sign-in.
            const outcome = await ensureDwConfigured();
            const configured = FORCE_DW_UNCONFIGURED ? false : true;
            setDwConfigured(configured);
            if (configured) {
                await fetchAgents();
            }
            else {
                setAgents([]);
                setSelectedId(null);
                setForm(null);
                setIsCreating(false);
            }
            // ensureDwConfigured handles the unconfigured→setup path itself,
            // so we only need to trigger runDwSetup when the dev switch forces
            // the "unconfigured" view and the user hasn't set it up yet.
            if (!configured && !FORCE_DW_UNCONFIGURED) {
                forgetDwConfigured();
                await runDwSetup();
            }
        }
        catch {
            setDwConfigured(null);
            setAgents([]);
            setAgentError("Failed to load Digital Workforce status");
        }
        finally {
            setAgentsLoading(false);
        }
    }, [fetchAgents, runDwSetup]);
    useEffect(() => {
        loadDwState();
    }, [loadDwState]);
    const selectedAgentName = selectedId ? agents.find((a) => a.id === selectedId)?.name : undefined;
    // Activity lists load ACTIVITY_PAGE_SIZE at a time; scrolling to the end of a
    // list loads the next page. Changing agent or date range starts over.
    const activityAgent = selectedId && selectedAgentName && activeTab === "activity" && !isCreating
        ? { id: selectedId, name: selectedAgentName }
        : null;
    const activityKey = activityAgent
        ? `${activityAgent.id}|${activityAgent.name}|${activityDateRange.from}|${activityDateRange.to}`
        : "";
    const agentKanbanTasks = usePagedActivity<AgentKanbanTaskRecord>(
        activityAgent
            ? (offset) => getAgentKanbanTasksPage(activityAgent.id, activityAgent.name, ACTIVITY_PAGE_SIZE, offset, activityDateRange.from, activityDateRange.to)
            : null,
        `${activityKey}|${taskRefreshKey}`,
    );
    const agentRoutines = usePagedActivity<AgentRoutineRunRecord>(
        activityAgent
            ? (offset) => getAgentRoutineRunsPage(activityAgent.id, activityAgent.name, ACTIVITY_PAGE_SIZE, offset, activityDateRange.from, activityDateRange.to)
            : null,
        activityKey,
    );
    const agentSessions = usePagedActivity<AgentChatRecord>(
        activityAgent
            ? (offset) => getAgentChatsPage(activityAgent.id, ACTIVITY_PAGE_SIZE, offset, activityDateRange.from, activityDateRange.to)
            : null,
        activityKey,
    );
    const updateForm = useCallback(<K extends keyof Agent>(key: K, value: Agent[K]) => {
        setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
        setSaved(false);
    }, []);
    // Agents whose saved effort/speed has been loaded (GET /agents/{id}) or that
    // the user set explicitly. Only those send effort/speed on save, so a list
    // row's default Medium/Normal never overwrites the real stored values.
    const runtimeLoadedRef = useRef<Set<string>>(new Set());
    const runtimeTouchedRef = useRef<Set<string>>(new Set());
    // Changing the model re-fits effort/speed to what the new model supports
    // (e.g. leaving Opus 4.8 drops fast mode).
    const handleModelChange = useCallback((model: string) => {
        setForm((prev) => {
            if (!prev) return prev;
            runtimeTouchedRef.current.add(prev.id);
            return { ...prev, model, ...normalizeAgentRuntime(model, prev.effort || DEFAULT_AGENT_EFFORT, prev.speed) };
        });
        setSaved(false);
    }, []);
    const updateRuntimeField = useCallback(<K extends "effort" | "speed">(key: K, value: Agent[K]) => {
        setForm((prev) => {
            if (!prev) return prev;
            runtimeTouchedRef.current.add(prev.id);
            return { ...prev, [key]: value };
        });
        setSaved(false);
    }, []);
    const handleSelect = useCallback((agent: Agent) => {
        setSelectedId(agent.id);
        setForm({ ...agent, skills: agent.skills.map((s) => ({ ...s })) });
        setIsCreating(false);
        setActiveTab("activity");
        setSaved(false);
        setSaveAttempted(false);
        // The global chat bar follows whoever you're looking at — opening an
        // agent's detail page re-targets it, same as picking them from the
        // Digital Team sidebar strip.
        const name = displayName(agent);
        setActiveAgent({
            id: agent.id,
            name,
            avatarColor: agent.avatarColor,
            avatarInitials: getInitials(name),
            avatarPhoto: agentPhotoFor(agent.id, agent.isSystem ? name : undefined),
        });
        loadAgentText(agent);
        // The list only carries the model id; fetch the live agent once for its
        // saved effort / speed. Until it lands, saving leaves both untouched.
        if (agent.harnessType !== "Hermes" && !runtimeLoadedRef.current.has(agent.id)) {
            getDwAgent(agent.id)
                .then((rec) => {
                    const runtime = readAgentRuntime(rec, agent.model);
                    if (!runtime.known) return;
                    runtimeLoadedRef.current.add(agent.id);
                    const patch = { effort: runtime.effort, speed: runtime.speed };
                    setAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, ...patch } : a)));
                    // Keep a value the user already changed while this was loading.
                    if (!runtimeTouchedRef.current.has(agent.id)) {
                        setForm((prev) => (prev && prev.id === agent.id ? { ...prev, ...patch } : prev));
                    }
                })
                .catch(() => { /* keep defaults; save then omits effort/speed */ });
        }
    }, [setActiveAgent, loadAgentText]);
    // Arriving from the "Digital Team" sidebar strip — jump straight to that
    // agent's detail page (Activity tab, i.e. their chats), same as clicking
    // their card by hand. Consumed once per navigation (location.key) so a
    // re-render doesn't re-trigger it, but clicking the same agent again does.
    const navState = location.state as { selectAgentId?: string; ownNav?: boolean } | null;
    const selectAgentIdFromNav = navState?.selectAgentId;
    const consumedNavKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (!selectAgentIdFromNav || consumedNavKeyRef.current === location.key) return;
        const target = agents.find((a) => a.id === selectAgentIdFromNav);
        if (!target) return;
        consumedNavKeyRef.current = location.key;
        handleSelect(target);
        // Drop the request from history so Back/refresh doesn't replay it;
        // ownNav tells the reset effect below this isn't a new visit.
        navigate(location.pathname, { replace: true, state: { ownNav: true } });
    }, [selectAgentIdFromNav, agents, handleSelect, navigate, location.pathname, location.key]);
    // Any other fresh navigation to this route — the left-menu item, the
    // "Agents" quick links, the Digital Team header arrow — lands on the
    // roster. React Router keeps this page mounted when the path doesn't
    // change, so without this a previously opened agent would stay open.
    // Each navigate() gets a new location.key, which is what this keys off.
    const lastNavKeyRef = useRef(location.key);
    useEffect(() => {
        if (location.key === lastNavKeyRef.current) return;
        lastNavKeyRef.current = location.key;
        if (navState?.ownNav || selectAgentIdFromNav) return;
        setSelectedId(null);
        setForm(null);
        setIsCreating(false);
    }, [location.key, navState?.ownNav, selectAgentIdFromNav]);
    // Logo picked on the New Agent form — stored under the agent's id once it exists.
    const [pendingLogo, setPendingLogo] = useState<PendingAgentLogo | null>(null);
    const clearPendingLogo = useCallback(() => {
        setPendingLogo((prev) => {
            if (prev) URL.revokeObjectURL(prev.preview);
            return null;
        });
    }, []);
    const pickPendingLogo = useCallback((file: File) => {
        setPendingLogo((prev) => {
            if (prev) URL.revokeObjectURL(prev.preview);
            return { file, preview: URL.createObjectURL(file) };
        });
    }, []);
    // Leaving the New Agent form (cancel, back, or picking an agent) drops the draft.
    useEffect(() => {
        if (!isCreating) clearPendingLogo();
    }, [isCreating, clearPendingLogo]);
    const handleNew = useCallback(() => {
        clearPendingLogo();
        const blank = makeBlankAgent();
        setForm(blank);
        setSelectedId(null);
        setIsCreating(true);
        setActiveTab("overview");
        setSaved(false);
        setSaveAttempted(false);
    }, []);
    const handleSave = useCallback(async () => {
        setSaveAttempted(true);
        const normalizedName = (form?.name ?? "").trim().toLowerCase();
        const hasDuplicateName = !!form && !!normalizedName && agents.some((a) => a.id !== form.id && a.name.trim().toLowerCase() === normalizedName);
        if (!form?.name.trim() || !form.model.trim() || nameStartsWithDefault(form.name) || hasDuplicateName) {
            if (hasDuplicateName) {
                setAgentError("Agent name already exists. Please use a different name.");
            }
            setActiveTab("overview");
            return;
        }
        setAgentSaving(true);
        setAgentError(null);
        try {
            // Claude agents carry effort + speed; Hermes agents pick their own model
            // on their computer, so they keep the plain payload.
            const isClaudeHarness = form.harnessType !== "Hermes";
            const runtime = normalizeAgentRuntime(form.model, form.effort, form.speed);
            const sendRuntime = isClaudeHarness && (isCreating
                || runtimeLoadedRef.current.has(form.id)
                || runtimeTouchedRef.current.has(form.id));
            // Before the stored text has loaded, send only a text field the user changed.
            const listed = agents.find((a) => a.id === form.id);
            const textLoaded = isCreating || textLoadedRef.current.has(form.id);
            const changed = (key: "agentsMd" | "soul" | "heartbeat") => textLoaded || form[key] !== (listed?.[key] ?? "");
            const apiPayload = {
                name: form.name,
                description: form.description,
                ...(changed("soul") ? { soul: form.soul } : {}),
                ...(changed("heartbeat") ? { heartbeat: form.heartbeat } : {}),
                ...(changed("agentsMd") ? { system: form.agentsMd } : {}),
                model: form.model,
                ...(sendRuntime ? { effort: runtime.effort || null, speed: runtime.speed } : {}),
            };
            let rec: DwAgentRecord;
            if (isCreating) {
                rec = await createDwAgent(apiPayload);
                if (pendingLogo) {
                    // Best-effort: the agent is created either way; a storage
                    // failure just leaves it on its initials.
                    try { await setAgentPhotoFromFile(rec.agent_id, pendingLogo.file); }
                    catch { /* ignore */ }
                }
                saveMeta(rec.agent_id, {
                    description: form.description,
                    model: form.model,
                    avatarColor: form.avatarColor,
                    agentsMd: form.agentsMd,
                });
            }
            else {
                saveMeta(form.id, {
                    description: form.description,
                    model: form.model,
                    avatarColor: form.avatarColor,
                    agentsMd: form.agentsMd,
                });
                rec = await updateDwAgent(form.id, apiPayload);
            }
            const freshMeta = loadMeta();
            // create/update return the composed system prompt and no soul/heartbeat,
            // so keep the text that was just saved.
            const savedAgent = {
                ...apiToAgent(rec, freshMeta[rec.agent_id]),
                agentsMd: form.agentsMd,
                soul: form.soul,
                heartbeat: form.heartbeat,
            };
            if (isCreating)
                textLoadedRef.current.add(savedAgent.id);
            // create/update return the live model object, so its runtime is current.
            if (readAgentRuntime(rec, savedAgent.model).known) runtimeLoadedRef.current.add(savedAgent.id);
            runtimeTouchedRef.current.delete(form.id);
            setAgents((prev) => (isCreating ? [...prev, savedAgent] : prev.map((a) => (a.id === form.id ? savedAgent : a))));
            setForm(savedAgent);
            setSelectedId(savedAgent.id);
            setIsCreating(false);
            setSaveAttempted(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2200);
        }
        catch (e: unknown) {
            setAgentError(e instanceof Error ? e.message : "Save failed");
        }
        finally {
            setAgentSaving(false);
        }
    }, [form, isCreating, agents, pendingLogo]);
    const requestDelete = useCallback((id: string) => {
        const target = agents.find((a) => a.id === id);
        if (!target)
            return;
        if (target.isSystem) {
            setAgentError("Default system agents cannot be deleted.");
            return;
        }
        setPendingDeleteAgent({ id: target.id, name: target.name || target.id });
    }, [agents]);
    const handleDelete = useCallback(async (id: string) => {
        setAgentError(null);
        setAgentSaving(true);
        try {
            await archiveDwAgent(id);
            deleteMeta(id);
            setAgents((prev) => {
                const next = prev.filter((a) => a.id !== id);
                try {
                    localStorage.setItem("momentum_digital_workforce_agents", JSON.stringify(next.map((a) => ({ id: a.id, name: a.name }))));
                }
                catch { }
                return next;
            });
            if (form?.id === id) {
                setSelectedId(null);
                setForm(null);
                setIsCreating(false);
            }
        }
        catch (e: unknown) {
            setAgentError(e instanceof Error ? e.message : "Delete failed");
        }
        finally {
            setPendingDeleteAgent(null);
            setAgentSaving(false);
        }
    }, [form]);
    const handleSetPrimary = useCallback(async (id: string) => {
        setAgentError(null);
        setAgentSaving(true);
        try {
            await setPrimaryDwAgent(id);
            setAgents((prev) => prev.map((a) => ({ ...a, isPrimary: a.id === id })));
            setForm((prev) => (prev ? { ...prev, isPrimary: prev.id === id } : prev));
        }
        catch (e: unknown) {
            setAgentError(e instanceof Error ? e.message : "Failed to set primary agent");
        }
        finally {
            setAgentSaving(false);
        }
    }, []);
    const hideAgentsTab = !!form?.isPremade && !form?.isSystem && !isCreating;
    const agentsMdReadonly = !!form?.isSystem && !isCreating;
    useEffect(() => {
        if (hideAgentsTab && activeTab === "agents") {
            setActiveTab("overview");
        }
    }, [hideAgentsTab, activeTab]);
    const premadeReadonly = !!form?.isPremade && !isCreating;
    const showFieldValidation = !!form && !premadeReadonly && saveAttempted;
    const nameRequiredInvalid = !!form && !form.name.trim();
    const nameReservedInvalid = !!form && nameStartsWithDefault(form.name);
    const normalizedFormName = (form?.name ?? "").trim().toLowerCase();
    const nameDuplicateInvalid = !!form && !!normalizedFormName && agents.some((a) => a.id !== form.id && a.name.trim().toLowerCase() === normalizedFormName);
    const showDuplicateNameValidation = !!form && !premadeReadonly && !!normalizedFormName && nameDuplicateInvalid;
    // A Hermes agent's model lives on its Hermes computer, not in this field —
    // AGENT_MODELS holds Anthropic ids only, so none of them apply and the
    // picker would only ever record a meaningless value.
    const modelApplies = !!form && form.harnessType !== "Hermes";
    const modelRequiredInvalid = !!form && !form.model.trim();
    const canSave = !premadeReadonly && !!form?.name.trim() && !!form?.model.trim() && !nameStartsWithDefault(form.name) && !nameDuplicateInvalid;
    // The backend refuses ANY update to an is_system agent (ClaudeManagedError
    // SYSTEM_AGENT_IMMUTABLE in _assert_agent_mutable), so offering editable
    // fields and a Save button for one is a dead end. The seven persona agents
    // were already covered because they carry a persona_key and so read as
    // premade; Hermes Agent and Rate Quoting Agent have no persona_key, so they
    // slipped through and presented a Save that always failed server-side.
    // Same is_system && !isCreating rule the agents.md tab already uses.
    const systemReadonly = !!form?.isSystem && !isCreating;
    const fieldsReadonly = premadeReadonly || systemReadonly;
    const showSaveButton = !premadeReadonly && !systemReadonly && (activeTab === "overview" || (activeTab === "agents" && !agentsMdReadonly));
    const visibleTabs = DOC_TABS.filter((t) => !(hideAgentsTab && t.id === "agents"));
    /** Strip "-{userId}" suffix from system agent display names (e.g. "Rate Quoting Agent-42" → "Rate Quoting Agent") */
    const displayName = (a: typeof agents[number]) => a.isSystem ? a.name.replace(/-\d+$/, '') : a.name;
    const filteredAgents = agents.filter((a) => !agentSearch || a.name.toLowerCase().includes(agentSearch.toLowerCase()) || a.description.toLowerCase().includes(agentSearch.toLowerCase()));
    const defaultAgents = filteredAgents.filter((a) => a.isSystem);
    const myAgents = filteredAgents.filter((a) => !a.isSystem);
    return (<div className="dw-page">
      
      <div className={`dw-body${form ? " dw-body--detail" : ""}`}>
        
        <div className="dw-list">
          {dwConfigured !== false && (<div className="dw-list__top">
              <div className="dw-list__heading-block">
                {agents.length > 0 && (<div className="dw-list__search dw-list__search--inline">
                    <FiSearch size={12} className="dw-list__search-icon"/>
                    <input className="dw-list__search-input" placeholder="Search agents…" value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)}/>
                    {agentSearch && (<button className="dw-list__search-clear" onClick={() => setAgentSearch("")}>
                        <FiX size={11}/>
                      </button>)}
                  </div>)}
              </div>
              <div className="dw-list__actions">
                <button className="dw-list__new-btn" onClick={() => navigate(ROUTES.DW_ARTIFACTS)}>
                  <FiBarChart2 size={12}/> Agent Dashboard
                </button>
                {SHOW_ASSIGN_TASK_BUTTON && (<button className="dw-list__new-btn" onClick={() => openAssignTaskForAgent(form?.name || "", form?.id || "", form?.model || "")}>
                    <FiClipboard size={12}/> Assign Task
                  </button>)}
                <button className="dw-list__new-btn" onClick={handleNew}>
                  <FiPlus size={13}/> New Agent
                </button>
              </div>
            </div>)}
          <div className={`dw-list__inner${agentsLoading ? " dw-list__inner--loading" : ""}${!agentsLoading && agents.length === 0 ? " dw-list__inner--empty" : ""}`}>
            {agentsLoading ? (<>
                <div className="dw-agent-group dw-agent-group--skeleton">
                  <div className="dw-agent-skeleton__group-title"/>
                  <div className="dw-agent-skeleton__group-count"/>
                </div>
                {[0, 1].map((i) => (<div key={`default-skel-${i}`} className="dw-agent-card dw-agent-card--skeleton">
                    <div className="dw-agent-card__top">
                      <div className="dw-agent-card__info">
                        <div className="dw-agent-skeleton__line dw-agent-skeleton__line--title" style={{ width: i === 0 ? "72%" : "58%" }}/>
                        <div className="dw-agent-skeleton__line dw-agent-skeleton__line--sub" style={{ width: i === 0 ? "63%" : "70%" }}/>
                        <div className="dw-agent-card__meta">
                          <div className="dw-agent-skeleton__chip"/>
                          <div className="dw-agent-skeleton__dot"/>
                          <div className="dw-agent-skeleton__time"/>
                        </div>
                      </div>
                    </div>
                  </div>))}

                <div className="dw-agent-group dw-agent-group--secondary dw-agent-group--skeleton">
                  <div className="dw-agent-skeleton__group-title dw-agent-skeleton__group-title--secondary"/>
                  <div className="dw-agent-skeleton__group-count"/>
                </div>
                {[0].map((i) => (<div key={`custom-skel-${i}`} className="dw-agent-card dw-agent-card--skeleton">
                    <div className="dw-agent-card__top">
                      <div className="dw-agent-card__info">
                        <div className="dw-agent-skeleton__line dw-agent-skeleton__line--title dw-agent-skeleton__line--title-custom"/>
                        <div className="dw-agent-skeleton__line dw-agent-skeleton__line--sub dw-agent-skeleton__line--sub-custom"/>
                        <div className="dw-agent-card__meta">
                          <div className="dw-agent-skeleton__chip dw-agent-skeleton__chip--custom"/>
                          <div className="dw-agent-skeleton__dot"/>
                          <div className="dw-agent-skeleton__time"/>
                        </div>
                      </div>
                    </div>
                  </div>))}
              </>) : dwConfigured === false ? (<div className="dw-list__empty dw-setup-state">
                <div className="dw-setup-state__badge">
                  <span className="dw-setup-state__badge-dot"/>
                  DIGITAL WORKFORCE
                </div>

                <div className="dw-setup-state__body">
                  <div className="dw-setup-state__icon-col">
                    <div className="dw-setup-state__orb">
                      <div className="dw-setup-state__loader-ring dw-setup-state__loader-ring--outer"/>
                      <div className="dw-setup-state__loader-ring dw-setup-state__loader-ring--inner"/>
                      <div className="dw-setup-state__orb-core">
                        <img src={logo} alt="Digital Workforce" className="dw-setup-state__logo"/>
                        {setupBusy && <FiRefreshCw size={18} className="dw-setup-state__orb-spin dw-setup-state__orb-spinner"/>}
                      </div>
                    </div>
                  </div>

                  <div className="dw-setup-state__text-col">
                    <div className="dw-setup-state__title">
                      {setupError ? "Setup didn't finish." : "Setting up your Digital Workforce."}
                    </div>
                    <div className="dw-setup-state__desc">
                      {setupError
                        ? setupError
                        : "Activating your managed runtime and your default agents for this workspace. This happens once; you can create and run agents as soon as it finishes."}
                    </div>

                    <div className="dw-setup-state__features">
                      <span className="dw-setup-state__tag">
                        <FiCheck size={12}/> Provision secure runtime
                      </span>
                      <span className="dw-setup-state__tag">
                        <FiCheck size={12}/> Apply starter template
                      </span>
                      <span className="dw-setup-state__tag">
                        <FiLock size={12}/> Keep setup user-scoped
                      </span>
                    </div>

                    {setupError && (<div className="dw-setup-state__actions">
                        <button className="dw-list__new-btn dw-setup-state__btn" onClick={runDwSetup} disabled={setupBusy}>
                          {setupBusy ? (<>
                              <FiRefreshCw size={12} className="ca-fp-spin"/> Preparing workspace...
                            </>) : (<>
                              <FiRefreshCw size={12}/> Retry setup
                            </>)}
                        </button>
                      </div>)}

                    <div className="dw-setup-state__note">
                      {setupError
                        ? "If this keeps happening, sign out and back in, then open Digital Workforce again."
                        : "Takes around 10-20 seconds. Your agents will appear automatically when setup completes."}
                    </div>
                  </div>
                </div>
              </div>) : agents.length === 0 ? (<div className="dw-list__empty">
                No agents yet.
                <br />
                Click New Agent to get started.
              </div>) : null}
            <AnimatePresence initial={false}>
              {filteredAgents.length > 0 && (<div className="dw-agent-group">
                  <div className="dw-agent-group__title">
                    <FiZap size={12}/> Default Agents
                  </div>
                  <div className="dw-agent-group__count">{defaultAgents.length + COMING_SOON_AGENTS.length}</div>
                </div>)}
              {defaultAgents.map((agent) => {
            const visual = getDefaultAgentVisual(displayName(agent));
            const VisualIcon = visual?.icon;
            return (<motion.div key={agent.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.14 }}>
                  <div className={`dw-agent-card dw-agent-card--photo${selectedId === agent.id && !isCreating ? " dw-agent-card--active" : ""}`} onClick={() => handleSelect(agent)}>
                    <button type="button" className={`dw-agent-card__primary-btn${agent.isPrimary ? " dw-agent-card__primary-btn--active" : ""}`} onClick={(e) => { e.stopPropagation(); if (!agent.isPrimary)
            handleSetPrimary(agent.id); }} disabled={agentSaving || agent.isPrimary} title={agent.isPrimary ? "This is the primary agent" : "Set as primary agent"}>
                      <FiStar size={13}/>
                    </button>
                    {visual && (<div className="dw-agent-card__photo-wrap">
                        <img src={visual.image} alt="" className="dw-agent-card__photo"/>
                        {VisualIcon && (<span className="dw-agent-card__icon-badge"><VisualIcon size={14}/></span>)}
                      </div>)}
                    <div className="dw-agent-card__top">
                      <div className="dw-agent-card__info">
                        <div className="dw-agent-card__name">{displayName(agent)}</div>
                        <div className="dw-agent-card__role">{agent.description || "No description"}</div>
                        <div className="dw-agent-card__meta">
                          <span className="dw-agent-card__kind dw-agent-card__kind--system">Default</span>

                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>);
        })}
              {filteredAgents.length > 0 && COMING_SOON_AGENTS.map((agent) => {
            const VisualIcon = agent.icon;
            return (<div key={agent.id} className="dw-agent-card dw-agent-card--photo dw-agent-card--coming-soon" title="Coming soon">
                  <div className="dw-agent-card__photo-wrap">
                    <img src={agent.image} alt="" className="dw-agent-card__photo"/>
                    <span className="dw-agent-card__icon-badge"><VisualIcon size={14}/></span>
                  </div>
                  <div className="dw-agent-card__top">
                    <div className="dw-agent-card__info">
                      <div className="dw-agent-card__name">{agent.name}</div>
                      <div className="dw-agent-card__role">{agent.description}</div>
                      <div className="dw-agent-card__meta">
                        <span className="dw-agent-card__kind dw-agent-card__kind--soon">Coming Soon</span>
                      </div>
                    </div>
                  </div>
                </div>);
        })}

              {filteredAgents.length > 0 && (<div className="dw-agent-group dw-agent-group--secondary">
                  <div className="dw-agent-group__title">
                    <FiUsers size={12}/> My Agents
                  </div>
                  <div className="dw-agent-group__count">{myAgents.length}</div>
                </div>)}
              {myAgents.map((agent) => {
            // A custom agent's uploaded logo gets the same photo area as the
            // default agents' cards; without one the card stays text-only.
            const logo = getUploadedAgentPhoto(agent.id);
            return (<motion.div key={agent.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.14 }}>
                  <div className={`dw-agent-card${logo ? " dw-agent-card--photo" : ""}${selectedId === agent.id && !isCreating ? " dw-agent-card--active" : ""}`} onClick={() => handleSelect(agent)}>
                    <button type="button" className={`dw-agent-card__primary-btn${agent.isPrimary ? " dw-agent-card__primary-btn--active" : ""}`} onClick={(e) => { e.stopPropagation(); if (!agent.isPrimary)
            handleSetPrimary(agent.id); }} disabled={agentSaving || agent.isPrimary} title={agent.isPrimary ? "This is the primary agent" : "Set as primary agent"}>
                      <FiStar size={13}/>
                    </button>
                    {logo && (<div className="dw-agent-card__photo-wrap dw-agent-card__photo-wrap--logo">
                        <img src={logo} alt="" className="dw-agent-card__logo"/>
                      </div>)}
                    <div className="dw-agent-card__top">
                      {(() => {
                        const photo = agentPhotoFor(agent.id);
                        return (<span className="dw-agent-avatar dw-agent-avatar--card dw-agent-logo__img" style={photo ? undefined : { background: agent.avatarColor }}>
                            {photo ? <img src={photo} alt=""/> : getInitials(agent.name)}
                          </span>);
                    })()}
                      <div className="dw-agent-card__info">
                        <div className="dw-agent-card__name">{agent.name}</div>
                        <div className="dw-agent-card__role">{agent.description || "No description"}</div>
                        <div className="dw-agent-card__meta">
                          <span className="dw-agent-card__kind dw-agent-card__kind--custom">Custom</span>
                          <span className="dw-agent-card__dot">•</span>
                          <span className="dw-agent-card__time">{formatRelTime(agent.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>);
        })}
            </AnimatePresence>
          </div>
        </div>

        
        <div className="dw-panel">
          <AnimatePresence mode="wait">
            {form ? (<motion.div key={form.id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.14 }} className="dw-panel__form-motion">
                
                <div className="dw-panel__header">
                  <button className="dw-panel__back-btn" onClick={() => {
                setForm(null);
                setSelectedId(null);
                setIsCreating(false);
            }} title="Back to agents">
                    <FiChevronLeft size={15}/>
                    <span>Agents</span>
                  </button>
                  <AgentLogoControl agentId={form.id || ""} name={form.name || "New Agent"} color={form.avatarColor} isSystem={!!form.isSystem} editable={isCreating || (!form.isSystem && !!form.id)} draft={isCreating ? { logo: pendingLogo, onPick: pickPendingLogo, onClear: clearPendingLogo } : undefined}/>
                  <div className="dw-panel__title-area">
                    <div className="dw-panel__name">
                      {form.name || "New Agent"}
                      {!isCreating && form.isPrimary && (<span className="dw-panel__primary-badge" title="This is the primary agent">
                          <FiStar size={10}/> Primary
                        </span>)}
                    </div>
                    <div className="dw-panel__role-type">{form.harnessType === "Hermes"
                    ? "Auto (managed)"
                    : AGENT_MODELS.find((m) => m.value === form.model)?.label ?? form.model}</div>
                  </div>
                  {/* One primary action (Start Chat); everything else is quiet. */}
                  <div className="dw-panel__actions">
                    {!isCreating && !form.isPrimary && (<button type="button" className="dw-ghost-btn" onClick={() => handleSetPrimary(form.id)} title="Make this the agent the chat bar talks to by default" disabled={agentSaving}>
                        <FiStar size={13}/> Set as primary
                      </button>)}
                    {!isCreating && SHOW_ASSIGN_TASK_BUTTON && (<button type="button" className="dw-ghost-btn" onClick={() => openAssignTaskForAgent(form.name || "", form.id || "", form.model || "")} title="Assign a command to this agent">
                        <FiClipboard size={13}/> Assign task
                      </button>)}
                    {!isCreating && !form.isSystem && (<button type="button" className="dw-ghost-btn dw-ghost-btn--icon" onClick={() => setAgentShareMenuOpen(true)} title="Share this agent" aria-label="Share this agent">
                        <FiShare2 size={14}/>
                      </button>)}
                    {!isCreating && !form.isSystem && (<button type="button" className="dw-ghost-btn dw-ghost-btn--icon dw-ghost-btn--danger" onClick={() => requestDelete(form.id)} title="Delete agent" aria-label="Delete agent" disabled={agentSaving}>
                        {agentSaving ? <FiRefreshCw size={14} className="ca-fp-spin"/> : <FiTrash2 size={14}/>}
                      </button>)}
                    {!isCreating && (<button type="button" className="dw-primary-btn" onClick={() => startChatWithAgent(form.id, form.name, form.avatarColor, form.isSystem)} title="Start a new chat with this agent">
                        <FiMessageSquare size={13}/> Start chat
                      </button>)}
                  </div>
                </div>

                {!isCreating && !form.isSystem && agentShareMenuOpen && (<AgentShareMenuModal agentId={form.id} agentName={form.name || 'Agent'} isAgencyOwner={isAgencyOwner} onClose={() => setAgentShareMenuOpen(false)}/>)}

                
                <div className="dw-tabs" role="tablist" aria-label="Agent sections">
                  {visibleTabs.map((t) => {
                const isLocked = isCreating && (t.id === "tools" || t.id === "mcp" || t.id === "activity");
                return (<button key={t.id} type="button" role="tab" aria-selected={activeTab === t.id} className={`dw-tab${activeTab === t.id ? " dw-tab--active" : ""}${isLocked ? " dw-tab--locked" : ""}`} onClick={() => !isLocked && setActiveTab(t.id)} disabled={isLocked} title={isLocked ? "Create the agent first to enable this tab" : undefined}>
                        {t.label}
                        {isLocked && <FiLock size={10} style={{ marginLeft: 4, opacity: 0.5 }}/>}
                      </button>);
            })}
                </div>

                
                <div className="dw-tab-body">
                  
                  {activeTab === "overview" && (<motion.div key="overview-agent" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content">
                      <div className="dw-agent-ov">
                        
                        <div className="dw-agent-details dw-agent-details--ov">
                          <div className="dw-agent-details__row">
                            <div className="dw-field dw-field--grow">
                              <label className="dw-field__label">Name</label>
                              <input className="dw-field__input" placeholder="e.g. Aria" value={form.name} onChange={(e) => updateForm("name", e.target.value)} disabled={fieldsReadonly} style={(showFieldValidation && (nameRequiredInvalid || nameReservedInvalid)) || showDuplicateNameValidation
                    ? { borderColor: "#ef4444" }
                    : undefined}/>
                              {showFieldValidation && nameRequiredInvalid && (<p className="dw-fp-field-error">Please enter agent name.</p>)}
                              {showFieldValidation && !nameRequiredInvalid && nameReservedInvalid && (<p className="dw-fp-field-error">Names starting with "Default" are reserved.</p>)}
                              {showDuplicateNameValidation && !nameRequiredInvalid && !nameReservedInvalid && (<p className="dw-fp-field-error">Agent name already exists. Use a unique name.</p>)}
                            </div>
                            <div className="dw-field dw-field--grow">
                              <label className="dw-field__label">{modelApplies ? "Model *" : "Model"}</label>
                              {modelApplies ? (<>
                                  <ModelPicker value={form.model} onChange={handleModelChange} disabled={fieldsReadonly} invalid={!!(showFieldValidation && modelRequiredInvalid)} onOpenChange={setModelPickerOpen}/>
                                  {!modelPickerOpen && showFieldValidation && modelRequiredInvalid && (<p className="dw-fp-field-error">Please select a model.</p>)}
                                  {!modelPickerOpen && !modelRequiredInvalid && AGENT_MODELS.find((m) => m.value === form.model)?.hint && (<p className="dw-field__hint">{AGENT_MODELS.find((m) => m.value === form.model)?.hint}</p>)}
                                </>) : (<>
                                  <input className="dw-field__input" value="Managed by the agent's computer" disabled readOnly/>
                                  <p className="dw-field__hint">This agent runs on its own computer, which selects and updates its model automatically.</p>
                                </>)}
                            </div>
                          </div>
                          {modelApplies && (() => {
                    // Shown from the start (a new agent has no model yet) so the
                    // settings are discoverable; disabled until a model is picked,
                    // since the available options depend on it.
                    const hasModel = !!form.model.trim();
                    const modelLabel = AGENT_MODELS.find((m) => m.value === form.model)?.label ?? form.model;
                    const efforts = getModelEfforts(form.model);
                    const effortOptions = AGENT_EFFORT_OPTIONS.filter((o) => efforts.includes(o.value));
                    const fastAvailable = modelSupportsFast(form.model);
                    const effortHint = AGENT_EFFORT_OPTIONS.find((o) => o.value === form.effort)?.hint;
                    return (<div className="dw-agent-details__row dw-agent-details__row--runtime">
                              <div className="dw-field dw-field--grow">
                                <label className="dw-field__label">Thinking effort</label>
                                {!hasModel ? (<>
                                    <div className="dw-field__select-wrap">
                                      <select className="dw-field__select" value={DEFAULT_AGENT_EFFORT} disabled>
                                        <option value={DEFAULT_AGENT_EFFORT}>Medium (default)</option>
                                      </select>
                                    </div>
                                    <p className="dw-field__hint">Select a model first — available levels depend on the model.</p>
                                  </>) : effortOptions.length > 0 ? (<>
                                    <div className="dw-field__select-wrap">
                                      <select className="dw-field__select" value={form.effort || DEFAULT_AGENT_EFFORT} disabled={fieldsReadonly} onChange={(e) => updateRuntimeField("effort", e.target.value as AgentEffort)}>
                                        {effortOptions.map((o) => (<option key={o.value} value={o.value}>
                                            {o.label}{o.value === DEFAULT_AGENT_EFFORT ? " (default)" : ""}
                                          </option>))}
                                      </select>
                                    </div>
                                    {effortHint && <p className="dw-field__hint">{effortHint}</p>}
                                  </>) : (<>
                                    <input className="dw-field__input" value="Not supported" disabled readOnly/>
                                    <p className="dw-field__hint">{modelLabel} doesn't support effort levels.</p>
                                  </>)}
                              </div>
                              <div className="dw-field dw-field--grow">
                                <label className="dw-field__label">Speed</label>
                                <div className="dw-field__select-wrap">
                                  <select className="dw-field__select" value={fastAvailable ? form.speed : "standard"} disabled={fieldsReadonly || !hasModel || !fastAvailable} onChange={(e) => updateRuntimeField("speed", e.target.value as AgentSpeed)}>
                                    <option value="standard">Normal</option>
                                    <option value="fast">Fast</option>
                                  </select>
                                </div>
                                <p className="dw-field__hint">
                                  {!hasModel
                    ? "Select a model first — fast mode is available on Opus 4.8."
                    : !fastAvailable
                    ? `Fast mode isn't available on ${modelLabel} — available on Opus 4.8.`
                    : form.speed === "fast"
                        ? "Up to ~2.5x faster responses at premium pricing."
                        : "Standard speed and pricing."}
                                </p>
                              </div>
                            </div>);
                })()}
                          <div className="dw-field">
                            <label className="dw-field__label">Description</label>
                            <textarea className="dw-field__input dw-field__textarea dw-field__textarea--lg" placeholder="Describe what this agent does…" value={form.description} rows={24} disabled={fieldsReadonly} onChange={(e) => updateForm("description", e.target.value)}/>
                          </div>
                        </div>
                      </div>
                      
                    </motion.div>)}

                  
                  {activeTab === "activity" && (<motion.div key="activity-agent" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content">
                      <div className="dw-agent-ov dw-agent-ov--fill">
                        
                        <div className="dw-activity-tabs-row">
                          <div className="dw-activity-tabs" role="tablist" aria-label="Activity">
                            {[
                    {
                        key: "tasks" as const,
                        icon: <FiClipboard size={13}/>,
                        val: agentKanbanTasks.total,
                        lbl: "Assigned Commands",
                    },
                    { key: "routines" as const, icon: <FiRepeat size={13}/>, val: agentRoutines.total, lbl: "Routines" },
                    {
                        key: "sessions" as const,
                        icon: <FiMessageSquare size={13}/>,
                        val: agentSessions.total,
                        lbl: "Chat History",
                    },
                ].map((c) => {
                    const active = activitySubTab === c.key;
                    return (<button key={c.key} type="button" role="tab" aria-selected={active} className={`dw-activity-tab${active ? " dw-activity-tab--active" : ""}`} onClick={() => setActivitySubTab(c.key)}>
                                  {c.icon} {c.lbl}
                                  <span className="dw-activity-tab__count">
                                    {c.val}
                                  </span>
                                </button>);
                })}
                          </div>

                          
                          <div className="dw-daterange">
                            <span className="dw-daterange__label">
                              Date range
                            </span>
                            <input type="date" className="dw-daterange__input" value={activityDateRange.from} max={activityDateRange.to} onChange={(e) => setActivityDateRange((prev) => ({ ...prev, from: e.target.value }))}/>
                            <span className="dw-daterange__sep">to</span>
                            <input type="date" className="dw-daterange__input" value={activityDateRange.to} min={activityDateRange.from} max={toDateInputValue(new Date())} onChange={(e) => setActivityDateRange((prev) => ({ ...prev, to: e.target.value }))}/>
                            <button type="button" className="dw-daterange__reset" onClick={() => setActivityDateRange(defaultActivityDateRange())}>
                              Reset to last 7 days
                            </button>
                          </div>
                        </div>

                              
                              {activitySubTab === 'tasks' && (<div className="dw-ov-block dw-ov-block--fill">
                                  {agentKanbanTasks.loading ? (<div className="dw-ov-task-list">
                                      {Array.from({ length: 3 }).map((_, i) => (<div key={i} className="dw-ov-task dw-ov-task--skeleton">
                                          <div className="dw-ov-task__body">
                                            <div className="dw-ov-task__title dw-ov-task__title--skeleton">Loading...</div>
                                          </div>
                                        </div>))}
                                    </div>) : agentKanbanTasks.items.length === 0 ? (<div className="dw-ov-block__empty">
                                      <FiClipboard size={18} style={{ opacity: 0.2 }}/>
                                      <span>No tasks assigned yet</span>
                                    </div>) : (<div className="dw-ov-task-list dw-ov-task-list--scroll">
                                      {(() => {
                            return agentKanbanTasks.items.map(task => {
                                const sCfg = activityStatusBadge(task.status_id);
                                return (<div key={task.id} className="dw-ov-task dw-ov-task--clickable" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); (e.currentTarget as HTMLElement).click(); } }} onClick={async () => {
                                        const txId = await resolveTaskTransactionId(String(task.id));
                                        navigate(txId
                                            ? `${ROUTES.MOMENTUM_COMMAND}/${encodeURIComponent(txId)}`
                                            : `${ROUTES.MOMENTUM_COMMAND}?taskId=${task.id}`);
                                    }}>
                                              <div className="dw-ov-task__body">
                                                <div className="dw-ov-task__title dw-ov-task__title--row">
                                                  {task.task_number && (<span className="dw-ov-task__num-badge">
                                                      {task.task_number}
                                                    </span>)}
                                                  <span className="dw-ov-task__title-text">{task.title}</span>
                                                </div>
                                                {task.description && <div className="dw-ov-task__desc">{task.description}</div>}
                                              </div>
                                              <div className="dw-ov-task__aside">
                                                <span className="dw-ov-task__status"><span className="dw-ov-task__status-dot" style={{ background: sCfg.color }} aria-hidden="true"/>{sCfg.label}</span>
                                                <span className="dw-ov-task__time">{formatKanbanTaskTime(task.created_at)}</span>
                                              </div>
                                            </div>);
                            });
                        })()}
                                      <LoadMoreSentinel list={agentKanbanTasks}/>
                                    </div>)}
                                </div>)}

                              {activitySubTab === 'routines' && (<div className="dw-ov-block dw-ov-block--fill">
                                  {agentRoutines.loading ? (<div className="dw-ov-task-list">
                                      {Array.from({ length: 3 }).map((_, i) => (<div key={i} className="dw-ov-task dw-ov-task--skeleton">
                                          <div className="dw-ov-task__body">
                                            <div className="dw-ov-task__title dw-ov-task__title--skeleton">Loading...</div>
                                          </div>
                                        </div>))}
                                    </div>) : agentRoutines.items.length === 0 ? (<div className="dw-ov-block__empty">
                                      <FiRepeat size={18} style={{ opacity: 0.2 }}/>
                                      <span>No routines assigned yet</span>
                                    </div>) : (<div className="dw-ov-task-list dw-ov-task-list--scroll">
                                      {agentRoutines.items.map(r => {
                            const sCfg = activityStatusBadge(r.status);
                            const duration = formatDurationSec(r.duration_sec);
                            return (<div key={r.id} className="dw-ov-task dw-ov-task--routine" style={{ cursor: r.routine_id ? 'pointer' : 'default' }} onClick={r.routine_id ? () => navigate(`${ROUTES.MOMENTUM_ROUTINES}/${encodeURIComponent(r.routine_id!)}`) : undefined}>
                                            <div className="dw-ov-task__body">
                                              <div className="dw-ov-task__title">{r.name || routineRunSummary(r.notes)}</div>
                                              {r.name && r.notes && <div className="dw-ov-task__desc">{routineRunSummary(r.notes)}</div>}
                                            </div>
                                            <div className="dw-ov-task__aside">
                                              <span className="dw-ov-task__status"><span className="dw-ov-task__status-dot" style={{ background: sCfg.color }} aria-hidden="true"/>{sCfg.label}</span>
                                              <span className="dw-ov-task__time">
                                                {r.started_at && `Started: ${formatRelTime(r.started_at)}`}
                                                {r.started_at && r.finished_at && ' · '}
                                                {r.finished_at && `Ended: ${formatRelTime(r.finished_at)}${duration ? ` (${duration})` : ''}`}
                                                {!r.started_at && !r.finished_at && '—'}
                                              </span>
                                            </div>
                                          </div>);
                        })}
                                      <LoadMoreSentinel list={agentRoutines}/>
                                    </div>)}
                                </div>)}

                              {activitySubTab === 'sessions' && (<div className="dw-ov-block dw-ov-block--fill">
                                  {agentSessions.loading ? (<div className="dw-ov-task-list">
                                      {Array.from({ length: 3 }).map((_, i) => (<div key={i} className="dw-ov-task dw-ov-task--skeleton">
                                          <div className="dw-ov-task__body">
                                            <div className="dw-ov-task__title dw-ov-task__title--skeleton">Loading...</div>
                                          </div>
                                        </div>))}
                                    </div>) : agentSessions.items.length === 0 ? (<div className="dw-ov-block__empty">
                                      <FiMessageSquare size={18} style={{ opacity: 0.2 }}/>
                                      <span>No conversations yet</span>
                                    </div>) : (<div className="dw-ov-task-list dw-ov-task-list--scroll">
                                      {(() => {
                            return agentSessions.items.map(s => (<div key={s.id} className="dw-ov-task dw-ov-task--chat" onClick={() => navigate(`${ROUTES.CHAT}/${s.id}`, {
                                    state: { dwAgentId: selectedId, dwAgentName: selectedAgentName, dwSessionId: s.id },
                                })}>
                                            <div className="dw-ov-task__body">
                                              <div className="dw-ov-task__title">{chatSessionTitle(s.title)}</div>
                                              {s.description && <div className="dw-ov-task__desc">{s.description}</div>}
                                            </div>
                                            <div className="dw-ov-task__aside">
                                              <span className="dw-ov-task__time">{formatRelTime(s.created)}</span>
                                            </div>
                                          </div>));
                        })()}
                                      <LoadMoreSentinel list={agentSessions}/>
                                    </div>)}
                                </div>)}

                      </div>
                      
                    </motion.div>)}

                  {activeTab === "heartbeat" && (<motion.div key="heartbeat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content">
                      <p className="dw-tab-hint">
                        Operational parameters — triggers, health checks, timeouts, and runtime configuration for this agent.
                      </p>
                      <MdEditor value={form.heartbeat || ""} onChange={(v) => updateForm("heartbeat", v)} placeholder={`# Heartbeat\n\n## Triggers\n- Incoming client email\n- New support ticket\n\n## Health Checks\n- AMS connection: active\n- Response latency < 2s\n\n## Parameters\n- Max tokens: 4096\n- Temperature: 0.3`} minHeight={300}/>
                    </motion.div>)}

                  {activeTab === "soul" && (<motion.div key="soul" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content">
                      <p className="dw-tab-hint">
                        The agent's core identity — personality, values, domain expertise, and communication style. Write it as if describing a
                        person, not configuring a machine.
                      </p>
                      <MdEditor value={form.soul} onChange={(v) => updateForm("soul", v)} placeholder={`# Agent Name\n\n## Identity\nYou are...\n\n## Core Values\n- Empathy\n- Accuracy\n\n## Communication Style\nFriendly, direct, professional.`} minHeight={300}/>
                    </motion.div>)}

                  {activeTab === "tools" &&
                (isCreating ? (<motion.div key="tools-locked" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content dw-tab-locked">
                        <FiLock size={24} style={{ opacity: 0.25, marginBottom: 10 }}/>
                        <div className="dw-tab-locked__title">Agent ID required</div>
                        <div className="dw-tab-locked__msg">Save the agent first — skills can only be attached after the agent has been created.</div>
                      </motion.div>) : (<motion.div key={`tools-${form.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-panel__tab-flex-fill">
                        <DwSkillsTab agentId={form.id} agentName={form.name}/>
                      </motion.div>))}

                  {!hideAgentsTab && activeTab === "agents" && (<motion.div key="agents-tab" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content">
                      {agentsMdReadonly ? (<div className="ca-fp-anthropic-view dw-fp-anthropic-view--flush">
                          <div className="ca-fp-anthropic-view__banner">
                            <div className="ca-fp-anthropic-view__banner-icon">
                              <FiLock size={16}/>
                            </div>
                            <div className="ca-fp-anthropic-view__banner-text">
                              <span className="ca-fp-anthropic-view__banner-title">System Agent</span>
                              <span className="ca-fp-anthropic-view__banner-desc">
                                This is a system agent. agents.md content is hidden and cannot be viewed or updated here.
                              </span>
                            </div>
                          </div>
                        </div>) : (<MdEditor value={form.agentsMd || ""} onChange={(v) => updateForm("agentsMd", v)} placeholder={`# ${form.name || "Agent"} — Agents\n\n## Collaborators\n- List other agents this agent works with\n\n## Handoffs\n- Conditions under which this agent hands off to another\n\n## Escalation\n- When to escalate to a human`} minHeight={320}/>)}
                    </motion.div>)}

                  {activeTab === "mcp" &&
                (isCreating ? (<motion.div key="mcp-locked" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content dw-tab-locked">
                        <FiLock size={24} style={{ opacity: 0.25, marginBottom: 10 }}/>
                        <div className="dw-tab-locked__title">Agent ID required</div>
                        <div className="dw-tab-locked__msg">
                          Save the agent first — MCP servers can only be connected after the agent has been created.
                        </div>
                      </motion.div>) : (<motion.div key={`mcp-${form.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-panel__tab-flex-fill">
                        <DwMcpTab agentId={form.id} agentName={form.name}/>
                      </motion.div>))}

                  {activeTab === "integrations" &&
                    (isCreating ? (
                      <motion.div key="integrations-locked" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content dw-tab-locked">
                        <FiLock size={24} style={{ opacity: 0.25, marginBottom: 10 }} />
                        <div className="dw-tab-locked__title">Agent ID required</div>
                        <div className="dw-tab-locked__msg">
                          Save the agent first — integrations can only be enabled after the agent has been created.
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key={`integrations-${form.id}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="dw-panel__tab-flex-fill"
                      >
                        <DwIntegrationsTab agentId={form.id} agentName={form.name || "This agent"} />
                      </motion.div>
                    ))}

                  {activeTab === "memory" &&
                (isCreating ? (<motion.div key="memory-locked" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content dw-tab-locked">
                        <FiLock size={24} style={{ opacity: 0.25, marginBottom: 10 }}/>
                        <div className="dw-tab-locked__title">Agent ID required</div>
                        <div className="dw-tab-locked__msg">Save the agent first — memory is stored per agent, so it only exists after the agent has been created.</div>
                      </motion.div>) : (<motion.div key={`memory-${form.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dw-tab-content">
                        <DwMemoryTab agentId={form.id}/>
                      </motion.div>))}

                </div>
                

                
                {(agentError || showSaveButton) && (<div className="dw-panel__footer">
                    {agentError ? <span className="dw-panel__footer-error">{agentError}</span> : null}
                    {showSaveButton && (<button type="button" className={`dw-save-btn${saved ? " dw-save-btn--saved" : ""}`} onClick={handleSave} disabled={!canSave || agentSaving}>
                        {saved ? (<>
                            <FiCheck size={13}/> Saved
                          </>) : agentSaving ? (<>
                            <FiRefreshCw size={13} style={{ animation: "dw-spin 0.8s linear infinite" }}/> {isCreating ? "Creating…" : "Saving…"}
                          </>) : isCreating ? (<>
                            <FiPlus size={13}/> Create Agent
                          </>) : (<>
                            <FiCheck size={13}/> Save Changes
                          </>)}
                      </button>)}
                  </div>)}
              </motion.div>) : (<motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="dw-panel__empty">
                <div className="dw-panel__empty-icon">
                  <FiUsers size={22}/>
                </div>
                <div className="dw-panel__empty-text">Select an agent to view details</div>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </div>
      

      <AnimatePresence>
        {pendingDeleteAgent && (<motion.div className="dw-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !agentSaving && setPendingDeleteAgent(null)}>
            <motion.div className="dw-modal dw-archive-modal" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.16 }} onClick={(e) => e.stopPropagation()}>
              <div className="dw-modal__header dw-archive-modal__header">
                <div className="dw-archive-modal__title-wrap">
                  <span className="dw-archive-modal__icon">
                    <FiAlertTriangle size={14}/>
                  </span>
                  <span>Archive Agent</span>
                </div>
              </div>
              <div className="dw-modal__body dw-archive-modal__body">
                <p className="dw-archive-modal__text">
                  Archive <strong>{pendingDeleteAgent.name}</strong>?
                </p>
                <p className="dw-archive-modal__subtext">
                  This will remove the agent from active lists. You can restore it later only if unarchive is supported by the backend.
                </p>
              </div>
              <div className="dw-modal__footer dw-archive-modal__footer">
                <button className="dw-modal__cancel-btn dw-archive-modal__cancel" onClick={() => setPendingDeleteAgent(null)} disabled={agentSaving}>
                  Cancel
                </button>
                <button className="dw-archive-modal__confirm" onClick={() => handleDelete(pendingDeleteAgent.id)} disabled={agentSaving} title="Archive agent">
                  {agentSaving ? <FiRefreshCw size={13} className="ca-fp-spin"/> : <FiTrash2 size={13}/>}
                  <span>Archive</span>
                </button>
              </div>
            </motion.div>
          </motion.div>)}
      </AnimatePresence>

      
      <AnimatePresence>
        {assignTaskDraft && (<motion.div className="dw-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => {
                setAssignTaskDraft(null);
                setTaskRefreshKey((k) => k + 1);
            }}>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.18 }} onClick={(e) => e.stopPropagation()} className="dw-fp-assign-modal">
              
              <div className="dw-fp-assign-modal__header">
                <FiClipboard size={13} style={{ color: "#a200ff", flexShrink: 0 }}/>
                <span className="dw-fp-assign-modal__title">Assign Task</span>
                {assignTaskDraft.modelLabel && (<span className="dw-fp-assign-modal__model-badge">
                    {assignTaskDraft.modelLabel}
                  </span>)}
                <button onClick={() => {
                setAssignTaskDraft(null);
                setTaskRefreshKey((k) => k + 1);
            }} className="dw-fp-assign-modal__close-btn">
                  <FiX size={16}/>
                </button>
              </div>

              
              <div className="dw-task-panel-wrap">
                <CommandIssuePanel key={draftPanelKey} issue={assignTaskDraft.issue} mode={assignTaskDraft.issue.id === "__new__" ? "create" : "edit"} splitTitleRow onBack={() => {
                setAssignTaskDraft(null);
                setTaskRefreshKey((k) => k + 1);
            }} onSave={(updated) => {
                setAssignTaskDraft((prev) => (prev ? { ...prev, issue: updated } : null));
                setTaskRefreshKey((k) => k + 1);
            }} onSaveSilent={(updated) => setAssignTaskDraft((prev) => (prev ? { ...prev, issue: updated } : null))} onClose={() => {
                setAssignTaskDraft(null);
                setTaskRefreshKey((k) => k + 1);
            }}/>
              </div>
            </motion.div>
          </motion.div>)}
      </AnimatePresence>
    </div>);
};
export default DigitalWorkforcePage;
